import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { Card, Row, Col, Typography, Table, Tag, Button, DatePicker, Select, Statistic, Spin, Space, Progress, Segmented, Alert, Tabs, Tooltip as AntTooltip, InputNumber, Popover, Modal, Form, Input, Divider, message, Dropdown, TimePicker, Checkbox, Collapse } from 'antd';
import ResizableTitle from '../components/ResizableTitle';
import AgentGradeCard from '../components/AgentGradeCard';
import { CalendarOutlined, UnorderedListOutlined, TagOutlined, AppstoreOutlined, WarningOutlined, MinusCircleFilled, DownOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import {
  ComposedChart, BarChart, Bar, Cell, LabelList,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { UserContext, hasGrant } from '../App';
import dayjs from 'dayjs';
import { formatTS, secsToDuration } from '../utils/time';
import OpenScheduleVariancesTable from './OpenScheduleVariancesTable';
import WFMSkillsDrawer from './WFMSkillsDrawer';
import api from '../api';
import CampaignUpdateReadModal from './CampaignUpdateReadModal';
import { usePageLabel } from './BackChip';
import ComplianceCreateModal from '../components/ComplianceCreateModal';
import { buildScheduleCols, buildAwsStateCols } from '../pages/DBALPage';
import HelpdeskInvolvedTickets, { fetchInvolvedTickets, involvedTicketColumns, HD_OPEN_STATUSES } from './HelpdeskInvolvedTickets';
import AgentTimeline from '../components/AgentTimeline';
import AbsenceCreateModal from '../components/AbsenceCreateModal';
import ErrorCreateModal from '../components/ErrorCreateModal';
import CoachingCreateModal from '../components/CoachingCreateModal';
import AgentRecapModal, { recapObjectsFor } from './AgentRecapModal';
import AbsenceTag from './AbsenceTag';
import AgentBadges from './AgentBadges';
import ManageBadgesModal from './ManageBadgesModal';

// Rolls over to hours past 60 minutes; see utils/time.
function fmtTime(seconds) {
  if (!seconds) return '0:00';
  return secsToDuration(seconds) ?? '0:00';
}

// CNE stores every duration as a fraction of an hour. The panel's formatters
// take seconds, so convert once here rather than at each call site.
const hrsToSecs = (h) => (h == null ? null : Math.round(Number(h) * 3600));


function fmtSecs(s) {
  if (s == null) return null;
  const t = Math.round(s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${m}:${String(sec).padStart(2,'0')}`;
}

// Clock times read as AM/PM. Midnight is a time, not a missing value, so
// 00:00 gives 12:00 AM and only an absent value gives a dash.
//
// Not fmtTimeStr below, which formats a DURATION. The two take the same shape
// of string and mean entirely different things: running thirteen minutes of
// handle time through this would report it as one in the afternoon.
const fmtClock = (v) => {
  if (!v) return '—';
  const [hStr, mStr] = String(v).split(':');
  const h = Number(hStr);
  if (Number.isNaN(h)) return v;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mStr ?? '00').padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

function fmtTimeStr(s) {
  // Formats "HH:MM:SS" string from SQL time(0) to "M:SS" or "H:MM:SS"
  if (!s) return null;
  const [h, m, sec] = s.split(':').map(Number);
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${m}:${String(sec).padStart(2,'0')}`;
}

function scoreColor(pct) {
  if (pct == null) return '#d9d9d9';
  if (pct >= 90) return '#52c41a';
  if (pct >= 70) return '#fa8c16';
  return '#ff4d4f';
}

function ScoreBarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ color: scoreColor(d.AvgScore) }}>Avg Score: <strong>{d.AvgScore}%</strong></div>
      <div style={{ color: '#595959' }}>Completed evals: {d.Count}</div>
      {d.AvgTargetScore != null && <div style={{ color: '#1677ff', marginTop: 4 }}>Target (Gold): {d.AvgTargetScore}%</div>}
      {d.AvgHighScore   != null && <div style={{ color: '#722ed1' }}>Platinum: {d.AvgHighScore}%</div>}
    </div>
  );
}

function ScoreBarLabel({ x, y, width, height, value }) {
  if (value == null || height < 20) return null;
  return (
    <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight={600} fill="#fff">
      {value}%
    </text>
  );
}

const EVAL_STATUS_COLOR = {
  assigned: 'blue', in_progress: 'orange', completed: 'green', rescore_requested: 'purple',
};
const EVAL_STATUS_LABEL = {
  assigned: 'Draft', in_progress: 'In Progress', completed: 'Completed', rescore_requested: 'Rescore',
};

const evalColumns = [
  { title: 'Call Date',    dataIndex: 'CallDate',  key: 'CallDate',  width: 110 },
  { title: 'Eval Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175,
    defaultSortOrder: 'descend',
    sorter: (a, b) => (a.CreatedAt || '').localeCompare(b.CreatedAt || ''),
    render: (v) => formatTS(v) },
  { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 140, ellipsis: true },
  { title: 'Form', dataIndex: 'FormName', key: 'FormName' },
  {
    title: 'Status',
    dataIndex: 'Status',
    key: 'Status',
    width: 110,
    render: (s) => <Tag color={EVAL_STATUS_COLOR[s] ?? 'default'}>{EVAL_STATUS_LABEL[s] ?? s}</Tag>,
  },
  {
    title: 'Acknowledge Deadline', key: 'DisputeDeadline', width: 125,
    render: (_, r) => {
      if (r.Status !== 'completed' || !r.CompletedAt) return null;
      const dl = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
      return <span style={{ color: dayjs().isAfter(dl) ? '#8c8c8c' : '#fa8c16', fontSize: 12 }}>{dl.format('YYYY-MM-DD')}</span>;
    },
  },
  {
    title: 'Score',
    key: 'Score',
    width: 80,
    // Against the campaign goal for that campaign and month: green at or
    // above Target (Gold), orange at or above Silver, red below; no colour
    // when the campaign has no goal.
    render: (_, r) => {
      if (r.ScorePct === null) return '—';
      const color = r.GoalTargetPct != null && r.ScorePct >= r.GoalTargetPct ? 'green'
        : r.GoalSilverPct != null && r.ScorePct >= r.GoalSilverPct ? 'orange'
          : r.GoalTargetPct != null || r.GoalSilverPct != null ? 'red' : 'default';
      return <Tag color={color}>{r.ScorePct}%</Tag>;
    },
  },
  {
    title: '',
    key: 'view',
    width: 70,
    render: (_, r) => (
      <Link to={`/cases/${r.ID}`}>
        <Button size="small">View</Button>
      </Link>
    ),
  },
];

const ERROR_STATUS_COLOR = {
  assigned: 'blue', in_progress: 'orange', completed: 'green', rescore_requested: 'purple',
};
const ERROR_STATUS_LABEL = {
  assigned: 'Draft', in_progress: 'In Progress', completed: 'Completed', rescore_requested: 'Disputed',
};

const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };

const errorColumns = [
  { title: 'Create Date', dataIndex: 'CreateDate', key: 'CreateDate', width: 110,
    defaultSortOrder: 'descend',
    sorter: (a, b) => (a.CreateDate || '').localeCompare(b.CreateDate || '') },
  { title: 'Date of Error', dataIndex: 'DateOfError', key: 'DateOfError', width: 110 },
  { title: 'Campaign', dataIndex: 'Campaign',   key: 'Campaign',   width: 140, ellipsis: true },
  {
    title: 'Status', dataIndex: 'Status', key: 'Status', width: 120,
    render: (s) => <Tag color={ERROR_STATUS_COLOR[s] ?? 'default'}>{ERROR_STATUS_LABEL[s] ?? s}</Tag>,
  },
  {
    title: 'Acknowledge Deadline', key: 'AcknowledgeDeadline', width: 140,
    render: (_, r) => {
      if (r.Status !== 'completed' || !r.CompletedAt) return null;
      const dl = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
      return <span style={{ color: dayjs().isAfter(dl) ? '#8c8c8c' : '#fa8c16', fontSize: 12 }}>{dl.format('YYYY-MM-DD')}</span>;
    },
  },
  {
    // Sections that produced an error, same idea as Error Category on the
    // coaching session page. Pipe-delimited from the API because a section
    // name can contain a comma.
    title: 'Error Category', dataIndex: 'ErrorSections', key: 'ErrorSections', width: 220,
    render: (v) => {
      const cats = (v || '').split('|').filter(Boolean);
      if (!cats.length) return <Typography.Text type="secondary">—</Typography.Text>;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {cats.map((c) => <Tag key={c}>{c}</Tag>)}
        </div>
      );
    },
  },
  {
    title: 'Errors', dataIndex: 'TotalErrors', key: 'TotalErrors', width: 80, align: 'right',
    render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <Tag>{v}</Tag>,
  },
  {
    title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 90, align: 'right',
    render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>{v}</Tag>,
  },
  {
    // Sessions raised from this submission, linked on ErrorSubmissionID. A
    // submission can raise more than one, so this is a list rather than a
    // single value -- same field and rendering as the error detail page.
    title: 'Coaching Session', dataIndex: 'Coachings', key: 'Coachings', width: 260,
    render: (list) => !list?.length
      ? <Typography.Text type="secondary">—</Typography.Text>
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {list.map((c) => (
            <div key={c.ID} style={{ fontSize: 12, lineHeight: 1.4 }}>
              <Typography.Link onClick={() => window.open(`/coaching/sessions/${c.ID}`, '_blank')}>
                #{c.ID}
              </Typography.Link>
              {c.CreatedOn && <span style={{ color: '#8c8c8c' }}> · {c.CreatedOn}</span>}
              {c.RequestType && <span style={{ color: '#8c8c8c' }}> · {c.RequestType}</span>}
              {c.Linked != null && <span style={{ color: '#8c8c8c' }}> · {c.Linked ? 'Linked Sections' : 'All Sections'}</span>}
              <Tag color={SESSION_STATUS_COLOR[c.Status] ?? 'default'} style={{ marginLeft: 6 }}>
                {SESSION_STATUS_LABEL[c.Status] ?? c.Status}
              </Tag>
            </div>
          ))}
        </div>
      ),
  },
  {
    title: '', key: 'view', width: 70,
    render: (_, r) => <Link to={`/errors/${r.ID}`}><Button size="small">View</Button></Link>,
  },
];

const breakdownColumns = [
  { title: 'Campaign',        dataIndex: 'Campaign',        key: 'Campaign',        ellipsis: true },
  { title: 'Media',           dataIndex: 'Media',           key: 'Media' },
  { title: 'Direction',       dataIndex: 'Direction',       key: 'Direction' },
  { title: 'Calls',           dataIndex: 'CallCount',       key: 'CallCount' },
  { title: 'Avg Talk',        dataIndex: 'AvgAgentTime',    key: 'AvgAgentTime',    render: fmtTime },
  { title: 'Avg ACW',         dataIndex: 'AvgACWTime',      key: 'AvgACWTime',      render: fmtTime },
  { title: 'Avg AHT',         dataIndex: 'AvgAHT',          key: 'AvgAHT',          render: fmtTime },
  { title: 'Called Support',  dataIndex: 'CalledSupportQty', key: 'CalledSupportQty', align: 'right' },
  { title: 'Called Tier 2',   dataIndex: 'CalledTier2Qty',   key: 'CalledTier2Qty',   align: 'right' },
];

export default function AgentDetailPanel({ agentUserId, agentRecord, canCreateIncident = true, showRankingSection = true, hideCCaasStatus = false, hideUpsell = false, hideActionsButton = false, hideAdherenceMonthlyTables = false, actionHandlerRef = null }) {
  const [wfmSkillsOpen, setWfmSkillsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [colWidths, setColWidths] = useState({});

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  // Tab, date range and campaign live in the URL too (?tab=&from=&to=&campaign=),
  // so the Back chip, the browser's Back and a shared link return to exactly
  // this view.
  const [activeTab, setActiveTab]     = useState(searchParams.get('tab') || 'todo');
  const [dateRange, setDateRange]     = useState(() => {
    const f = dayjs(searchParams.get('from') || '', 'YYYY-MM-DD', true);
    const t = dayjs(searchParams.get('to') || '', 'YYYY-MM-DD', true);
    return f.isValid() && t.isValid() ? [f, t] : [dayjs().subtract(3, 'month').startOf('month'), dayjs().add(1, 'day')];
  });
  const [recapOpen, setRecapOpen]     = useState(false);
  const [campaign, setCampaign]       = useState(() => Number(searchParams.get('campaign')) || null);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [callDirection,    setCallDirection]    = useState('Inbound');
  const [excludeNoContact, setExcludeNoContact] = useState(false);
  const [callSkillView,    setCallSkillView]    = useState('campaign');
  const [skillMetric,   setSkillMetric]   = useState('AHT');
  const [callStats, setCallStats]         = useState(null);
  const [dailyStats, setDailyStats]       = useState([]);
  const [ahtTrend,  setAhtTrend]          = useState([]);
  const [refusals,  setRefusals]          = useState(null);
  const [refusalsView, setRefusalsView]   = useState('month');
  const [evalHistory,     setEvalHistory]     = useState([]);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [sectionStats, setSectionStats]     = useState([]);
  const [sectionMode, setSectionMode]       = useState('summary');
  const [sectionLoading, setSectionLoading] = useState(false);
  const [questionStats, setQuestionStats]   = useState([]);
  const [questionMode, setQuestionMode]     = useState('summary');
  const [questionLoading, setQuestionLoading] = useState(false);
  // CNE daily call rows for this agent. hasAwsId decides whether the tab is
  // offered at all: an agent with an AWS ID and no rows gets an empty tab, one
  // without the id gets no tab, and those are different answers.
  const [awsCalls, setAwsCalls]               = useState([]);
  const [awsHasId, setAwsHasId]               = useState(false);
  // Evaluations of this agent ever (any date), or null until known.
  const [evalCountEver, setEvalCountEver]     = useState(null);
  const [awsLoading, setAwsLoading]           = useState(false);
  const [errorHistory, setErrorHistory]         = useState([]);
  const [errorLoading, setErrorLoading]         = useState(false);
  const [errorQuestionStats, setErrorQuestionStats] = useState([]);
  const [errorQsLoading, setErrorQsLoading]     = useState(false);
  const [errorSecStats,    setErrorSecStats]    = useState([]);
  const [errorSecLoading,  setErrorSecLoading]  = useState(false);
  const [errorQuestionMode, setErrorQuestionMode] = useState('summary');
  const [loading, setLoading]         = useState(false);
  const [complianceRows, setComplianceRows]   = useState([]);
  const [activeWarningRows, setActiveWarningRows] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [notifRows, setNotifRows]     = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [coachingSessions, setCoachingSessions]   = useState([]);
  const [coachingLoading, setCoachingLoading]     = useState(false);
  const [percentileRows, setPercentileRows]       = useState([]);
  const [percentileLoading, setPercentileLoading] = useState(false);
  const [absenceRows, setAbsenceRows]             = useState([]);
  const [absenceLoading, setAbsenceLoading]       = useState(false);
  const [monthlyStats, setMonthlyStats]           = useState([]);
  const [timecard, setTimecard]                   = useState(null);
  const [timecardLoading, setTimecardLoading]     = useState(false);
  const [ccaasMonthly, setCcaasMonthly]           = useState([]);
  const [ccaasMonthlyLoading, setCcaasMonthlyLoading] = useState(false);
  const [ccaasView, setCcaasView]                 = useState(searchParams.get('ccaas_view') || 'summary');
  const [ccaasDaily, setCcaasDaily]               = useState([]);
  const [ccaasDailyLoading, setCcaasDailyLoading] = useState(false);
  const [ccaasDailyRange, setCcaasDailyRange]     = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [ccaasStatuses,  setCcaasStatuses]         = useState([]);
  // AWS Adherence and AWS States: shown only when the agent has AWS data in
  // the range (has_aws), built with DBAL's own columns.
  const [awsSched,       setAwsSched]              = useState({ has_aws: false, rows: [] });
  const [awsStateData,   setAwsStateData]          = useState({ states: [], columns: {}, rows: [] });
  const [awsTabLoading,  setAwsTabLoading]         = useState(false);
  // Help Desk: the HelpDesk tab and two To Do cards (open tickets the agent is
  // on as the Impacted Employee, or only as a watcher). In testing: admin and
  // qa_admin only for now.
  const [hdImpacted,     setHdImpacted]            = useState([]);
  // My Open Project Tasks: tasks assigned to me, not Done.
  const [myProjTasks,    setMyProjTasks]           = useState([]);
  const [myCampUpdates,  setMyCampUpdates]         = useState([]);
  const [hdWatching,     setHdWatching]            = useState([]);
  const [dbalThresholds, setDbalThresholds]        = useState([]);
  const [ccaasOtherData,    setCcaasOtherData]    = useState([]);
  const [ccaasOtherLoading, setCcaasOtherLoading] = useState(false);
  const [ccaasLog,             setCcaasLog]             = useState([]);
  const [ccaasLogLoading,      setCcaasLogLoading]      = useState(false);
  const [ccaasDate,            setCcaasDate]            = useState(() => {
    const d = searchParams.get('date');
    return d ? dayjs(d) : dayjs().subtract(1, 'day');
  });
  const [timelineStateOptions,   setTimelineStateOptions]   = useState([]);
  const [selectedTimelineStates, setSelectedTimelineStates] = useState([]);
  const [pendingTimelineState,   setPendingTimelineState]   = useState(null);
  const [complianceModal, setComplianceModal] = useState(null); // { day, description }
  const [complianceRefreshKey, setComplianceRefreshKey] = useState(0);
  const [todoEvals,      setTodoEvals]      = useState([]);
  const [todoFeedback,   setTodoFeedback]   = useState([]);
  const [todoErrors,     setTodoErrors]     = useState([]);
  const [todoCompliance, setTodoCompliance] = useState([]);
  const [todoAbsences,   setTodoAbsences]   = useState([]);
  const [todoAhtTrend,   setTodoAhtTrend]   = useState([]);
  const [todoLoading,          setTodoLoading]          = useState(false);
  const [todoAssignedEvals,      setTodoAssignedEvals]      = useState([]);
  const [todoAssignedFeedback,   setTodoAssignedFeedback]   = useState([]);
  const [todoAssignedErrors,     setTodoAssignedErrors]     = useState([]);
  const [todoAssignedCompliance, setTodoAssignedCompliance] = useState([]);
  const [coachingAsCoach,        setCoachingAsCoach]        = useState([]);
  const [todoVariances,          setTodoVariances]          = useState([]);

  // Action modal state
  const [actionErrorOpen,   setActionErrorOpen]   = useState(false);
  const [actionAbsenceOpen, setActionAbsenceOpen] = useState(false);

  const [actionCoachingOpen, setActionCoachingOpen] = useState(false);
  const [badgesOpen,         setBadgesOpen]         = useState(false);

  // Current (unexpired) badges for the header strip.
  const [badges, setBadges] = useState([]);
  const loadBadges = useCallback(() => {
    if (!agentUserId) { setBadges([]); return; }
    api.get(`/api/users/${agentUserId}/badges`)
      .then((r) => setBadges(r.data || []))
      .catch(() => setBadges([]));
  }, [agentUserId]);
  useEffect(() => { loadBadges(); }, [loadBadges]);

  const currentUser = useContext(UserContext);
  const [employeeProfile, setEmployeeProfile]     = useState(null);
  const [cxoneData,            setCxoneData]            = useState([]);
  const [cxoneLoading,         setCxoneLoading]         = useState(false);
  const [cxoneSections,        setCxoneSections]        = useState([]);
  const [cxoneSectionsLoading, setCxoneSectionsLoading] = useState(false);
  const [cxoneSectionMode,     setCxoneSectionMode]     = useState('summary');
  const [cxoneQuestions,       setCxoneQuestions]       = useState([]);
  const [cxoneQuestionsLoading,setCxoneQuestionsLoading]= useState(false);
  const [cxoneQuestionMode,    setCxoneQuestionMode]    = useState('summary');

  const [rankData,        setRankData]        = useState(null);
  const [rankLoading,     setRankLoading]     = useState(false);
  const [rankViewMode,    setRankViewMode]    = useState('single');
  const [rankSingleMonth, setRankSingleMonth] = useState(
    dayjs().date() >= 16 ? dayjs().format('YYYY-MM') : dayjs().subtract(1, 'month').format('YYYY-MM')
  );
  const [rankMonthRange,  setRankMonthRange]  = useState(null);
  const [rankMinCalls,    setRankMinCalls]    = useState(20);
  const [rankPeerScope,   setRankPeerScope]   = useState('all');
  const [goals,           setGoals]           = useState([]);
  const [agentCheers,     setAgentCheers]     = useState([]);
  const [cheersSince,     setCheersSince]     = useState('');
  const [upsellStats,     setUpsellStats]     = useState(null);

  const navigate = useNavigate();
  const start = dateRange?.[0]?.format('YYYY-MM-DD');
  const end   = dateRange?.[1]?.format('YYYY-MM-DD');
  // Outstanding means unacknowledged, not "unacknowledged and still inside the
  // dispute window". A record whose deadline has passed is the one most worth
  // chasing, and Run-AutoAcknowledge now closes it a fortnight later, so
  // nothing sits here forever.
  const todoActionCount =
    todoEvals.filter((r) => r.Status === 'rescore_requested' || (r.Status === 'completed' && !r.AcknowledgedAt)).length +
    todoErrors.filter((r) => r.Status === 'rescore_requested' || (r.Status === 'completed' && !r.AcknowledgedAt)).length +
    coachingSessions.filter((r) => r.Status === 'active' && !['completed','canceled'].includes(r.MyStatus || '')).length +
    todoFeedback.filter((r) => r.Status === 'rescore_requested' || (r.Status === 'completed' && !r.AcknowledgedAt)).length +
    todoCompliance.filter((r) => ['complete', 'inactive'].includes(r.Status) && !r.AcknowledgedAt).length +
    hdImpacted.length + hdWatching.length + myProjTasks.length + myCampUpdates.length +
    (agentRecord == null ? (
      todoAssignedEvals.filter((r) => !['completed', 'canceled'].includes(r.Status)).length +
      todoAssignedFeedback.filter((r) => !['completed', 'canceled'].includes(r.Status)).length +
      todoAssignedErrors.filter((r) => !['completed', 'canceled'].includes(r.Status)).length +
      todoAssignedCompliance.filter((r) => !['complete', 'inactive', 'canceled'].includes(r.Status)).length +
      coachingAsCoach.filter((r) => !['completed', 'canceled'].includes((r.Status || '').toLowerCase())).length +
      todoVariances.length
    ) : 0);

  useEffect(() => {
    if (!agentUserId) { setCxoneData([]); return; }
    setCxoneLoading(true);
    api.get('/api/agent/cxone-scorecards', { params: { user_id: agentUserId, start_date: start, end_date: end } })
      .then((r) => setCxoneData(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCxoneData([]))
      .finally(() => setCxoneLoading(false));
  }, [agentUserId, start, end]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setCxoneSections([]); return; }
    const controller = new AbortController();
    setCxoneSections([]);
    setCxoneSectionsLoading(true);
    const apiMode = cxoneSectionMode === 'month_pivot' || cxoneSectionMode === 'occ_pivot' ? 'month' : cxoneSectionMode;
    api.get('/api/agent/cxone-section-stats', { params: { user_id: agentUserId, start_date: start, end_date: end, mode: apiMode }, signal: controller.signal })
      .then((r) => setCxoneSections(Array.isArray(r.data) ? r.data : []))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setCxoneSections([]); })
      .finally(() => setCxoneSectionsLoading(false));
    return () => controller.abort();
  }, [agentUserId, start, end, cxoneSectionMode]); // eslint-disable-line

  // Merge rows whose SectionName differs only by the trailing "- XX pts" suffix.
  // Uses point value as the weight so the merged score is mathematically exact.
  const cxoneSectionsMerged = useMemo(() => {
    const PTS_RE = /\s*-\s*(\d+)\s*pts\s*$/i;
    const byMonth = cxoneSectionMode !== 'summary';
    const map = new Map();
    const order = [];
    cxoneSections.forEach((r) => {
      const m = r.SectionName.match(PTS_RE);
      const baseName = m ? r.SectionName.replace(PTS_RE, '').trim() : r.SectionName;
      const maxPts   = m ? parseInt(m[1], 10) : 1;
      const k = byMonth
        ? `${r.Campaign}||${baseName}||${r.MonthKey}`
        : `${r.Campaign}||${baseName}`;
      if (!map.has(k)) {
        map.set(k, { ...r, SectionName: baseName, EvalCount: 0, FPCount: 0, PPCount: 0, _ws: 0, _wt: 0 });
        order.push(k);
      }
      const acc = map.get(k);
      const pct    = parseFloat(r.SectionScorePct) || 0;
      const weight = (r.EvalCount || 0) * maxPts;
      acc.EvalCount += r.EvalCount || 0;
      acc.FPCount   += r.FPCount   || 0;
      acc.PPCount   += r.PPCount   || 0;
      acc._ws       += pct * weight;
      acc._wt       += weight;
    });
    return order.map((k) => {
      const row = map.get(k);
      const merged = row._wt > 0 ? (Math.round(row._ws / row._wt * 100) / 100).toFixed(2) : null;
      return { ...row, SectionScorePct: merged };
    });
  }, [cxoneSections, cxoneSectionMode]);

  useEffect(() => {
    if (!agentUserId) { setCxoneQuestions([]); return; }
    const controller = new AbortController();
    setCxoneQuestions([]);
    setCxoneQuestionsLoading(true);
    const apiMode = cxoneQuestionMode === 'month_pivot' ? 'month' : cxoneQuestionMode;
    api.get('/api/agent/cxone-question-stats', { params: { user_id: agentUserId, start_date: start, end_date: end, mode: apiMode }, signal: controller.signal })
      .then((r) => setCxoneQuestions(Array.isArray(r.data) ? r.data : []))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setCxoneQuestions([]); })
      .finally(() => setCxoneQuestionsLoading(false));
    return () => controller.abort();
  }, [agentUserId, start, end, cxoneQuestionMode]); // eslint-disable-line

  const [cxoneQuestionsExporting, setCxoneQuestionsExporting] = useState(false);

  const handleExportCxoneQuestions = async () => {
    if (!agentUserId) return;
    setCxoneQuestionsExporting(true);
    try {
      const r = await api.get('/api/agent/cxone-question-stats', {
        params: { user_id: agentUserId, start_date: start, end_date: end, mode: 'month' },
      });
      const rows = Array.isArray(r.data) ? r.data : [];
      const PTS_RE = /\s*-\s*(\d+)\s*pts\s*$/i;
      const cols = [
        { label: 'Agent',     key: '_Agent' },
        { label: 'Section',   key: '_Sec' },
        { label: 'Question',  key: 'QuestionName' },
        { label: 'Campaign',  key: 'Campaign' },
        { label: 'Month',     key: 'Month' },
        { label: 'Count',     key: 'AnswerCount' },
        { label: 'Score %',   key: 'QuestionScorePct' },
        { label: 'FP',        key: 'FPCount' },
        { label: 'PP',        key: 'PPCount' },
      ];
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return (s.includes(',') || s.includes('"') || s.includes('\n'))
          ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const header = cols.map((c) => esc(c.label)).join(',');
      const body = rows.map((row) => {
        const r2 = {
          ...row,
          _Agent: agentName || '',
          _Sec: row.SectionName ? row.SectionName.replace(PTS_RE, '').trim() : row.SectionName,
        };
        return cols.map((c) => esc(r2[c.key])).join(',');
      }).join('\n');
      const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${agentName ? agentName.replace(/[^a-z0-9]/gi, '_') : 'agent'}-cxone-questions.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Export failed.');
    } finally {
      setCxoneQuestionsExporting(false);
    }
  };

  useEffect(() => {
    if (!agentUserId) { setAbsenceRows([]); return; }
    setAbsenceLoading(true);
    api.get('/api/absences', { params: { user_id: agentUserId, start_date: start } })
      .then((r) => setAbsenceRows(r.data))
      .catch(() => setAbsenceRows([]))
      .finally(() => setAbsenceLoading(false));
  }, [agentUserId, start, end]); // eslint-disable-line


  useEffect(() => {
    if (!agentUserId) { setMonthlyStats([]); return; }
    api.get('/api/agent/monthly-stats', { params: { user_id: agentUserId } })
      .then((r) => setMonthlyStats(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMonthlyStats([]));
  }, [agentUserId]); // eslint-disable-line


  useEffect(() => {
    api.get('/api/goals').then((r) => setGoals(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!agentUserId) { setRankData(null); return; }
    const controller = new AbortController();
    setRankLoading(true);
    const params = { user_id: agentUserId, min_calls: rankMinCalls, scope: rankPeerScope };
    if (rankViewMode === 'single') {
      if (rankSingleMonth) { params.month_from = rankSingleMonth; params.month_to = rankSingleMonth; }
    } else {
      if (rankMonthRange?.[0]) {
        params.month_from = rankMonthRange[0].format('YYYY-MM');
        params.month_to   = rankMonthRange[1].format('YYYY-MM');
      }
    }
    api.get('/api/agent/percentile-rank', { params, signal: controller.signal })
      .then((r) => {
        setRankData(r.data);
        if (rankViewMode === 'trend' && !rankMonthRange && r.data?.month_from)
          setRankMonthRange([dayjs(r.data.month_from + '-01'), dayjs(r.data.month_to + '-01')]);
      })
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setRankData(null); })
      .finally(() => setRankLoading(false));
    return () => controller.abort();
  }, [agentUserId, rankViewMode, rankSingleMonth, rankMonthRange, rankMinCalls, rankPeerScope]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setEmployeeProfile(null); return; }
    api.get('/api/agent/employee-profile', { params: { user_id: agentUserId } })
      .then((r) => setEmployeeProfile(r.data))
      .catch(() => setEmployeeProfile(null));
  }, [agentUserId]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setHdImpacted([]); setHdWatching([]); return; }
    fetchInvolvedTickets({ userId: agentUserId, involvedAs: 'impacted', statuses: HD_OPEN_STATUSES })
      .then(setHdImpacted).catch(() => setHdImpacted([]));
    fetchInvolvedTickets({ userId: agentUserId, involvedAs: 'watcher', statuses: HD_OPEN_STATUSES })
      .then(setHdWatching).catch(() => setHdWatching([]));
  }, [agentUserId]);

  // Only on your own summary (someone else's project tasks may be ones you
  // cannot see), and only with Projects Access.
  const projTasksShown = !!agentUserId && agentUserId === currentUser?.ID && currentUser?.Role !== 'agent'
    && (currentUser?.Role === 'admin' || (currentUser?.CanEditProjects ?? 0) >= 1);
  useEffect(() => {
    if (!projTasksShown) { setMyProjTasks([]); return; }
    api.get('/api/project-tasks', { params: { assigned_to: 'me', status: 'Open,In Progress' } })
      .then((r) => setMyProjTasks(r.data || [])).catch(() => setMyProjTasks([]));
  }, [projTasksShown]);

  // Campaign Updates waiting for my acknowledgement -- my own summary only
  // (acknowledging is a person's own action). Everyone, like My Campaign
  // Updates.
  const campUpdatesShown = !!agentUserId && agentUserId === currentUser?.ID;
  const loadCampUpdates = useCallback(() => {
    if (!campUpdatesShown) { setMyCampUpdates([]); return; }
    api.get('/api/campaign-updates/mine').then((r) => setMyCampUpdates(r.data || [])).catch(() => setMyCampUpdates([]));
  }, [campUpdatesShown]);
  useEffect(() => { loadCampUpdates(); }, [loadCampUpdates]);
  // Acknowledging happens in the update's read panel (CampaignUpdateReadModal),
  // not from the list.
  const [readUpdate, setReadUpdate] = useState(null);

  // Same range as Adherence (the timecard), so the tabs line up.
  useEffect(() => {
    if (!agentUserId) { setAwsSched({ has_aws: false, rows: [] }); setAwsStateData({ states: [], columns: {}, rows: [] }); return; }
    setAwsTabLoading(true);
    const p = { user_id: agentUserId, end_date: end };
    Promise.all([
      api.get('/api/agent/aws-schedule', { params: p }).then((r) => r.data).catch(() => ({ has_aws: false, rows: [] })),
      api.get('/api/agent/aws-state-daily', { params: p }).then((r) => r.data).catch(() => ({ states: [], columns: {}, rows: [] })),
    ]).then(([sch, st]) => { setAwsSched(sch); setAwsStateData(st); })
      .finally(() => setAwsTabLoading(false));
  }, [agentUserId, end]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setTimecard(null); return; }
    setTimecardLoading(true);
    api.get('/api/agent/timecard', { params: { user_id: agentUserId, end_date: end } })
      .then((r) => setTimecard(r.data))
      .catch(() => setTimecard(null))
      .finally(() => setTimecardLoading(false));
  }, [agentUserId, end]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setCcaasMonthly([]); return; }
    setCcaasMonthlyLoading(true);
    api.get('/api/agent/ccaas-monthly', { params: { user_id: agentUserId, start_date: start, end_date: end } })
      .then((r) => setCcaasMonthly(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCcaasMonthly([]))
      .finally(() => setCcaasMonthlyLoading(false));
  }, [agentUserId, start, end]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId || ccaasView !== 'summary') return;
    const controller = new AbortController();
    setCcaasDailyLoading(true);
    const ds = ccaasDailyRange?.[0]?.format('YYYY-MM-DD');
    const de = ccaasDailyRange?.[1]?.format('YYYY-MM-DD');
    api.get('/api/agent/ccaas-daily', { params: { user_id: agentUserId, start_date: ds, end_date: de }, signal: controller.signal })
      .then((r) => setCcaasDaily(Array.isArray(r.data) ? r.data : []))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setCcaasDaily([]); })
      .finally(() => setCcaasDailyLoading(false));
    return () => controller.abort();
  }, [agentUserId, ccaasView, ccaasDailyRange]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId || ccaasView !== 'other') return;
    const controller = new AbortController();
    setCcaasOtherLoading(true);
    const ds = ccaasDailyRange?.[0]?.format('YYYY-MM-DD');
    const de = ccaasDailyRange?.[1]?.format('YYYY-MM-DD');
    api.get('/api/agent/ccaas-other-daily', { params: { user_id: agentUserId, start_date: ds, end_date: de }, signal: controller.signal })
      .then((r) => setCcaasOtherData(Array.isArray(r.data) ? r.data : []))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') { setCcaasOtherData([]); message.error(err.response?.data?.error || 'Failed to load Other Stats data.'); } })
      .finally(() => setCcaasOtherLoading(false));
    return () => controller.abort();
  }, [agentUserId, ccaasView, ccaasDailyRange]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId || ccaasView !== 'log') return;
    const controller = new AbortController();
    setCcaasLogLoading(true);
    const d = ccaasDate.format('YYYY-MM-DD');
    api.get('/api/agent/ccaas-log', { params: { user_id: agentUserId, start_date: d, end_date: d }, signal: controller.signal })
      .then((r) => setCcaasLog(Array.isArray(r.data) ? r.data : []))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setCcaasLog([]); })
      .finally(() => setCcaasLogLoading(false));
    return () => controller.abort();
  }, [agentUserId, ccaasView, ccaasDate]); // eslint-disable-line

  useEffect(() => {
    api.get('/api/admin/ccaas-statuses').then((r) => setCcaasStatuses(r.data)).catch(() => {});
    api.get('/api/admin/dbal-thresholds').then((r) => setDbalThresholds(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!agentUserId) { setPercentileRows([]); return; }
    setPercentileLoading(true);
    api.get(`/api/users/${agentUserId}/percentiles`)
      .then((r) => setPercentileRows(r.data))
      .catch(() => setPercentileRows([]))
      .finally(() => setPercentileLoading(false));
  }, [agentUserId]);

  useEffect(() => {
    if (!agentUserId) { setAgentCheers([]); setCheersSince(''); return; }
    api.get('/api/cheers/agent', { params: { user_id: agentUserId } })
      .then((r) => {
        const d = r.data;
        if (d && Array.isArray(d.cheers)) {
          setAgentCheers(d.cheers);
          setCheersSince(d.min_date || '');
        } else {
          // fallback for old array format
          setAgentCheers(Array.isArray(d) ? d : []);
        }
      })
      .catch(() => { setAgentCheers([]); setCheersSince(''); });
  }, [agentUserId]);

  useEffect(() => {
    if (!agentUserId) { setUpsellStats(null); return; }
    const params = { user_id: agentUserId };
    if (campaign) params.campaign_id = campaign;
    api.get('/api/agent/upsell-stats', { params })
      .then((r) => setUpsellStats(r.data))
      .catch(() => setUpsellStats(null));
  }, [agentUserId, campaign]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setCoachingSessions([]); return; }
    setCoachingLoading(true);
    const dueDateFrom = dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD');
    const dueDateTo   = dayjs().add(30, 'day').format('YYYY-MM-DD');
    const params = { view: 'participant', participant_user_id: agentUserId, due_date_from: dueDateFrom, due_date_to: dueDateTo };
    if (campaign) params.campaign_id = campaign;
    api.get('/api/coaching/sessions', { params })
      .then((r) => setCoachingSessions(r.data))
      .catch(() => setCoachingSessions([]))
      .finally(() => setCoachingLoading(false));
  }, [agentUserId, campaign]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setComplianceRows([]); return; }
    setComplianceLoading(true);
    Promise.all([
      (agentUserId === currentUser?.ID || (currentUser?.CanEditIncidents ?? 0) >= 1)
        ? api.get('/api/compliance', { params: {
            type: 'incident',
            ...(agentUserId === currentUser?.ID ? { mine: '1' } : { employee_user_id: agentUserId }),
            date_from: start,
            date_to: end,
          }})
        : Promise.resolve({ data: [] }),
      // Formal write-ups: never a client's own login.
      (currentUser?.CanEditFormalWriteups ?? 0) >= 1 && currentUser?.Role !== 'client'
        ? api.get('/api/compliance', { params: { type: 'writeup', employee_user_id: agentUserId, date_from: start, date_to: end } })
        : Promise.resolve({ data: [] }),
    ])
      .then(([incRes, wpRes]) => {
        const combined = [...incRes.data, ...wpRes.data]
          .sort((a, b) => (b.DateOfIncident || b.CreatedAt).localeCompare(a.DateOfIncident || a.CreatedAt));
        setComplianceRows(combined);
      })
      .catch(() => setComplianceRows([]))
      .finally(() => setComplianceLoading(false));
  }, [agentUserId, start, end, complianceRefreshKey]); // eslint-disable-line

  // Active warnings: completed records carrying a warning whose Warning Expires
  // On date has not passed. Not limited to the date range above -- a warning
  // issued before the range is still in force until it expires.
  useEffect(() => {
    if (!agentUserId) { setActiveWarningRows([]); return; }
    const own = agentUserId === currentUser?.ID;
    Promise.all([
      (own || (currentUser?.CanEditIncidents ?? 0) >= 1)
        ? api.get('/api/compliance', { params: { type: 'incident', ...(own ? { mine: '1' } : { employee_user_id: agentUserId }) } })
        : Promise.resolve({ data: [] }),
      (currentUser?.CanEditFormalWriteups ?? 0) >= 1 && currentUser?.Role !== 'client'
        ? api.get('/api/compliance', { params: { type: 'writeup', employee_user_id: agentUserId } })
        : Promise.resolve({ data: [] }),
    ])
      .then(([incRes, wpRes]) => {
        setActiveWarningRows(
          [...incRes.data, ...wpRes.data]
            .filter((r) => r.Status === 'complete' && r.WarningNumber
              && r.WarningExpiresOn && r.WarningExpiresOn >= dayjs().format('YYYY-MM-DD'))
            .sort((a, b) => (b.DateOfIncident || '').localeCompare(a.DateOfIncident || ''))
        );
      })
      .catch(() => setActiveWarningRows([]));
  }, [agentUserId, complianceRefreshKey]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setNotifRows([]); return; }
    setNotifLoading(true);
    api.get('/api/notifications', { params: { agent_user_id: agentUserId, status: 'Open,In_Progress' } })
      .then((r) => setNotifRows((r.data || []).sort((a, b) => (b.CreateDate || '').localeCompare(a.CreateDate || ''))))
      .catch(() => setNotifRows([]))
      .finally(() => setNotifLoading(false));
  }, [agentUserId]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setCampaignOptions([]); setCampaign(null); return; }
    api.get('/api/agent/campaigns', { params: { user_id: agentUserId } })
      .then((r) => {
        setCampaignOptions(r.data.map((c) => ({ label: c.Name, value: c.ID })));
        setCampaign((prev) => (prev && r.data.some((c) => c.ID === prev) ? prev : null));
      })
      .catch(() => setCampaignOptions([]));
  }, [agentUserId]);

  useEffect(() => {
    if (!agentUserId || !start || !end) return;
    const controller = new AbortController();
    const p = { user_id: agentUserId, start_date: start, end_date: end };
    if (callDirection !== 'All') p.direction = callDirection;
    if (campaign) p.campaign = campaign;
    if (callSkillView === 'skill') p.by_skill = '1';
    if (callSkillView === 'dispo') p.by_dispo = '1';
    if (excludeNoContact) p.exclude_no_contact = '1';
    Promise.all([
      api.get('/api/agent/call-stats', { params: p, signal: controller.signal }),
      api.get('/api/agent/aht-trend',  { params: p, signal: controller.signal }),
    ]).then(([statsRes, trendRes]) => {
      setCallStats(statsRes.data);
      setAhtTrend(trendRes.data);
    }).catch((err) => { if (err?.code !== 'ERR_CANCELED') throw err; });
    return () => controller.abort();
  }, [agentUserId, start, end, callDirection, campaign, callSkillView, excludeNoContact]);

  useEffect(() => {
    if (!agentUserId) { setDailyStats([]); return; }
    const controller = new AbortController();
    api.get('/api/agent/call-stats-daily', { params: { user_id: agentUserId }, signal: controller.signal })
      .then((r) => setDailyStats(r.data || []))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setDailyStats([]); });
    return () => controller.abort();
  }, [agentUserId]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId) { setRefusals(null); return; }
    const controller = new AbortController();
    api.get('/api/agent/refusals', { params: { user_id: agentUserId, start_date: start, end_date: end }, signal: controller.signal })
      .then((r) => setRefusals(r.data))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setRefusals(null); });
    return () => controller.abort();
  }, [agentUserId, start, end]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId || !start || !end) return;
    // Another agent's evaluations only with Evaluations access.
    if ((currentUser?.CanEditEvals ?? 0) < 1 && agentUserId !== currentUser?.ID) {
      setEvalHistory([]); setFeedbackHistory([]); return;
    }
    setLoading(true);
    const p = { user_id: agentUserId, start_date: start, end_date: end };
    Promise.all([
      api.get('/api/agent/eval-history', { params: p }),
      api.get('/api/agent/eval-history', { params: { ...p, is_feedback_only: '1' } }),
    ])
      .then(([evalRes, fboRes]) => {
        setEvalHistory(evalRes.data);
        setFeedbackHistory(fboRes.data);
      })
      .finally(() => setLoading(false));
  }, [agentUserId, start, end]);

  useEffect(() => {
    if (!agentUserId || !start || !end) return;
    const controller = new AbortController();
    setSectionLoading(true);
    const apiMode = sectionMode === 'month_pivot' || sectionMode === 'occ_pivot' ? 'month' : sectionMode;
    const params = { user_id: agentUserId, start_date: start, end_date: end, group_by: apiMode };
    if (campaign) params.campaign = campaign;
    api.get('/api/agent/section-stats', { params, signal: controller.signal })
      .then((r) => setSectionStats(r.data))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setSectionStats([]); })
      .finally(() => setSectionLoading(false));
    return () => controller.abort();
  }, [agentUserId, start, end, sectionMode, campaign]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId || !start || !end) return;
    const controller = new AbortController();
    setQuestionLoading(true);
    const apiMode = questionMode === 'month_pivot' ? 'month' : questionMode;
    const params = { user_id: agentUserId, start_date: start, end_date: end, group_by: apiMode };
    if (campaign) params.campaign = campaign;
    api.get('/api/agent/question-stats', { params, signal: controller.signal })
      .then((r) => setQuestionStats(r.data))
      .catch((err) => { if (err?.code !== 'ERR_CANCELED') setQuestionStats([]); })
      .finally(() => setQuestionLoading(false));
    return () => controller.abort();
  }, [agentUserId, start, end, questionMode, campaign]); // eslint-disable-line

  useEffect(() => {
    setEvalCountEver(null);
    if (!agentUserId) return;
    api.get('/api/agent/eval-count', { params: { user_id: agentUserId } })
      .then((r) => setEvalCountEver(r.data?.count ?? null))
      .catch(() => setEvalCountEver(null));
  }, [agentUserId]);

  useEffect(() => {
    if (!agentUserId || !start || !end) { setAwsCalls([]); setAwsHasId(false); return; }
    setAwsLoading(true);
    api.get('/api/agent/aws-calls', { params: { user_id: agentUserId, start_date: start, end_date: end } })
      .then((r) => { setAwsCalls(r.data?.rows || []); setAwsHasId(!!r.data?.has_awsid); })
      .catch(() => { setAwsCalls([]); setAwsHasId(false); })
      .finally(() => setAwsLoading(false));
  }, [agentUserId, start, end]);

  useEffect(() => {
    if (!agentUserId || !start || !end) return;
    if ((currentUser?.CanEditErrors ?? 0) < 1 && agentUserId !== currentUser?.ID) {
      setErrorHistory([]); return;
    }
    setErrorLoading(true);
    const p = { user_id: agentUserId, start_date: start, end_date: end };
    if (campaign) p.campaign = campaign;
    api.get('/api/agent/error-history', { params: p })
      .then((r) => setErrorHistory(r.data))
      .catch(() => setErrorHistory([]))
      .finally(() => setErrorLoading(false));
  }, [agentUserId, start, end, campaign]);

  const fetchErrorQuestionStats = useCallback(() => {
    if (!agentUserId || !start || !end) return;
    setErrorQsLoading(true);
    const apiMode = errorQuestionMode === 'month_pivot' ? 'month' : errorQuestionMode;
    const p = { user_id: agentUserId, start_date: start, end_date: end, group_by: apiMode };
    if (campaign) p.campaign = campaign;
    api.get('/api/agent/error-question-stats', { params: p })
      .then((r) => setErrorQuestionStats(r.data))
      .catch(() => setErrorQuestionStats([]))
      .finally(() => setErrorQsLoading(false));
  }, [agentUserId, start, end, errorQuestionMode, campaign]);

  useEffect(() => { fetchErrorQuestionStats(); }, [fetchErrorQuestionStats]);

  const fetchErrorSecStats = useCallback(() => {
    if (!agentUserId || !start || !end) return;
    setErrorSecLoading(true);
    const apiMode = errorQuestionMode === 'month_pivot' ? 'section_month' : 'section';
    const p = { user_id: agentUserId, start_date: start, end_date: end, group_by: apiMode };
    if (campaign) p.campaign = campaign;
    api.get('/api/agent/error-question-stats', { params: p })
      .then((r) => setErrorSecStats(r.data))
      .catch(() => setErrorSecStats([]))
      .finally(() => setErrorSecLoading(false));
  }, [agentUserId, start, end, errorQuestionMode, campaign]);

  useEffect(() => { fetchErrorSecStats(); }, [fetchErrorSecStats]);

  useEffect(() => {
    if (!agentUserId) {
      setTodoEvals([]); setTodoFeedback([]); setTodoErrors([]);
      setTodoCompliance([]); setTodoAbsences([]); setTodoAhtTrend([]);
      setTodoAssignedEvals([]); setTodoAssignedFeedback([]); setTodoAssignedErrors([]); setTodoAssignedCompliance([]);
      return;
    }
    setTodoLoading(true);
    const today  = dayjs().format('YYYY-MM-DD');
    const ago90  = dayjs().subtract(90, 'day').format('YYYY-MM-DD');
    const ago1yr = dayjs().subtract(1, 'year').format('YYYY-MM-DD');
    const ago30  = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    const ago3m  = dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD');
    const p90      = { user_id: agentUserId, start_date: ago90,  end_date: today };
    const pErrors  = { user_id: agentUserId, start_date: ago1yr, end_date: today };
    const pAssign  = { assigned_to_user_id: agentUserId };
    const own      = agentRecord == null;
    Promise.allSettled([
      api.get('/api/agent/eval-history',  { params: p90 }),
      api.get('/api/agent/eval-history',  { params: { ...p90, is_feedback_only: '1' } }),
      api.get('/api/agent/error-history', { params: pErrors }),
      // Another agent's incidents only with Compliance Incidents access.
      (own || (currentUser?.CanEditIncidents ?? 0) >= 1)
        ? api.get('/api/compliance', { params: own ? { type: 'incident', mine: '1', date_from: ago30, date_to: today } : { type: 'incident', employee_user_id: agentUserId, date_from: ago30, date_to: today } })
        : Promise.resolve({ data: [] }),
      api.get('/api/compliance', { params: own ? { type: 'writeup',  mine: '1', date_from: ago30, date_to: today } : { type: 'writeup',  employee_user_id: agentUserId, date_from: ago30, date_to: today } }),
      api.get('/api/absences',   { params: { user_id: agentUserId, start_date: today, end_date: dayjs().add(1, 'year').format('YYYY-MM-DD') } }),
      api.get('/api/agent/aht-trend', { params: { user_id: agentUserId, start_date: ago3m, end_date: today } }),
      own ? api.get('/api/agent/eval-history',  { params: pAssign }) : Promise.resolve({ data: [] }),
      own ? api.get('/api/agent/eval-history',  { params: { ...pAssign, is_feedback_only: '1' } }) : Promise.resolve({ data: [] }),
      own ? api.get('/api/agent/error-history', { params: pAssign }) : Promise.resolve({ data: [] }),
      own ? api.get('/api/compliance', { params: { type: 'incident', assigned_to_user_id: agentUserId } }) : Promise.resolve({ data: [] }),
    ]).then((results) => {
      const get = (i) => results[i].status === 'fulfilled' ? (results[i].value.data || []) : [];
      const evR = get(0); const fbR = get(1); const erR = get(2);
      const incR = get(3); const wpR = get(4); const absR = get(5); const ahtR = get(6);
      const aEvR = get(7); const aFbR = get(8); const aErR = get(9); const aCompR = get(10);
      setTodoEvals(evR);
      setTodoFeedback(fbR);
      setTodoErrors(erR);
      setTodoCompliance([
        ...incR.map((r) => ({ ...r, Type: 'incident' })),
        ...wpR.map((r)  => ({ ...r, Type: 'writeup'  })),
      ]);
      setTodoAbsences(absR);
      setTodoAhtTrend(ahtR);
      setTodoAssignedEvals(aEvR);
      setTodoAssignedFeedback(aFbR);
      setTodoAssignedErrors(aErR);
      setTodoAssignedCompliance(aCompR);
    }).finally(() => setTodoLoading(false));
  }, [agentUserId]); // eslint-disable-line

  useEffect(() => {
    if (!agentUserId || agentRecord != null) { setCoachingAsCoach([]); return; }
    api.get('/api/coaching/sessions', { params: { view: 'coach' } })
      .then((r) => setCoachingAsCoach(r.data || []))
      .catch(() => setCoachingAsCoach([]));
  }, [agentUserId, agentRecord]); // eslint-disable-line

  // Supervisors (JobID 6) see their own team's Open schedule variances on
  // their own summary. The list is the Schedule Variance Log's, so it needs
  // View on that log as well.
  const showVarianceTodo = agentRecord == null && currentUser?.JobID === 6
    && (currentUser?.CanEditScheduleVariance ?? 0) >= 1 && !!currentUser?.TeamID;
  useEffect(() => {
    if (!agentUserId || !showVarianceTodo) { setTodoVariances([]); return; }
    api.get('/api/schedule-variances', { params: { status: 'Open', team_id: currentUser.TeamID } })
      .then((r) => setTodoVariances(r.data || []))
      .catch(() => setTodoVariances([]));
  }, [agentUserId, showVarianceTodo, currentUser?.TeamID]); // eslint-disable-line

  const agentName = agentRecord?.DisplayName ?? currentUser?.DisplayName;

  const openComplianceModal = (day, state, mins, count, customDesc) => {
    const description = customDesc != null
      ? customDesc
      : `CCaaS: ${state} - ${mins} min on ${dayjs(day).format('M/D/YY (ddd)')}`;
    setComplianceModal({ day, description });
  };

  // ── Action menu handlers ──────────────────────────────────────────────────




  // Client-side campaign filter
  const filteredEvalHistory = campaign
    ? evalHistory.filter((e) => e.CampaignID === campaign)
    : evalHistory;

  const filteredFeedbackHistory = campaign
    ? feedbackHistory.filter((e) => e.CampaignID === campaign)
    : feedbackHistory;

  const pendingCount   = filteredEvalHistory.filter((e) => ['assigned', 'in_progress'].includes(e.Status)).length;
  const rescoreCount   = filteredEvalHistory.filter((e) => e.Status === 'rescore_requested').length;
  const completedCount = filteredEvalHistory.filter((e) => e.Status === 'completed').length;
  const scores         = filteredEvalHistory.filter((e) => e.ScorePct !== null).map((e) => e.ScorePct);
  const avgScore       = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10 : null;

  const errorPendingCount   = errorHistory.filter((e) => ['assigned', 'in_progress'].includes(e.Status)).length;
  const errorDisputedCount  = errorHistory.filter((e) => e.Status === 'rescore_requested').length;
  const errorCompletedCount = errorHistory.filter((e) => e.Status === 'completed').length;
  const totalErrorCount     = errorHistory.reduce((sum, e) => sum + (e.TotalErrors    || 0), 0);
  const totalFeedbackCount  = errorHistory.reduce((sum, e) => sum + (e.TotalFeedbacks || 0), 0);

  const scoreByMonth = (() => {
    const byMonth = {};
    const avg = (arr) => arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 10) / 10 : null;
    filteredEvalHistory
      .filter((e) => e.Status === 'completed' && e.ScorePct !== null)
      .forEach((e) => {
        const mk = (e.CallDate || '').substring(0, 7);
        if (!mk) return;
        if (!byMonth[mk]) byMonth[mk] = { scores: [], minPcts: [], targetPcts: [], highPcts: [] };
        byMonth[mk].scores.push(e.ScorePct);
        // Campaign goals are already percentages; the form thresholds these
        // replace were in points and had to be divided by the form's max.
        if (e.GoalSilverPct   != null) byMonth[mk].minPcts.push(e.GoalSilverPct);
        if (e.GoalTargetPct   != null) byMonth[mk].targetPcts.push(e.GoalTargetPct);
        if (e.GoalPlatinumPct != null) byMonth[mk].highPcts.push(e.GoalPlatinumPct);
      });
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mk, data]) => ({
        MonthKey:       mk,
        Month:          dayjs(mk + '-01').format('MMM YYYY'),
        AvgScore:       avg(data.scores),
        Count:          data.scores.length,
        AvgMinScore:    avg(data.minPcts),
        AvgTargetScore: avg(data.targetPcts),
        AvgHighScore:   avg(data.highPcts),
      }));
  })();

  const isSelf = agentUserId === currentUser?.ID;

  // Keep the view in the URL (replace, not push: no history entry per click).
  const urlFrom = dateRange?.[0]?.format('YYYY-MM-DD');
  const urlTo   = dateRange?.[1]?.format('YYYY-MM-DD');
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const put = (k, v) => (v ? next.set(k, String(v)) : next.delete(k));
      put('tab', activeTab !== 'todo' ? activeTab : null);
      put('from', urlFrom); put('to', urlTo);
      put('campaign', campaign);
      return next.toString() === prev.toString() ? prev : next;
    }, { replace: true });
  }, [agentUserId, activeTab, urlFrom, urlTo, campaign]); // eslint-disable-line react-hooks/exhaustive-deps

  // The Back chip's name for this view: whose summary and which tab.
  const TAB_NAMES = { todo: 'To Do', evaluations: 'Evaluations', errors: 'Errors', coaching: 'Coaching',
    compliance: 'Compliance', helpdesk: 'HelpDesk', absences: 'Absence Notifications', timecard: 'Adherence',
    cheers: 'Cheers for Peers', 'cxone-scorecards': 'CXOne Evals' };
  usePageLabel(isSelf ? `My Summary · ${TAB_NAMES[activeTab] || activeTab}`
    : agentName ? `Agent Summary: ${agentName} · ${TAB_NAMES[activeTab] || activeTab}` : null);
  // An AWS agent with no evaluation ever: no Evaluations tab. Comes back with
  // their first evaluation (e.g. after moving to an evaluated team).
  const awsNoEvals = awsHasId && evalCountEver === 0;
  const _isAgentRole = ['agent', 'support_agent'].includes(currentUser?.Role);
  // A client's own login (Role 'client') goes by its permissions even on its
  // own summary, never gets To Do or Adherence, and starts on Rankings.
  const _isClient = currentUser?.Role === 'client';
  const _hiddenTabs = isSelf && !_isClient ? new Set(awsNoEvals ? ['evaluations'] : []) : new Set([
    ...((currentUser?.CanEditEvals     ?? 0) < 1 ? ['evaluations', 'cxone-scorecards'] : []),
    ...(awsNoEvals ? ['evaluations'] : []),
    ...((currentUser?.CanEditIncidents ?? 0) < 1 ? ['compliance'] : []),
    ...((currentUser?.CanEditCoaching  ?? 0) < 1 ? ['coaching'] : []),
    ...((currentUser?.CanEditErrors    ?? 0) < 1 ? ['errors'] : []),
    ...((currentUser?.CanEditHelpDesk  ?? 0) < 1 ? ['helpdesk'] : []),
    ...((currentUser?.CanEditAbsences  ?? 0) < 1 ? ['absences'] : []),
  ]);
  if (_isClient) { _hiddenTabs.add('todo'); _hiddenTabs.add('timecard'); _hiddenTabs.add('helpdesk'); }
  // Month View's Agent Monthly Stats columns: each measure needs its area's
  // access, but on your own summary (not a client's own login).
  const mvCan = (k) => (isSelf && !_isClient) || (currentUser?.[k] ?? 0) >= 1;
  const _activeTab = _hiddenTabs.has(activeTab) ? (_isClient ? 'rankings' : 'todo') : activeTab;

  return (
    <>
      <WFMSkillsDrawer userId={wfmSkillsOpen ? agentUserId : null} onClose={() => setWfmSkillsOpen(false)} />
      {agentUserId && _activeTab !== 'todo' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          {!['cheers', 'compliance', 'absences', 'timecard', 'aws-adherence', 'aws-states', 'ccaas-status', 'helpdesk'].includes(activeTab) && (campaignOptions.length === 0 ? (
            <Typography.Text type="secondary" style={{ fontStyle: 'italic' }}>
              No campaign data found for this agent.
            </Typography.Text>
          ) : (
            <Space size={4}>
              <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>Campaign:</Typography.Text>
              <Select
                allowClear
                placeholder="All campaigns"
                value={campaign}
                onChange={setCampaign}
                options={campaignOptions}
                style={{ minWidth: 160 }}
              />
              {campaignOptions.length > 1 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#e8682a', color: '#fff',
                  fontSize: 11, fontWeight: 700, lineHeight: 1, flexShrink: 0,
                }}>
                  {campaignOptions.length}
                </span>
              )}
            </Space>
          ))}
          {activeTab !== 'rankings' && activeTab !== 'ccaas-status' && activeTab !== 'cheers' && activeTab !== 'upsells' && activeTab !== 'helpdesk' && (
            <Space size={4}>
              <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>Date range:</Typography.Text>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={setDateRange}
                allowClear={false}
              />
            </Space>
          )}
          {/* What was written in the agent's evaluations, errors and coaching,
              for staff with access to at least one of them. */}
          {['evaluations', 'errors', 'coaching'].includes(_activeTab) && recapObjectsFor(currentUser).length > 0 && (
            <Button icon={<FileTextOutlined />} onClick={() => setRecapOpen(true)} style={{ marginLeft: 'auto' }}>
              Generate Recap
            </Button>
          )}
        </div>
      )}

      <AgentRecapModal
        open={recapOpen}
        onClose={() => setRecapOpen(false)}
        currentUser={currentUser}
        agentUserId={agentUserId}
        agentName={agentName}
        campaignOptions={campaignOptions}
        defaultCampaign={campaign}
        defaultRange={dateRange}
      />

      {agentUserId && loading && (
        <div style={{ textAlign: 'center', padding: 64 }}>
          <Spin size="large" />
        </div>
      )}

      {agentUserId && !loading && (
        <>
          {/* ── Employee profile banner ──────────────────────────────────── */}
          {(() => {
            const STATUS_COLOR = { Active: 'success', Terminated: 'error', LeaveWithoutPay: 'warning', LeaveWithPay: 'warning' };
            const isSentinel = (d) => !d || dayjs(d).year() === 1900;
            const fmtDate = (d) => isSentinel(d) ? null : dayjs(d).format('MM/DD/YYYY');
            const tenure = (d) => {
              if (!d) return null;
              const ms = Date.now() - new Date(d).getTime();
              const yrs = Math.floor(ms / (365.25 * 24 * 3600 * 1000));
              const mos = Math.floor((ms % (365.25 * 24 * 3600 * 1000)) / (30.44 * 24 * 3600 * 1000));
              return yrs > 0 ? `${yrs}y ${mos}m` : `${mos}m`;
            };
            const tenureStart = employeeProfile && !isSentinel(employeeProfile.RehireDate)
              ? employeeProfile.RehireDate
              : employeeProfile?.HireDate;

            // Expose modal openers to parent via ref
            if (actionHandlerRef) {
              actionHandlerRef.current = {
                openCompliance: () => openComplianceModal(dayjs().format('YYYY-MM-DD'), null, null, null, ''),
                openError:      () => setActionErrorOpen(true),
                openAbsence:    () => setActionAbsenceOpen(true),
                openCoaching:   () => setActionCoachingOpen(true),
                openBadges:     () => setBadgesOpen(true),
              };
            }

            return (
              <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }} styles={{ body: { padding: '8px 16px' } }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>

                  {/* Agent name */}
                  {agentName && (
                    <Typography.Text strong style={{ fontSize: 16 }}>{agentName}</Typography.Text>
                  )}

                  {/* Status */}
                  {employeeProfile && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Status</Typography.Text><br />
                      <Tag color={STATUS_COLOR[employeeProfile.Status] ?? 'default'} style={{ marginTop: 2 }}>
                        {employeeProfile.Status || '—'}
                      </Tag>
                    </div>
                  )}
                  {employeeProfile?.Job && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Job Title</Typography.Text><br />
                      <Typography.Text strong>{employeeProfile.Job}</Typography.Text>
                    </div>
                  )}
                  {(employeeProfile?.Department || employeeProfile?.DepartmentCode) && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Department</Typography.Text><br />
                      <Typography.Text>{employeeProfile.Department || employeeProfile.DepartmentCode}</Typography.Text>
                    </div>
                  )}
                  {employeeProfile && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Full Time</Typography.Text><br />
                      <Typography.Text>{employeeProfile.FullTime ? 'Yes' : 'No'}</Typography.Text>
                    </div>
                  )}
                  {employeeProfile && (employeeProfile.HireDate || !isSentinel(employeeProfile.RehireDate)) && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Hire Date</Typography.Text><br />
                      <Typography.Text>{fmtDate(!isSentinel(employeeProfile.RehireDate) ? employeeProfile.RehireDate : employeeProfile.HireDate)}</Typography.Text>
                    </div>
                  )}
                  {employeeProfile && !isSentinel(employeeProfile.RehireDate) && employeeProfile.HireDate && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Original Hire</Typography.Text><br />
                      <Typography.Text>{fmtDate(employeeProfile.HireDate)}</Typography.Text>
                    </div>
                  )}
                  {tenureStart && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Tenure</Typography.Text><br />
                      <Typography.Text>{tenure(tenureStart)}</Typography.Text>
                    </div>
                  )}
                  {/* WFM Skills: who can view WFM Skills Assign; read only. */}
                  {agentUserId && (currentUser?.Role === 'admin' || (currentUser?.CanEditWFMSkills ?? 0) >= 1) && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>WFM</Typography.Text><br />
                      <Typography.Link onClick={() => setWfmSkillsOpen(true)}>WFM Skills</Typography.Link>
                    </div>
                  )}
                  {employeeProfile && !isSentinel(employeeProfile.TermDate) && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Term Date</Typography.Text><br />
                      <Typography.Text type="danger">{fmtDate(employeeProfile.TermDate)}</Typography.Text>
                    </div>
                  )}

                  {/* Payroll ID + Agent Nos at end of bar — admin/qa_admin only */}
                  {['admin', 'qa_admin'].includes(currentUser?.Role) && agentRecord?.Payroll_ID && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>Payroll ID</Typography.Text><br />
                      <Typography.Text strong>{agentRecord.Payroll_ID}</Typography.Text>
                    </div>
                  )}
                  {['admin', 'qa_admin'].includes(currentUser?.Role) && agentRecord?.AgentNos?.length > 0 && (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        Agent No{agentRecord.AgentNos.length > 1 ? 's' : ''}
                      </Typography.Text><br />
                      <Typography.Text>{agentRecord.AgentNos.join(', ')}</Typography.Text>
                    </div>
                  )}

                  {/* Badges last, after every field */}
                  <AgentBadges badges={badges} />
                </div>
              </Card>
            );
          })()}

          {/* ── Percentile rank card ─────────────────────────────────────── */}
          {(percentileLoading || percentileRows.length > 0) && (() => {
            // pivot: campaign → { AHT, EvalScore, Occurrences, Errors }
            const METRICS = [
              { key: 'AHT',         label: 'AHT',         higherIsBetter: false, fmt: (v) => v != null ? `${Math.round(v)}s` : '—' },
              { key: 'EvalScore',   label: 'Eval Score',  higherIsBetter: true,  fmt: (v) => v != null ? `${Number(v).toFixed(1)}%` : '—' },
              { key: 'Occurrences', label: 'Occurrences', higherIsBetter: false, fmt: (v) => v != null ? Math.round(v) : '—' },
              { key: 'Errors',      label: 'Errors',      higherIsBetter: false, fmt: (v) => v != null ? Math.round(v) : '—' },
            ];
            const ordinal = (rank) => {
              const n = Math.round(rank * 100);
              if (n === 0) return '<1st %ile';
              const s = ['th','st','nd','rd'];
              const v = n % 100;
              return `${n}${s[(v - 20) % 10] || s[v] || s[0]} %ile`;
            };
            const tagColor = (rank, higherIsBetter) => {
              const pct = rank * 100;
              const good = higherIsBetter ? pct >= 66 : pct <= 33;
              const bad  = higherIsBetter ? pct <  33 : pct >  66;
              return good ? 'success' : bad ? 'error' : 'warning';
            };
            const pivot = {};
            let computedAt = null;
            let periodStart = null, periodEnd = null;
            for (const r of percentileRows) {
              if (!pivot[r.Campaign]) pivot[r.Campaign] = {};
              pivot[r.Campaign][r.MetricName] = r;
              if (!computedAt) { computedAt = r.ComputedAt; periodStart = r.PeriodStart; periodEnd = r.PeriodEnd; }
            }
            const campaigns = Object.keys(pivot).sort();
            const columns = [
              { title: 'Campaign', dataIndex: 'campaign', key: 'campaign', width: 160 },
              ...METRICS.map((m) => ({
                title: m.label, key: m.key, width: 130, align: 'center',
                render: (_, row) => {
                  const d = row[m.key];
                  if (!d) return <Typography.Text type="secondary">—</Typography.Text>;
                  return (
                    <div style={{ lineHeight: 1.3 }}>
                      <Tag color={tagColor(d.PercentileRank, m.higherIsBetter)} style={{ marginBottom: 2 }}>
                        {ordinal(d.PercentileRank)}
                      </Tag>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {m.fmt(d.MetricValue)} · {d.AgentCount} agents
                      </div>
                    </div>
                  );
                },
              })),
            ];
            const dataSource = campaigns.map((c) => ({ key: c, campaign: c, ...pivot[c] }));
            const subtitle = periodStart && periodEnd
              ? `${dayjs(periodStart).format('MMM D')} – ${dayjs(periodEnd).format('MMM D, YYYY')}  ·  updated ${formatTS(computedAt, 'MMM D h:mm A z')}`
              : computedAt ? `Updated ${formatTS(computedAt, 'MMM D h:mm A z')}` : '';
            return (
              <Card
                size="small"
                title="Percentile Rank — Current Month"
                extra={<Typography.Text type="secondary" style={{ fontSize: 11 }}>{subtitle}</Typography.Text>}
                style={{ marginBottom: 16 }}
              >
                <Spin spinning={percentileLoading}>
                  <Table
                    rowKey="key"
                    dataSource={dataSource}
                    columns={columns}
                    size="small"
                    pagination={false}
                    locale={{ emptyText: 'No percentile data computed yet.' }}
                  />
                </Spin>
              </Card>
            );
          })()}

          <Tabs activeKey={_activeTab} onChange={setActiveTab} items={[
            // ── To Do ─────────────────────────────────────────────────────────
            {
              key: 'todo',
              label: todoActionCount > 0
                ? <span>To Do <Tag color="red" style={{ marginLeft: 2, fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>{todoActionCount}</Tag></span>
                : 'To Do',
              children: (() => {
                if (todoLoading) return <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>;
                // Acknowledged or not: that is the whole test, and it is what makes
                // these two lists mutually exclusive. A record used to leave the
                // action list when its deadline passed and appear as finalized
                // while still unacknowledged, which claimed the agent had dealt
                // with something they never saw. Run-AutoAcknowledge closes the
                // genuinely abandoned ones a fortnight past the deadline, so
                // nothing accumulates here indefinitely.
                const deadlinePassed = (r) => !!r.CompletedAt && dayjs().isAfter(dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day'));
                const evalsNeeded    = todoEvals.filter((r)  => r.Status === 'rescore_requested' || (r.Status === 'completed' && !r.AcknowledgedAt));
                const cutoff30 = dayjs().subtract(30, 'day');
                const evalsFinalized = todoEvals.filter((r)  => r.Status === 'completed' && !!r.AcknowledgedAt && dayjs(r.CompletedAt).isAfter(cutoff30));
                const errorsNeeded   = todoErrors.filter((r) => r.Status === 'rescore_requested' || (r.Status === 'completed' && !r.AcknowledgedAt));
                const errorsFinalized= todoErrors.filter((r) => r.Status === 'completed' && !!r.AcknowledgedAt && dayjs(r.CompletedAt).isAfter(cutoff30));
                const coachingNeeded = coachingSessions.filter((r) => r.Status === 'active' && !['completed','canceled'].includes(r.MyStatus || ''));
                const feedbackNeeded    = todoFeedback.filter((r) => r.Status === 'rescore_requested' || (r.Status === 'completed' && !r.AcknowledgedAt));
                const feedbackFinalized = todoFeedback.filter((r) => r.Status === 'completed' && !!r.AcknowledgedAt && dayjs(r.CompletedAt).isAfter(cutoff30));
                // Compliance carries its deadline on the record rather than deriving
                // it: the window is one app-wide setting, so computing it here would
                // move every past deadline whenever it changed.
                const complianceOverdue = (r) => !!r.AcknowledgeDueDate && dayjs().isAfter(dayjs(r.AcknowledgeDueDate), 'day');
                // Inactive is a completed record whose warning has lapsed; it
                // is acknowledged and listed exactly as a completed one is.
                const complianceToAck = todoCompliance.filter((r) => ['complete', 'inactive'].includes(r.Status) && !r.AcknowledgedAt);
                const complianceFyi  = todoCompliance.filter((r) => ['complete', 'inactive'].includes(r.Status) && !!r.AcknowledgedAt);
                const absUpcoming    = todoAbsences.filter((r) => r.Status !== 'Canceled');

                const avg = (arr) => arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 10) / 10 : null;
                const scoreMap = {};
                todoEvals.filter((r) => r.Status === 'completed' && r.ScorePct != null).forEach((r) => {
                  const mk = (r.CallDate || '').substring(0, 7); if (!mk) return;
                  if (!scoreMap[mk]) scoreMap[mk] = { scores: [], targetPcts: [], highPcts: [] };
                  scoreMap[mk].scores.push(r.ScorePct);
                  // Already percentages -- see the note on the other trend.
                  if (r.GoalTargetPct   != null) scoreMap[mk].targetPcts.push(r.GoalTargetPct);
                  if (r.GoalPlatinumPct != null) scoreMap[mk].highPcts.push(r.GoalPlatinumPct);
                });
                const scoreMonths = Object.entries(scoreMap).sort(([a],[b]) => a.localeCompare(b)).slice(-3)
                  .map(([mk, d]) => ({ m: dayjs(mk+'-01').format('MMM YY'), score: avg(d.scores), n: d.scores.length, target: avg(d.targetPcts), high: avg(d.highPcts) }));
                const ahtMonths = todoAhtTrend.slice(-3).map((r) => ({
                  m: dayjs(r.MonthKey+'-01').format('MMM YY'), Calls: r.CallCount,
                  Talk: r.AvgAgentTime, ACW: r.AvgACWTime, AHT: r.AvgAHT,
                }));
                const fmtSecs = (s) => { if (s == null) return '—'; const m = Math.floor(s/60); return `${m}:${String(s%60).padStart(2,'0')}`; };

                const assignedEvalsActive    = todoAssignedEvals.filter((r) => !['completed', 'canceled'].includes(r.Status));
                const assignedErrorsActive   = todoAssignedErrors.filter((r) => !['completed', 'canceled'].includes(r.Status));
                const assignedFeedbackActive = todoAssignedFeedback.filter((r) => !['completed', 'canceled'].includes(r.Status));
                const coachingAsCoachActive  = coachingAsCoach.filter((r) => !['completed', 'canceled'].includes((r.Status || '').toLowerCase()));

                // Compliance cards: the record's warning, blank when it has none.
                const warningCol = {
                  title: 'Warning', dataIndex: 'WarningNumber', key: 'WarningNumber', width: 80,
                  render: (v) => v
                    ? <Tag color={{ '1st': 'blue', '2nd': 'orange', '3rd': 'volcano', 'Final': 'red' }[v]} style={{ margin: 0 }}>{v}</Tag>
                    : <Typography.Text type="secondary">—</Typography.Text>,
                };

                // A card with nothing to do is just its header line (title and
                // the green tick); `empty` is no longer shown. todoFirst puts the
                // cards with something to do above them, keeping the order within each.
                // eslint-disable-next-line no-unused-vars
                const sec = (title, color, count, empty, body) => (
                  <Card key={title} data-count={count} size="small"
                    style={{ marginBottom: count > 0 ? 12 : 8, borderLeft: `4px solid ${color}` }}
                    styles={count > 0 ? undefined : { body: { display: 'none' } }}
                    title={<Space size={6}><Typography.Text strong>{title}</Typography.Text><Tag color={count > 0 ? 'red' : 'green'} style={{ fontWeight: 600 }}>{count > 0 ? count : '✓'}</Tag></Space>}>
                    {count > 0 ? body : null}
                  </Card>
                );
                const todoFirst = (cards) => {
                  const shown = cards.filter(Boolean);
                  const has = (c) => (c.props['data-count'] || 0) > 0;
                  return [...shown.filter(has), ...shown.filter((c) => !has(c))];
                };

                return (
                  <Row gutter={[20, 0]}>
                    <Col xs={24} lg={12}>
                      {/* The heading only when something needs doing: with every
                          card at zero it warned about nothing. */}
                      {(() => {
                      const cards = todoFirst([
                        sec('Agent — Evaluations to Acknowledge', evalsNeeded.length > 0 ? '#ff4d4f' : '#52c41a', evalsNeeded.length, 'No evaluations need attention.',
                        <Table rowKey="ID" dataSource={evalsNeeded} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          onRow={(r) => ({ onClick: () => navigate(`/cases/${r.ID}`), style: { cursor: 'pointer' } })}
                          columns={[
                            { title: 'Call Date', dataIndex: 'CallDate',  key: 'CallDate',  width: 100 },
                            { title: 'Campaign',  dataIndex: 'Campaign',  key: 'Campaign',  width: 140, ellipsis: true },
                            { title: 'Score', dataIndex: 'ScorePct', key: 'ScorePct', width: 72, align: 'right',
                              render: (v) => v != null ? <span style={{ color: scoreColor(v), fontWeight: 600 }}>{v}%</span> : '—' },
                            { title: 'Status', dataIndex: 'Status', key: 'Status', width: 95,
                              render: (v) => <Tag color={EVAL_STATUS_COLOR[v] ?? 'default'}>{EVAL_STATUS_LABEL[v] ?? v}</Tag> },
                            { title: <span style={{ whiteSpace: 'nowrap' }}>Acknowledge Deadline</span>, key: 'DisputeDeadline',
                              render: (_, r) => {
                                if (r.Status !== 'completed' || !r.CompletedAt) return null;
                                const dl = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
                                // Grey used to be right here, when a passed deadline meant the
                                // record had left this list. It stays now and still needs doing,
                                // so past due is the urgent case, not the settled one.
                                const over = deadlinePassed(r);
                                return (
                                  <span style={{ color: over ? '#ff4d4f' : '#fa8c16', fontSize: 12, fontWeight: over ? 600 : undefined, whiteSpace: 'nowrap' }}>
                                    {dl.format('YYYY-MM-DD')}{over ? ' (overdue)' : ''}
                                  </span>
                                );
                              } },
                          ]}
                        />
                        ),
                        sec('Agent — Error Submissions to Acknowledge', errorsNeeded.length > 0 ? '#ff4d4f' : '#52c41a', errorsNeeded.length, 'No error submissions need attention.',
                        <Table rowKey="ID" dataSource={errorsNeeded} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          onRow={(r) => ({ onClick: () => navigate(`/errors/${r.ID}`), style: { cursor: 'pointer' } })}
                          columns={[
                            { title: 'Date',      dataIndex: 'CreateDate',    key: 'CreateDate',    width: 100 },
                            { title: 'Campaign',  dataIndex: 'Campaign',      key: 'Campaign',      width: 140, ellipsis: true },
                            { title: 'Errors',    dataIndex: 'TotalErrors',   key: 'TotalErrors',   width: 65, align: 'right',
                              render: (v) => v > 0 ? <Tag color="red" style={{ margin: 0 }}>{v}</Tag> : v },
                            { title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 100, align: 'right',
                              render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : v },
                            { title: 'Status',    dataIndex: 'Status',        key: 'Status',        width: 95,
                              render: (v) => <Tag color={ERROR_STATUS_COLOR[v] ?? 'default'}>{ERROR_STATUS_LABEL[v] ?? v}</Tag> },
                            { title: <span style={{ whiteSpace: 'nowrap' }}>Acknowledge Deadline</span>, key: 'AcknowledgeDeadline',
                              render: (_, r) => {
                                if (r.Status !== 'completed' || !r.CompletedAt) return null;
                                const dl = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
                                // Grey used to be right here, when a passed deadline meant the
                                // record had left this list. It stays now and still needs doing,
                                // so past due is the urgent case, not the settled one.
                                const over = deadlinePassed(r);
                                return (
                                  <span style={{ color: over ? '#ff4d4f' : '#fa8c16', fontSize: 12, fontWeight: over ? 600 : undefined, whiteSpace: 'nowrap' }}>
                                    {dl.format('YYYY-MM-DD')}{over ? ' (overdue)' : ''}
                                  </span>
                                );
                              } },
                          ]}
                        />
                        ),
                        sec('Agent — Coaching Sessions to Complete', coachingNeeded.length > 0 ? '#fa8c16' : '#52c41a', coachingNeeded.length, 'No coaching sessions awaiting your completion.',
                        <Table rowKey="ID" dataSource={coachingNeeded} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          columns={[
                            { title: 'Type',     dataIndex: 'RequestTypeName',  key: 'RequestTypeName',  ellipsis: true },
                            { title: 'Coach',    dataIndex: 'CoachName',        key: 'CoachName',        width: 130, ellipsis: true },
                            { title: 'My Status', dataIndex: 'MyStatus', key: 'MyStatus', width: 100,
                              render: (v) => <Tag color={{ pending: 'orange', canceled: 'default' }[v] ?? 'orange'}>{v || 'Pending'}</Tag> },
                            { title: 'Due Date', dataIndex: 'DueDate',          key: 'DueDate',          width: 100 },
                            { title: '', key: 'view', width: 55,
                              render: (_, r) => <Link to={`/coaching/sessions/${r.ID}`}><Button size="small">View</Button></Link> },
                          ]}
                        />
                        ),
                        sec('Agent — Non-scored Feedback to Acknowledge', feedbackNeeded.length > 0 ? '#fa8c16' : '#52c41a', feedbackNeeded.length, 'No feedback items need attention.',
                        <Table rowKey="ID" dataSource={feedbackNeeded} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          onRow={(r) => ({ onClick: () => navigate(`/cases/${r.ID}`), style: { cursor: 'pointer' } })}
                          columns={[
                            { title: 'Call Date', dataIndex: 'CallDate',  key: 'CallDate',  width: 100 },
                            { title: 'Campaign',  dataIndex: 'Campaign',  key: 'Campaign',  width: 140, ellipsis: true },
                            { title: 'Score', dataIndex: 'ScorePct', key: 'ScorePct', width: 72, align: 'right',
                              render: (v) => v != null ? <span style={{ color: scoreColor(v), fontWeight: 600 }}>{v}%</span> : '—' },
                            { title: 'Status', dataIndex: 'Status', key: 'Status', width: 95,
                              render: (v) => <Tag color={EVAL_STATUS_COLOR[v] ?? 'default'}>{EVAL_STATUS_LABEL[v] ?? v}</Tag> },
                            { title: <span style={{ whiteSpace: 'nowrap' }}>Acknowledge Deadline</span>, key: 'DisputeDeadline',
                              render: (_, r) => {
                                if (r.Status !== 'completed' || !r.CompletedAt) return null;
                                const dl = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
                                // Grey used to be right here, when a passed deadline meant the
                                // record had left this list. It stays now and still needs doing,
                                // so past due is the urgent case, not the settled one.
                                const over = deadlinePassed(r);
                                return (
                                  <span style={{ color: over ? '#ff4d4f' : '#fa8c16', fontSize: 12, fontWeight: over ? 600 : undefined, whiteSpace: 'nowrap' }}>
                                    {dl.format('YYYY-MM-DD')}{over ? ' (overdue)' : ''}
                                  </span>
                                );
                              } },
                          ]}
                        />
                        ),
                        sec('Agent — Compliance Issues to Acknowledge', complianceToAck.length > 0 ? '#ff4d4f' : '#52c41a', complianceToAck.length, 'No compliance issues need acknowledgement.',
                        <Table rowKey="ID" dataSource={complianceToAck} size="small" pagination={{ defaultPageSize: 4, size: 'small', hideOnSinglePage: true }}
                          onRow={(r) => ({ onClick: () => navigate(`/compliance/${r.Type === 'writeup' ? 'my-writeups' : 'my-issues'}/${r.ID}`), style: { cursor: 'pointer' } })}
                          columns={[
                            { title: 'Type', dataIndex: 'Type', key: 'Type', width: 130,
                              render: (v) => <Tag color={v === 'writeup' ? 'purple' : 'blue'}>{v === 'writeup' ? 'Formal Writeup' : 'Incident'}</Tag> },
                            { title: 'Date',   dataIndex: 'DateOfIncident', key: 'DateOfIncident', width: 100 },
                            warningCol,
                            { title: 'Issues', key: 'Issues', ellipsis: true,
                              render: (_, r) => (r.Issues || []).length ? (r.Issues || []).map((s, i) => <Tag key={i} style={{ marginBottom: 2 }}>{s}</Tag>) : '—' },
                            // Same treatment as the evaluation and error lists: the
                            // deadline, in red once it has passed, since the record
                            // stays here until it is acknowledged either way.
                            { title: <span style={{ whiteSpace: 'nowrap' }}>Acknowledge Deadline</span>, key: 'AcknowledgeDueDate', width: 170,
                              render: (_, r) => {
                                if (!r.AcknowledgeDueDate) return <Typography.Text type="secondary">—</Typography.Text>;
                                const over = complianceOverdue(r);
                                return (
                                  <span style={{ color: over ? '#ff4d4f' : '#fa8c16', fontSize: 12, fontWeight: over ? 600 : undefined }}>
                                    {r.AcknowledgeDueDate}{over ? ' (overdue)' : ''}
                                  </span>
                                );
                              } },
                          ]}
                        />
                        ),
                        sec('Agent — My Open Tickets as Impacted Employee', hdImpacted.length > 0 ? '#fa8c16' : '#52c41a', hdImpacted.length, '',
                          <Table rowKey="ID" dataSource={hdImpacted} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => navigate(`/helpdesk/${r.ID}`), style: { cursor: 'pointer' } })}
                            columns={involvedTicketColumns(hdImpacted, { showRole: false, compact: true })} />
                        ),
                        campUpdatesShown && sec('Agent — Campaign Updates to Acknowledge', myCampUpdates.length > 0 ? '#ff4d4f' : '#52c41a', myCampUpdates.length, '',
                          <>
                          <Table rowKey="ID" dataSource={myCampUpdates} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => setReadUpdate(r), style: { cursor: 'pointer' } })}
                            columns={[
                              // Opens the read panel (links and Acknowledge are there).
                              { title: 'Update', dataIndex: 'Title', key: 'Title',
                                render: (v, r) => (
                                  <div>
                                    <a>{v}</a>
                                    {r.Description && (
                                      <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }}
                                        style={{ fontSize: 12, margin: 0 }}>{r.Description}</Typography.Paragraph>
                                    )}
                                  </div>
                                ) },
                              { title: 'Date', dataIndex: 'UpdateDate', key: 'UpdateDate', width: 100 },
                              { title: 'Due', dataIndex: 'AckDueDate', key: 'AckDueDate', width: 100,
                                render: (v) => (v
                                  ? <span style={{ color: v < dayjs().format('YYYY-MM-DD') ? '#ff4d4f' : undefined }}>{v}</span>
                                  : <Typography.Text type="secondary">—</Typography.Text>) },
                            ]} />
                          <CampaignUpdateReadModal update={readUpdate} canAcknowledge
                            onClose={() => setReadUpdate(null)} onAcknowledged={loadCampUpdates} />
                          </>
                        ),
                        projTasksShown && sec('My Open Project Tasks', myProjTasks.length > 0 ? '#fa8c16' : '#52c41a', myProjTasks.length, '',
                          <Table rowKey="ID" dataSource={myProjTasks} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => navigate(`/folders/${r.ProjectID}/projects/${r.FolderID}`), style: { cursor: 'pointer' } })}
                            columns={[
                              { title: 'Task', dataIndex: 'Title', key: 'Title', ellipsis: true },
                              { title: 'Folder', dataIndex: 'ProjectName', key: 'ProjectName', width: 170, ellipsis: true },
                              { title: 'Due', dataIndex: 'DueDate', key: 'DueDate', width: 100,
                                render: (v) => (v
                                  ? <span style={{ color: v < dayjs().format('YYYY-MM-DD') ? '#ff4d4f' : undefined }}>{v}</span>
                                  : <Typography.Text type="secondary">—</Typography.Text>) },
                              { title: 'Status', dataIndex: 'Status', key: 'Status', width: 105,
                                render: (v) => <Tag color={v === 'In Progress' ? 'processing' : 'default'}>{v}</Tag> },
                            ]} />
                        ),
                        sec('Watcher — Open Tickets', hdWatching.length > 0 ? '#fa8c16' : '#52c41a', hdWatching.length, '',
                          <Table rowKey="ID" dataSource={hdWatching} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => navigate(`/helpdesk/${r.ID}`), style: { cursor: 'pointer' } })}
                            columns={involvedTicketColumns(hdWatching, { showRole: false, compact: true, showEmployee: true })} />
                        ),
                        agentRecord == null && !(currentUser?.Role === 'agent' && assignedEvalsActive.length === 0) && sec('QA — Evaluations to Complete', assignedEvalsActive.length > 0 ? '#ff4d4f' : '#52c41a', assignedEvalsActive.length, 'No evaluations assigned to you to complete.',
                        <Table rowKey="ID" dataSource={assignedEvalsActive} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          onRow={(r) => ({ onClick: () => navigate(`/cases/${r.ID}`), style: { cursor: 'pointer' } })}
                          columns={[
                            { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 100 },
                            { title: 'Campaign',  dataIndex: 'Campaign', key: 'Campaign', ellipsis: true },
                            { title: 'Form',      dataIndex: 'FormName', key: 'FormName', ellipsis: true },
                            { title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
                              render: (v) => <Tag color={EVAL_STATUS_COLOR[v] ?? 'default'}>{EVAL_STATUS_LABEL[v] ?? v}</Tag> },
                          ]}
                        />
                        ),
                        agentRecord == null && !(currentUser?.Role === 'agent' && assignedErrorsActive.length === 0) && sec('QA — Error Submissions to Complete', assignedErrorsActive.length > 0 ? '#ff4d4f' : '#52c41a', assignedErrorsActive.length, 'No error submissions assigned to you to complete.',
                        <Table rowKey="ID" dataSource={assignedErrorsActive} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          onRow={(r) => ({ onClick: () => navigate(`/errors/${r.ID}`), style: { cursor: 'pointer' } })}
                          columns={[
                            { title: 'Date',      dataIndex: 'CreateDate',    key: 'CreateDate',    width: 100 },
                            { title: 'Campaign',  dataIndex: 'Campaign',      key: 'Campaign',      ellipsis: true },
                            { title: 'Form',      dataIndex: 'FormName',      key: 'FormName',      ellipsis: true },
                            { title: 'Errors',    dataIndex: 'TotalErrors',   key: 'TotalErrors',   width: 65, align: 'right',
                              render: (v) => v > 0 ? <Tag color="red" style={{ margin: 0 }}>{v}</Tag> : v },
                            { title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 100, align: 'right',
                              render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : v },
                            { title: 'Status',    dataIndex: 'Status',        key: 'Status',        width: 90,
                              render: (v) => <Tag color={ERROR_STATUS_COLOR[v] ?? 'default'}>{ERROR_STATUS_LABEL[v] ?? v}</Tag> },
                          ]}
                        />
                        ),
                        agentRecord == null && !(currentUser?.Role === 'agent' && coachingAsCoachActive.length === 0) && sec('Coach — Coaching Sessions to Execute', coachingAsCoachActive.length > 0 ? '#fa8c16' : '#52c41a', coachingAsCoachActive.length, 'No coaching sessions awaiting your execution as coach.',
                        <Table rowKey="ID" dataSource={coachingAsCoachActive} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          columns={[
                            { title: 'Due Date', dataIndex: 'DueDate',         key: 'DueDate',         width: 100 },
                            { title: 'Type',     dataIndex: 'RequestTypeName', key: 'RequestTypeName', ellipsis: true },
                            // Same treatment as the Coaching Sessions page: the
                            // first name, a +N tag for the rest, and the whole
                            // list with the done count in the tooltip. A
                            // session can coach several people and the column
                            // has no room to name them all.
                            { title: 'Participant', key: 'Participants', ellipsis: true,
                              render: (_, r) => {
                                const names = (r.ParticipantNames || '').split(',').map((n) => n.trim()).filter(Boolean);
                                if (!names.length) return <span style={{ color: '#bfbfbf' }}>-</span>;
                                const done = `${r.CompletedCount ?? 0} of ${r.ParticipantCount ?? names.length} done`;
                                return (
                                  // AntTooltip, not Tooltip: recharts owns the
                                  // bare name in this file. The chart one
                                  // renders nothing outside a chart, so it
                                  // swallowed the names and left the column
                                  // blank without any build error.
                                  <AntTooltip title={<div>{names.map((n) => <div key={n}>{n}</div>)}<div style={{ marginTop: 4, opacity: 0.75 }}>{done}</div></div>}>
                                    <span>
                                      {names[0]}
                                      {names.length > 1 && <Tag color="blue" style={{ marginLeft: 6 }}>+{names.length - 1}</Tag>}
                                    </span>
                                  </AntTooltip>
                                );
                              } },
                            { title: 'Campaign', dataIndex: 'Campaign',        key: 'Campaign',        ellipsis: true },
                            { title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
                              render: (v) => <Tag color={{ active: 'blue', draft: 'default', completed: 'green', canceled: 'red' }[(v||'').toLowerCase()] ?? 'default'}>{v || 'Draft'}</Tag> },
                            { title: '', key: 'view', width: 55,
                              render: (_, r) => <Link to={`/coaching/sessions/${r.ID}`}><Button size="small">View</Button></Link> },
                          ]}
                        />
                        ),
                        agentRecord == null && !(currentUser?.Role === 'agent' && assignedFeedbackActive.length === 0) && sec('QA — Non-scored Feedback to Complete', assignedFeedbackActive.length > 0 ? '#fa8c16' : '#52c41a', assignedFeedbackActive.length, 'No feedback items assigned to you to complete.',
                        <Table rowKey="ID" dataSource={assignedFeedbackActive} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                          onRow={(r) => ({ onClick: () => navigate(`/cases/${r.ID}`), style: { cursor: 'pointer' } })}
                          columns={[
                            { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 100 },
                            { title: 'Campaign',  dataIndex: 'Campaign', key: 'Campaign', ellipsis: true },
                            { title: 'Form',      dataIndex: 'FormName', key: 'FormName', ellipsis: true },
                            { title: 'Status', dataIndex: 'Status', key: 'Status', width: 95,
                              render: (v) => <Tag color={{ completed: 'blue', disputed: 'orange' }[v] ?? 'default'}>{v}</Tag> },
                          ]}
                        />
                        ),
                        (() => {
                        if (agentRecord != null) return null;
                        const assignedComplianceActive = todoAssignedCompliance.filter((r) => !['complete', 'inactive', 'canceled'].includes(r.Status));
                        if (currentUser?.Role === 'agent' && assignedComplianceActive.length === 0) return null;
                        return sec('QA — Compliance Issues to Complete', assignedComplianceActive.length > 0 ? '#ff4d4f' : '#52c41a', assignedComplianceActive.length, 'No compliance issues assigned to you to complete.',
                          <Table rowKey="ID" dataSource={assignedComplianceActive} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => navigate(`/compliance/incidents/${r.ID}`), style: { cursor: 'pointer' } })}
                            columns={[
                              { title: 'Date',     dataIndex: 'DateOfIncident', key: 'DateOfIncident', width: 100 },
                              { title: 'Employee', dataIndex: 'EmployeeName',   key: 'EmployeeName',   ellipsis: true },
                              { title: 'Issues', key: 'Issues', ellipsis: true,
                                render: (_, r) => (r.Issues || []).length ? (r.Issues || []).map((s, i) => <Tag key={i} style={{ marginBottom: 2 }}>{s}</Tag>) : '—' },
                              { title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
                                render: (v) => <Tag color={{ draft: 'default', in_progress: 'blue', complete: 'green', inactive: 'gold', canceled: 'red' }[v] ?? 'default'}>{v === 'in_progress' ? 'In Progress' : v === 'complete' ? 'Complete' : v === 'inactive' ? 'Inactive' : v === 'canceled' ? 'Canceled' : 'Draft'}</Tag> },
                              warningCol,
                            ]}
                          />
                        );
                        })(),
                        showVarianceTodo && sec('Supervisor — Open Schedule Variances', todoVariances.length > 0 ? '#fa8c16' : '#52c41a', todoVariances.length, 'No open schedule variances for your team.',
                        <OpenScheduleVariancesTable rows={todoVariances} />
                        ),
                      ]);
                      const actionCount = cards.reduce((n, c) => n + (c.props['data-count'] || 0), 0);
                      return (
                        <>
                          {actionCount > 0 && (
                            <Typography.Text strong style={{ display: 'block', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>⚠ Action Required</Typography.Text>
                          )}
                          {cards}
                        </>
                      );
                      })()}
                    </Col>
                    <Col xs={24} lg={12}>
                      <Typography.Text strong style={{ display: 'block', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>ℹ FYI</Typography.Text>

                      {(() => {
                        const thirtyDaysAgo = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
                        const recentCheers  = agentCheers.filter(c => c.Date >= thirtyDaysAgo);
                        return recentCheers.length > 0 ? (
                          <Card size="small" style={{ marginBottom: 12 }}
                            title={<Space size={6}><Typography.Text strong>Recent Cheers for Peers</Typography.Text><Typography.Text type="secondary" style={{ fontSize: 11 }}>last 30 days</Typography.Text><Tag color="green">{recentCheers.length}</Tag></Space>}>
                            <Table
                              rowKey="ID"
                              dataSource={recentCheers}
                              size="small"
                              pagination={{ defaultPageSize: 4, size: 'small', hideOnSinglePage: true }}
                              columns={[
                                { title: 'Date',        dataIndex: 'Date',         key: 'Date',         width: 100 },
                                { title: 'Source',      dataIndex: 'SourceName',   key: 'SourceName',   width: 110, ellipsis: true },
                                { title: 'Category',    dataIndex: 'CategoryName', key: 'CategoryName', width: 120, ellipsis: true },
                                { title: 'Description', dataIndex: 'Description',  key: 'Description',  ellipsis: true,
                                  render: (v) => v ? v.slice(0, 100) : '—' },
                              ]}
                            />
                          </Card>
                        ) : null;
                      })()}

                      <Card size="small" style={{ marginBottom: 12 }}
                        title={<Space size={6}><Typography.Text strong>Upcoming Absence Notifications</Typography.Text>{absUpcoming.length > 0 && <Tag color="orange">{absUpcoming.length}</Tag>}</Space>}>
                        {absUpcoming.length === 0
                          ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>No upcoming absence notifications on record.</Typography.Text>
                          : <Table rowKey="ID" dataSource={absUpcoming} size="small" pagination={{ defaultPageSize: 4, size: 'small', hideOnSinglePage: true }}
                              onRow={(r) => ({ onClick: () => navigate(`/absences/${r.ID}`), style: { cursor: 'pointer' } })}
                              columns={[
                                { title: 'Date',   dataIndex: 'DateStart', key: 'DateStart', width: 100 },
                                { title: 'Reason', dataIndex: 'Reason',    key: 'Reason',    ellipsis: true },
                                { title: 'Status', dataIndex: 'Status',    key: 'Status',    width: 110,
                                  render: (v) => <Tag color={{ Submitted: 'blue', Acknowledged: 'cyan' }[v] ?? 'default'}>{v}</Tag> },
                              ]}
                            />
                        }
                      </Card>

                      <Card size="small" style={{ marginBottom: 12 }}
                        title={<Space size={6}><Typography.Text strong>Recent Compliance</Typography.Text>{complianceFyi.length > 0 && <Tag color="default">{complianceFyi.length}</Tag>}</Space>}>
                        {complianceFyi.length === 0
                          ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>No acknowledged compliance records in the last 30 days.</Typography.Text>
                          : <Table rowKey="ID" dataSource={complianceFyi} size="small" pagination={{ defaultPageSize: 4, size: 'small', hideOnSinglePage: true }}
                              onRow={(r) => ({ onClick: () => navigate(`/compliance/${r.Type === 'writeup' ? 'my-writeups' : 'my-issues'}/${r.ID}`), style: { cursor: 'pointer' } })}
                              columns={[
                                { title: 'Type', dataIndex: 'Type', key: 'Type', width: 130,
                                  render: (v) => <Tag color={v === 'writeup' ? 'purple' : 'blue'}>{v === 'writeup' ? 'Formal Writeup' : 'Incident'}</Tag> },
                                { title: 'Date',   dataIndex: 'DateOfIncident', key: 'DateOfIncident', width: 100 },
                                warningCol,
                                { title: 'Issues', key: 'Issues', ellipsis: true,
                                  render: (_, r) => (r.Issues || []).length ? (r.Issues || []).map((s, i) => <Tag key={i} style={{ marginBottom: 2 }}>{s}</Tag>) : '—' },
                              ]}
                            />
                        }
                      </Card>

                      {evalsFinalized.length > 0 && (
                        <Card size="small" style={{ marginBottom: 12 }}
                          title={<Space size={6}><Typography.Text strong>Recent Evaluations Finalized</Typography.Text><Tag>{evalsFinalized.length}</Tag></Space>}>
                          <Table rowKey="ID" dataSource={evalsFinalized} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => navigate(`/cases/${r.ID}`), style: { cursor: 'pointer' } })}
                            columns={[
                              { title: 'Call Date', dataIndex: 'CallDate',  key: 'CallDate',  width: 100 },
                              { title: 'Campaign',  dataIndex: 'Campaign',  key: 'Campaign',  ellipsis: true },
                              { title: 'Score', dataIndex: 'ScorePct', key: 'ScorePct', width: 72, align: 'right',
                                render: (v) => v != null ? <span style={{ color: scoreColor(v), fontWeight: 600 }}>{v}%</span> : '—' },
                            ]}
                          />
                        </Card>
                      )}

                      {errorsFinalized.length > 0 && (
                        <Card size="small" style={{ marginBottom: 12 }}
                          title={<Space size={6}><Typography.Text strong>Recent Error Submissions Finalized</Typography.Text><Tag>{errorsFinalized.length}</Tag></Space>}>
                          <Table rowKey="ID" dataSource={errorsFinalized} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => navigate(`/errors/${r.ID}`), style: { cursor: 'pointer' } })}
                            columns={[
                              { title: 'Date',      dataIndex: 'CreateDate',    key: 'CreateDate',    width: 100 },
                              { title: 'Campaign',  dataIndex: 'Campaign',      key: 'Campaign',      ellipsis: true },
                              { title: 'Errors',    dataIndex: 'TotalErrors',   key: 'TotalErrors',   width: 65, align: 'right',
                                render: (v) => v > 0 ? <Tag color="red" style={{ margin: 0 }}>{v}</Tag> : v },
                              { title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 100, align: 'right',
                                render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : v },
                            ]}
                          />
                        </Card>
                      )}

                      {feedbackFinalized.length > 0 && (
                        <Card size="small" style={{ marginBottom: 12 }}
                          title={<Space size={6}><Typography.Text strong>Recent Feedback Finalized</Typography.Text><Tag>{feedbackFinalized.length}</Tag></Space>}>
                          <Table rowKey="ID" dataSource={feedbackFinalized} size="small" pagination={{ defaultPageSize: 5, size: 'small', hideOnSinglePage: true }}
                            onRow={(r) => ({ onClick: () => navigate(`/cases/${r.ID}`), style: { cursor: 'pointer' } })}
                            columns={[
                              { title: 'Call Date', dataIndex: 'CallDate',  key: 'CallDate',  width: 100 },
                              { title: 'Campaign',  dataIndex: 'Campaign',  key: 'Campaign',  ellipsis: true },
                              { title: 'Score', dataIndex: 'ScorePct', key: 'ScorePct', width: 72, align: 'right',
                                render: (v) => v != null ? <span style={{ color: scoreColor(v), fontWeight: 600 }}>{v}%</span> : '—' },
                            ]}
                          />
                        </Card>
                      )}

                    </Col>
                  </Row>
                );
              })(),
            },
            {
              key: 'rankings',
              label: 'Rankings',
              children: (() => {
                const SECS_COLS  = new Set(['AvgTalkSecs', 'AvgACWSecs', 'AvgHTSecs']);
                const SCORE_COLS = new Set(['AvgCXOneQAScore', 'AvgQAScore', 'AdherencePct',
                  'CCaaSActive4SLAPct', 'CCaaSUnproductivePct', 'CCaaSUnavailablePct', 'CCaaSAvailablePct',
                  'ErrorsPct']);
                const HOURS_COLS = new Set(['WorkedHrs', 'ScheduledHrs']);
                const fmtRankVal = (metric, v) => {
                  if (v === null || v === undefined) return '—';
                  if (SECS_COLS.has(metric))  return fmtTime(Math.round(v));
                  if (SCORE_COLS.has(metric)) return `${Number(v).toFixed(1)}%`;
                  if (HOURS_COLS.has(metric)) return `${Number(v).toFixed(1)}h`;
                  if (Number.isInteger(v))    return v;
                  return Number(v).toFixed(1);
                };
                // The value as fmtRankVal shows it (whole seconds, else one
                // decimal), so the goal colour matches the number on screen:
                // a QA score of 94.96 shows as 95.0% and grades as 95.0.
                const shownVal = (metric, v) => {
                  if (v === null || v === undefined) return v;
                  if (SECS_COLS.has(metric)) return Math.round(v);
                  if (Number.isInteger(v))   return v;
                  return Number(Number(v).toFixed(1));
                };

                const pctTag = (pct) => {
                  if (pct === null || pct === undefined)
                    return <Typography.Text type="secondary">—</Typography.Text>;
                  const color = pct >= 75 ? 'success' : pct >= 50 ? 'processing' : pct >= 25 ? 'warning' : 'error';
                  return <Tag color={color}>{pct.toFixed(1)}th</Tag>;
                };

                const rankCell = (r) =>
                  r.rank != null
                    ? `${r.rank} / ${r.n}`
                    : <Typography.Text type="secondary">—</Typography.Text>;

                const METRIC_TO_GOAL = { ErrorsPct: 'ErrorPct' };
                const TIER = { silver: '#6b7280', gold: '#16a34a', platinum: '#6366f1' };

                const fmtGoalVal = (goal, v) => {
                  if (v == null) return null;
                  if (goal.format === 'mmss') return fmtTime(Math.round(v));
                  if (goal.format === 'pct')  return `${Number(v).toFixed(1)}%`;
                  return v;
                };

                const findGoal = (campaignName, metric, quarterly = false) => {
                  if (!rankSingleMonth || !goals.length) return null;
                  const goalType = METRIC_TO_GOAL[metric] || metric;
                  const periodMatches = goals.filter((g) => {
                    // A quarterly goal grades a quarterly figure and nothing
                    // else. The row that covers a quarter asks for them; every
                    // other row is a month's value and must not see them, or a
                    // third of a quarter's allowance would read as a pass.
                    if ((g.period === 'quarter') !== quarterly) return false;
                    const campMatch = g.CampaignID === 0 ? !campaignName : g.CampaignName === campaignName;
                    return campMatch && g.GoalType === goalType
                      && g.StartMonth <= rankSingleMonth
                      && (!g.EndMonth || g.EndMonth >= rankSingleMonth);
                  });
                  if (!periodMatches.length) return null;
                  // For UpsellCount prefer the FullTime-specific goal, fall back to NULL (all agents)
                  if (goalType === 'UpsellCount' && employeeProfile?.FullTime != null) {
                    const agentFT = employeeProfile.FullTime ? 1 : 0;
                    const specific = periodMatches.find((g) => g.FullTime != null && (g.FullTime ? 1 : 0) === agentFT);
                    if (specific) return specific;
                  }
                  return periodMatches.find((g) => g.FullTime == null) ?? periodMatches[0];
                };

                const getTierColor = (goal, value) => {
                  if (!goal || value == null) return undefined;
                  const lower = goal.direction === 'lower';
                  const beats = (t) => lower ? value <= t : value >= t;
                  if (goal.PlatinumValue != null && beats(goal.PlatinumValue)) return TIER.platinum;
                  if (beats(goal.TargetValue))                                  return TIER.gold;
                  if (goal.SilverValue != null && beats(goal.SilverValue))     return TIER.silver;
                  return undefined;
                };

                const renderGoal = (goal) => {
                  if (!goal) return <Typography.Text type="secondary" style={{ fontSize: 11 }}>—</Typography.Text>;
                  const noTiers = goal.SilverValue == null && goal.PlatinumValue == null;
                  if (noTiers) {
                    return <span style={{ color: TIER.gold, fontWeight: 500 }}>{fmtGoalVal(goal, goal.TargetValue)}</span>;
                  }
                  return (
                    <div style={{ lineHeight: 1.7 }}>
                      {goal.SilverValue   != null && <div style={{ color: TIER.silver,   fontSize: 11 }}>{fmtGoalVal(goal, goal.SilverValue)} Silver</div>}
                      <div style={{ color: TIER.gold,     fontSize: 11 }}>{fmtGoalVal(goal, goal.TargetValue)} Gold</div>
                      {goal.PlatinumValue != null && <div style={{ color: TIER.platinum, fontSize: 11 }}>{fmtGoalVal(goal, goal.PlatinumValue)} Platinum</div>}
                    </div>
                  );
                };

                const rankMonthLabel = dayjs(rankSingleMonth || rankData?.month_from, 'YYYY-MM').isValid()
                  ? dayjs(rankSingleMonth || rankData?.month_from, 'YYYY-MM').format('MMM YYYY')
                  : '—';

                const campCols = [
                  { title: 'Month', key: 'month', width: 95, render: () => rankMonthLabel },
                  { title: 'Campaign', dataIndex: 'campaign', key: 'campaign', width: 175, ellipsis: true, render: (v) => v || '—' },
                  { title: 'Metric',   dataIndex: 'label',    key: 'label',    width: 155 },
                  { title: 'Your Value', key: 'value', width: 110, align: 'right',
                    render: (_, r) => {
                      const isOverall = !r.campaign;
                      const goal = findGoal(isOverall ? null : r.campaign, r.metric, !!r.period);
                      const color = getTierColor(goal, shownVal(r.metric, r.value));
                      return <span style={color ? { color, fontWeight: 600 } : undefined}>{fmtRankVal(r.metric, r.value)}</span>;
                    },
                  },
                  { title: 'Goal', key: 'goal', width: 120, align: 'right',
                    render: (_, r) => {
                      const isOverall = !r.campaign;
                      return renderGoal(findGoal(isOverall ? null : r.campaign, r.metric, !!r.period));
                    },
                  },
                  { title: 'Percentile', key: 'percentile', width: 110, align: 'center', hidden: true,
                    render: (_, r) => pctTag(r.percentile),
                    sorter: (a, b) => (a.percentile ?? -1) - (b.percentile ?? -1) },
                  { title: 'Rank',  key: 'rank', width: 90,  align: 'right', render: (_, r) => rankCell(r) },
                  { title: 'Best',  key: 'best', width: 100, align: 'right', render: (_, r) => fmtRankVal(r.metric, r.best) },
                  { title: 'Avg',   key: 'avg',  width: 100, align: 'right', render: (_, r) => fmtRankVal(r.metric, r.avg) },
                ];

                const overallCols = [
                  // A row carrying its own period says so here rather than
                  // borrowing the table's month, which it does not cover.
                  { title: 'Month', key: 'month_o', width: 95,
                    render: (_, r) => r.period
                      ? <strong>{r.period}</strong>
                      : rankMonthLabel },
                  { title: 'Campaign', dataIndex: 'team', key: 'team', width: 175, ellipsis: true, render: (v) => v || '—' },
                  ...campCols.slice(2),
                ];

                const pivotMonths = rankData?.pivot_months || [];
                const pivotCell = (r, mk) => {
                  const cell = r[mk];
                  if (!cell || cell.percentile === null || cell.percentile === undefined)
                    return <Typography.Text type="secondary" style={{ fontSize: 11 }}>—</Typography.Text>;
                  const color = cell.percentile >= 75 ? 'success' : cell.percentile >= 50 ? 'processing' : cell.percentile >= 25 ? 'warning' : 'error';
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, lineHeight: 1.2 }}>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>{fmtRankVal(r.metric, cell.value)}</span>
                      <span style={{ fontSize: 10, color: '#8c8c8c' }}>{cell.rank}/{cell.n}</span>
                      <Tag color={color} style={{ margin: 0, fontSize: 10, padding: '0 4px', cursor: 'default' }}>
                        {cell.percentile.toFixed(0)}th
                      </Tag>
                    </div>
                  );
                };
                const monthCols = (withCampaign) => [
                  { title: withCampaign ? 'Campaign' : 'Team', key: 'campaign', width: 140, fixed: 'left', ellipsis: true,
                    render: withCampaign ? ((_, r) => r.campaign) : ((_, r) => r.team || '—') },
                  { title: 'Metric', dataIndex: 'label', key: 'label', width: 155, fixed: 'left' },
                  ...pivotMonths.map((mk) => ({
                    title: mk,
                    key: mk,
                    width: 115,
                    align: 'center',
                    render: (_, r) => pivotCell(r, mk),
                  })),
                ];

                const monthOptions = [...new Set((monthlyStats || []).map((r) => r.MonthKey))]
                  .sort().reverse()
                  .map((mk) => ({ label: mk, value: mk }));

                const handleViewModeChange = (mode) => {
                  setRankViewMode(mode);
                  if (mode === 'single') {
                    setRankSingleMonth(null);
                  } else {
                    const latest = monthOptions[0]?.value
                      || (rankData?.months || [])[0]
                      || rankData?.month_to;
                    if (latest) {
                      const end   = dayjs(latest, 'YYYY-MM');
                      const start = end.subtract(3, 'month');
                      setRankMonthRange([start, end]);
                    }
                    // If no fallback available, leave existing range as-is.
                  }
                };

                return (
                  <>
                    {/* ── Controls ──────────────────────────────────────── */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                      <Segmented
                        options={[
                          { label: 'Single Month', value: 'single' },
                          { label: 'Trend', value: 'trend' },
                        ]}
                        value={rankViewMode}
                        onChange={handleViewModeChange}
                        size="small"
                      />
                      <Space size={6}>
                        {rankViewMode === 'single' ? (
                          <>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Month</Typography.Text>
                            <Select
                              value={rankSingleMonth}
                              onChange={setRankSingleMonth}
                              options={monthOptions}
                              placeholder="Loading..."
                              style={{ width: 130, fontWeight: 700, fontSize: 15 }}
                            />
                          </>
                        ) : (
                          <>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Month Range</Typography.Text>
                            <DatePicker.RangePicker
                              picker="month"
                              value={rankMonthRange}
                              onChange={setRankMonthRange}
                              size="small"
                              style={{ width: 210 }}
                              allowClear={false}
                              format="YYYY-MM"
                            />
                          </>
                        )}
                      </Space>
                      <Space size={6}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Min Inbound Calls</Typography.Text>
                        <InputNumber
                          value={rankMinCalls}
                          onChange={(v) => setRankMinCalls(v ?? 5)}
                          min={1}
                          max={99}
                          size="small"
                          style={{ width: 65 }}
                        />
                      </Space>
                    </div>

                    {/* ── Grade Scorecard ───────────────────────────────── */}
                    {agentUserId && rankData && showRankingSection && !isSelf && (currentUser?.CanViewAgentSummary ?? 0) === 2 && (
                      <AgentGradeCard
                        overall={rankData.overall || []}
                        campaign={rankData.campaign || []}
                      />
                    )}

                    {rankViewMode === 'single' ? (
                      <>
                        <Typography.Title level={5} style={{ marginBottom: 8 }}>Campaign Metrics</Typography.Title>
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                          Ranked within each campaign. Call-time metrics excluded for agents with &le;{rankMinCalls} inbound calls.
                        </Typography.Text>
                        {(() => {
                          const selName = campaign ? campaignOptions.find(c => c.value === campaign)?.label : null;
                          const campRankRows = selName
                            ? (rankData?.campaign || []).filter(r => r.campaign === selName)
                            : (rankData?.campaign || []);
                          return (
                        <Table
                          rowKey={(r) => `${r.campaign}-${r.metric}`}
                          dataSource={campRankRows}
                          columns={campCols}
                          size="small"
                          pagination={false}
                          loading={rankLoading}
                          scroll={{ x: 'max-content' }}
                          locale={{ emptyText: agentUserId ? 'No campaign data for this month.' : 'Select an agent.' }}
                          style={{ marginBottom: 28 }}
                        />
                          );
                        })()}

                        {showRankingSection && (<>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                          <Typography.Title level={5} style={{ margin: 0 }}>Agent-Level Metrics</Typography.Title>
                          <Space size={6}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Peers</Typography.Text>
                            <Segmented
                              options={[{ label: 'All', value: 'all' }, { label: 'My Team', value: 'team' }]}
                              value={rankPeerScope}
                              onChange={setRankPeerScope}
                              size="small"
                            />
                          </Space>
                        </div>
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                          {rankPeerScope === 'team' && rankData?.agent_team
                            ? `Ranked within team: ${rankData.agent_team} (timecard and compliance metrics from monthly roll-up).`
                            : 'Ranked across all agents (timecard and compliance metrics from monthly roll-up).'}
                        </Typography.Text>
                        <Table
                          rowKey="metric"
                          dataSource={(rankData?.overall || []).filter((r) => r.metric !== 'CCaaSBreakLunchMins')}
                          columns={overallCols}
                          size="small"
                          pagination={false}
                          loading={rankLoading}
                          scroll={{ x: 'max-content' }}
                          locale={{ emptyText: agentUserId ? 'No agent-level data for this month.' : 'Select an agent.' }}
                        /></>)}
                      </>
                    ) : (
                      <>
                        <Typography.Title level={5} style={{ marginBottom: 8 }}>Campaign Metrics — Monthly Trend</Typography.Title>
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                          Percentile rank per month within each campaign. Hover a cell for value and rank.
                        </Typography.Text>
                        <Table
                          rowKey={(r) => `${r.campaign}-${r.metric}`}
                          dataSource={rankData?.pivot_campaign || []}
                          columns={monthCols(true)}
                          size="small"
                          pagination={false}
                          loading={rankLoading}
                          scroll={{ x: 'max-content' }}
                          locale={{ emptyText: agentUserId ? 'No campaign data for this range.' : 'Select an agent.' }}
                          style={{ marginBottom: 28 }}
                        />

                        {showRankingSection && (<>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                          <Typography.Title level={5} style={{ margin: 0 }}>Agent-Level Metrics — Monthly Trend</Typography.Title>
                          <Space size={6}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Peers</Typography.Text>
                            <Segmented
                              options={[{ label: 'All', value: 'all' }, { label: 'My Team', value: 'team' }]}
                              value={rankPeerScope}
                              onChange={setRankPeerScope}
                              size="small"
                            />
                          </Space>
                        </div>
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                          {rankPeerScope === 'team' && rankData?.agent_team
                            ? `Ranked within team: ${rankData.agent_team} per month. Hover a cell for value and rank.`
                            : 'Ranked across all agents per month. Hover a cell for value and rank.'}
                        </Typography.Text>
                        <Table
                          rowKey="metric"
                          dataSource={(rankData?.pivot_overall || []).filter((r) => r.metric !== 'CCaaSBreakLunchMins')}
                          columns={monthCols(false)}
                          size="small"
                          pagination={false}
                          loading={rankLoading}
                          scroll={{ x: 'max-content' }}
                          locale={{ emptyText: agentUserId ? 'No agent-level data for this range.' : 'Select an agent.' }}
                        /></>)}
                      </>
                    )}
                  </>
                );
              })(),
            },

            // ── Month View ───────────────────────────────────────────────────
            {
              key: 'quick-view',
              label: 'Month View',
              children: (() => {
                const n   = (v) => v != null ? v          : '—';
                const h   = (v) => v != null && v > 0 ? Number(v).toFixed(1) : '—';
                const t   = (v) => v != null ? fmtTime(v) : '—';
                const pct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '—';

                return (
                  <>
                  {monthlyStats.length === 0 && (
                    <Typography.Text type="secondary">No monthly stats found for this agent.</Typography.Text>
                  )}
                  {monthlyStats.length > 0 && (() => {
                    const selCampName = campaign ? campaignOptions.find(c => c.value === campaign)?.label : null;
                    const tcByMonth = {};
                    const campRowsByMonth = {};
                    monthlyStats.forEach((r) => {
                      if (r.Campaign === '__ALL__') { tcByMonth[r.MonthKey] = r; }
                      else if (!selCampName || r.Campaign === selCampName) {
                        (campRowsByMonth[r.MonthKey] = campRowsByMonth[r.MonthKey] || []).push(r);
                      }
                    });
                    const allMonthKeys = [...new Set(monthlyStats.map((r) => r.MonthKey))].sort();
                    const mergedStats = allMonthKeys.flatMap((mk) => {
                      const tc = tcByMonth[mk] || {};
                      const campRows = campRowsByMonth[mk];
                      if (campRows?.length) {
                        return campRows.map((r) => ({
                          ...r,
                          WorkedHrs:            r.WorkedHrs            ?? tc.WorkedHrs,
                          PTOHrs:               r.PTOHrs               ?? tc.PTOHrs,
                          OtherHrs:             r.OtherHrs             ?? tc.OtherHrs,
                          ScheduledHrs:         r.ScheduledHrs         ?? tc.ScheduledHrs,
                          AdherencePct:         r.AdherencePct         ?? tc.AdherencePct,
                          ShortTermNoticeCount: r.ShortTermNoticeCount ?? tc.ShortTermNoticeCount,
                          RecoachingCount:      r.RecoachingCount      ?? tc.RecoachingCount,
                          CheersFromPeers:      r.CheersFromPeers      ?? tc.CheersFromPeers,
                        }));
                      }
                      // No campaign rows this month — show the __ALL__ row so timecard data isn't lost
                      return tc.MonthKey ? [{ ...tc, Campaign: '—' }] : [];
                    });
                    return (
                      <>
                        <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
                          Agent Monthly Stats
                        </Typography.Title>
                        <Table
                          rowKey={(r) => `${r.MonthKey}-${r.Campaign}`}
                          dataSource={mergedStats}
                          size="small"
                          scroll={{ x: 'max-content' }}
                          pagination={false}
                          // Columns follow the viewer's access, as Campaign Summary's By
                          // month table: each measure needs its area's. Your own summary
                          // shows them all (not a client's own login).
                          columns={[
                            { title: 'Month',    dataIndex: 'MonthKey', key: 'MonthKey', width: 90,  fixed: 'left' },
                            { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 130, fixed: 'left' },
                            {
                              title: 'Calls (Inbound)',
                              children: [
                                { title: 'Calls',    dataIndex: 'InboundCalls', key: 'InboundCalls', width: 65, align: 'right', render: n },
                                { title: 'Avg Talk', dataIndex: 'AvgTalkSecs',  key: 'AvgTalkSecs',  width: 80, align: 'right', render: t },
                                { title: 'Avg ACW',  dataIndex: 'AvgACWSecs',   key: 'AvgACWSecs',   width: 75, align: 'right', render: t },
                                { title: 'Avg HT',   dataIndex: 'AvgHTSecs',    key: 'AvgHTSecs',    width: 75, align: 'right', render: t },
                              ],
                            },
                            {
                              title: 'Chats (Inbound)',
                              children: [
                                { title: 'Chats',    dataIndex: 'InboundChats',    key: 'InboundChats',    width: 65, align: 'right', render: n },
                                { title: 'Avg Talk', dataIndex: 'AvgChatTalkSecs', key: 'AvgChatTalkSecs', width: 80, align: 'right', render: t },
                                { title: 'Avg ACW',  dataIndex: 'AvgChatACWSecs',  key: 'AvgChatACWSecs',  width: 75, align: 'right', render: t },
                                { title: 'Avg HT',   dataIndex: 'AvgChatHTSecs',   key: 'AvgChatHTSecs',   width: 75, align: 'right', render: t },
                              ],
                            },
                            {
                              title: 'Total Contacts (Inbound)',
                              children: [
                                { title: 'Contacts', key: 'TotalContacts', width: 75, align: 'right',
                                  render: (_, r) => {
                                    const total = (r.InboundCalls || 0) + (r.InboundChats || 0);
                                    return total > 0 ? total : '—';
                                  },
                                },
                                { title: 'Avg HT', key: 'CombinedAvgHT', width: 75, align: 'right',
                                  render: (_, r) => {
                                    const calls = r.InboundCalls || 0; const chats = r.InboundChats || 0;
                                    const total = calls + chats;
                                    if (!total) return '—';
                                    const wht = ((calls * (r.AvgHTSecs || 0)) + (chats * (r.AvgChatHTSecs || 0))) / total;
                                    return t(Math.round(wht));
                                  },
                                },
                              ],
                            },
                            {
                              title: 'QA & Compliance',
                              children: [
                                // EvalCount/AvgQAScore carry the whole history: the refresh
                                // splices the pre-cutover CXOne figures into them, so the
                                // CXOne columns showed the same numbers under another name.
                                ...(mvCan('CanEditEvals') ? [
                                { title: 'QA Evals',    dataIndex: 'EvalCount',  key: 'EvalCount',  width: 90, align: 'right', render: n },
                                { title: 'QA Score',    dataIndex: 'AvgQAScore', key: 'AvgQAScore', width: 85, align: 'right', render: pct },
                                { title: 'Occurrences',      dataIndex: 'OccurrenceCount', key: 'OccurrenceCount', width: 95,  align: 'right', render: n },
                                ] : []),
                                ...(mvCan('CanEditErrors') ? [
                                { title: 'Errors',           dataIndex: 'ErrorCount',      key: 'ErrorCount',      width: 65,  align: 'right', render: n },
                                { title: 'Errors %', key: 'ErrorsPct', width: 75, align: 'right',
                                  render: (_, r) => {
                                    // ErrorCallQty: the historical count where one was backfilled.
                                    const calls = r.ErrorCallQty ?? r.InboundCalls; const errs = r.ErrorCount;
                                    if (!calls || errs == null) return '—';
                                    return `${(errs / calls * 100).toFixed(1)}%`;
                                  }
                                },
                                ] : []),
                                ...(mvCan('CanEditIncidents') ? [
                                { title: 'Compliance',       dataIndex: 'ComplianceCount', key: 'ComplianceCount', width: 90,  align: 'right', render: n },
                                ] : []),
                                ...(mvCan('CanEditCoaching') ? [
                                { title: 'Recoach Sessions', dataIndex: 'RecoachingCount', key: 'RecoachingCount', width: 130, align: 'right', render: n },
                                ] : []),
                              ],
                            },
                            {
                              title: 'Timecard & Schedule',
                              children: [
                                ...(mvCan('CanViewOversightStats') ? [
                                { title: 'Worked',      dataIndex: 'WorkedHrs',            key: 'WorkedHrs',            width: 72, align: 'right', render: h },
                                { title: 'PTO',         dataIndex: 'PTOHrs',               key: 'PTOHrs',               width: 60, align: 'right', render: h },
                                { title: 'Other',       dataIndex: 'OtherHrs',             key: 'OtherHrs',             width: 60, align: 'right', render: h },
                                { title: 'Scheduled',   dataIndex: 'ScheduledHrs',         key: 'ScheduledHrs',         width: 82, align: 'right', render: h },
                                { title: 'Adherence',   dataIndex: 'AdherencePct',         key: 'AdherencePct',         width: 82, align: 'right', render: pct },
                                ] : []),
                                ...(mvCan('CanEditAbsences') ? [
                                { title: 'Absence Notifications', dataIndex: 'ShortTermNoticeCount', key: 'ShortTermNoticeCount', width: 155, align: 'right', render: n },
                                ] : []),
                              ],
                            },
                            ...(mergedStats.some((r) => r.UpsellCount != null) ? [{
                              title: 'Upsells',
                              children: [
                                { title: 'Count', dataIndex: 'UpsellCount', key: 'UpsellCount', width: 75, align: 'right', render: n },
                              ],
                            }] : []),
                            {
                              title: 'Cheers for Peers',
                              children: [
                                { title: 'Received', dataIndex: 'CheersFromPeers', key: 'CheersFromPeers', width: 85, align: 'right', render: n },
                              ],
                            },
                          ].filter((c) => !c.children || c.children.length)}
                        />
                      </>
                    );
                  })()}
                  </>
                );
              })(),
            },

            // ── Cheers for Peers ─────────────────────────────────────────────
            {
              key: 'cheers',
              label: 'Cheers for Peers',
              children: (
                <>
                  {agentCheers.length > 0 && (() => {
                    const ytdCutoff = `${dayjs().year()}-01-01`;
                    const ytd = agentCheers.filter((c) => (c.Date || '') >= ytdCutoff).length;
                    const sinceLabel = cheersSince
                      ? `Cheers Since ${dayjs(cheersSince).format('MMM D, YYYY')}`
                      : 'All-Time Cheers';
                    return (
                      <Row gutter={[16, 16]} align="stretch" style={{ marginBottom: 16 }}>
                        <Col>
                          <Card size="small" style={{ height: '100%' }}>
                            <Statistic title={sinceLabel} value={agentCheers.length}
                              valueStyle={{ color: '#e8682a' }} />
                          </Card>
                        </Col>
                        <Col>
                          <Card size="small" style={{ height: '100%' }}>
                            <Statistic title="Cheers YTD" value={ytd}
                              valueStyle={{ color: '#e8682a' }} />
                          </Card>
                        </Col>
                      </Row>
                    );
                  })()}
                  <Table
                    rowKey="ID"
                    dataSource={agentCheers}
                    size="small"
                    pagination={{ defaultPageSize: 20, showSizeChanger: true }}
                    locale={{ emptyText: agentUserId ? 'No Cheers for Peers on record.' : 'Select an agent.' }}
                    columns={[
                      { title: 'Date',        dataIndex: 'Date',         key: 'Date',         width: 105 },
                      { title: 'Source',      dataIndex: 'SourceName',   key: 'SourceName',   width: 130, ellipsis: true },
                      { title: 'Category',    dataIndex: 'CategoryName', key: 'CategoryName', width: 140, ellipsis: true },
                      { title: 'Description', dataIndex: 'Description',  key: 'Description',  ellipsis: true },
                      { title: 'Contact ID',  dataIndex: 'ContactID',    key: 'ContactID',    width: 120 },
                      { title: 'Created By',  dataIndex: 'CreatedByName', key: 'CreatedByName', width: 140, ellipsis: true },
                    ]}
                  />
                </>
              ),
            },

            // ── Upsells ───────────────────────────────────────────────────────
            ...(!hideUpsell ? [{
              key: 'upsells',
              label: 'Upsells',
              children: (() => {
                const ytd        = upsellStats?.ytd        ?? null;
                const thisMonth  = upsellStats?.this_month ?? null;
                const total      = upsellStats?.total      ?? null;
                const minMonth   = upsellStats?.min_month  ?? null;
                const history    = upsellStats?.history    ?? [];
                const sinceLabel = minMonth
                  ? `Upsells Since ${dayjs(minMonth + '-01').format('MMM YYYY')}`
                  : 'Upsells (All Time)';
                return (
                  <>
                    <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                      <Col>
                        <Card size="small">
                          <Statistic
                            title={sinceLabel}
                            value={total ?? '—'}
                            valueStyle={{ color: '#1677ff' }}
                          />
                        </Card>
                      </Col>
                      <Col>
                        <Card size="small">
                          <Statistic
                            title="Upsells YTD"
                            value={ytd ?? '—'}
                            valueStyle={{ color: '#1677ff' }}
                          />
                        </Card>
                      </Col>
                      <Col>
                        <Card size="small">
                          <Statistic
                            title="Upsells This Month"
                            value={thisMonth ?? '—'}
                            valueStyle={{ color: '#1677ff' }}
                          />
                        </Card>
                      </Col>
                    </Row>
                    {(() => {
                      // Build flat grouped rows: campaign header → months → total
                      const grouped = {};
                      history.forEach((r) => {
                        const k = r.CampaignName || 'Unknown';
                        if (!grouped[k]) grouped[k] = [];
                        grouped[k].push(r);
                      });
                      const tableRows = [];
                      Object.entries(grouped).forEach(([campName, rows]) => {
                        const campTotal = rows.reduce((s, r) => s + (r.UpsellCount || 0), 0);
                        tableRows.push({ _type: 'campaign', _key: `hdr-${campName}`, label: campName, value: campTotal });
                        rows.forEach((r) => tableRows.push({ _type: 'month', _key: `${campName}-${r.MonthKey}`, label: r.MonthKey, value: r.UpsellCount }));
                      });
                      return (
                        <Table
                          rowKey="_key"
                          dataSource={tableRows}
                          size="small"
                          pagination={false}
                          title={() => <Typography.Text strong>Upsells By Month</Typography.Text>}
                          locale={{ emptyText: agentUserId ? 'No upsell data on record.' : 'Select an agent.' }}
                          onRow={(r) => r._type === 'campaign' ? { style: { background: 'rgba(0,0,0,0.04)', fontWeight: 600 } } : {}}
                          columns={[
                            {
                              title: 'Month',
                              dataIndex: 'label',
                              key: 'label',
                              render: (v, r) => r._type === 'campaign'
                                ? v
                                : (v ? dayjs(v + '-01').format('MMM YYYY') : '—'),
                            },
                            {
                              title: 'Upsells',
                              dataIndex: 'value',
                              key: 'value',
                              align: 'right',
                              width: 110,
                              render: (v) => v ?? '—',
                            },
                          ]}
                        />
                      );
                    })()}
                  </>
                );
              })(),
            }] : []),

            // Hidden only for an agent who takes calls in AWS and has none in
            // Superior. Both halves matter: without the AWS half, anyone off
            // for the whole range loses a tab that should show them zero;
            // without the calls half, an agent on both systems loses the half
            // of their work that lives in CXone.
            //
            // Asked of the calls rather than inferred from the AWS tab being
            // there. "This agent is on AWS" and "this agent has no Superior
            // calls" are different statements, and only the second is a reason
            // to hide a table of Superior calls.
            //
            // The check waits for the data: callStats is null until it loads,
            // and a null is not an answer of zero.
            ...(awsHasId && callStats && (callStats.totals?.CallCount ?? 0) === 0
                ? []
                : [{
              key: 'call-stats',
              label: 'Call Stats',
              children: (
                <>
          {(() => {
            const ALL_METRICS = [
              { label: 'Contacts',   key: 'Contacts',  time: false },
              { label: 'Avg Talk',   key: 'AvgTalk',   time: true  },
              { label: 'Avg ACW',    key: 'AvgACW',    time: true  },
              { label: 'Avg HT',     key: 'AvgHT',     time: true  },
              { label: 'Max Talk',   key: 'MaxTalk',   time: true  },
              { label: 'Max ACW',    key: 'MaxACW',    time: true  },
              { label: 'Max HT',     key: 'MaxHT',     time: true  },
              { label: 'No Contact', key: 'NoContact', time: false },
            ];
            const NM = ALL_METRICS.length;
            const fmt = (time, v) => {
              if (v == null) return null;
              const n = Number(v);
              return time ? fmtTime(n) : n;
            };
            const dates = [...new Set(dailyStats.map((r) => r.DateKey))].filter(Boolean).sort().reverse().slice(0, 31);
            const groups = [...new Set(dailyStats.map((r) => `${r.Campaign}|||${r.Media}|||${r.Direction ?? ''}`))].sort();
            const lookup = {};
            dailyStats.forEach((r) => { lookup[`${r.Campaign}|||${r.Media}|||${r.Direction ?? ''}|||${r.DateKey}`] = r; });

            const spanCell = (record, field) => ({ rowSpan: record._first ? record._span : 0 });
            const pivotCols = [
              { title: 'Campaign',  dataIndex: 'campaign',  width: 130, fixed: 'left', ellipsis: true, onCell: (rec) => spanCell(rec) },
              { title: 'Media',     dataIndex: 'media',     width: 90,  fixed: 'left', ellipsis: true, onCell: (rec) => spanCell(rec) },
              { title: 'Direction', dataIndex: 'direction', width: 90,  fixed: 'left', ellipsis: true, onCell: (rec) => spanCell(rec) },
              { title: '',          dataIndex: 'metric',    width: 88,  fixed: 'left',
                render: (v) => <span style={{ fontSize: 11, color: '#8c8c8c' }}>{v}</span> },
              ...dates.map((d) => ({
                title: d.slice(5),
                dataIndex: d,
                width: 72,
                align: 'right',
                render: (v) => v != null ? v : <span style={{ color: '#d9d9d9' }}>-</span>,
              })),
            ];
            const pivotData = groups.flatMap((g) => {
              const [campaign, media, direction] = g.split('|||');
              return ALL_METRICS.map(({ label, key, time }, mi) => {
                const row = { key: `${g}|||${label}`, campaign, media, direction, metric: label, _first: mi === 0, _span: NM };
                dates.forEach((d) => {
                  const r = lookup[`${g}|||${d}`];
                  row[d] = r ? fmt(time, r[key]) : null;
                });
                return row;
              });
            });
            return (
              <Collapse
                size="small"
                style={{ marginBottom: 16 }}
                items={[{
                  key: 'daily-pivot',
                  label: 'Last 31 Days — Daily Breakdown',
                  children: dailyStats.length === 0 ? (
                    <Typography.Text type="secondary">No call data in the last 31 days.</Typography.Text>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <Table
                        size="small"
                        columns={pivotCols}
                        dataSource={pivotData}
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        bordered
                      />
                    </div>
                  ),
                }]}
              />
            );
          })()}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Call Statistics
            </Typography.Title>
            <Segmented
              options={['Inbound', 'Outbound', 'All']}
              value={callDirection}
              onChange={setCallDirection}
              size="small"
            />
            <Checkbox
              checked={excludeNoContact}
              onChange={(e) => setExcludeNoContact(e.target.checked)}
            >
              Exclude no contact dispo calls
            </Checkbox>
          </div>

          <Row gutter={[16, 16]} align="stretch" style={{ marginBottom: 16 }}>
            <Col xs={12} sm={3}>
              <Card size="small" style={{ height: '100%' }}><Statistic title="Total Contacts" value={callStats?.totals?.CallCount ?? 0} /></Card>
            </Col>
            <Col xs={12} sm={3}>
              <Card size="small" style={{ height: '100%' }}>
                <Statistic title="Avg Talk Time" value={fmtTime(callStats?.totals?.AvgAgentTime)} />
              </Card>
            </Col>
            <Col xs={12} sm={3}>
              <Card size="small" style={{ height: '100%' }}>
                <Statistic title="Avg ACW" value={fmtTime(callStats?.totals?.AvgACWTime)} />
              </Card>
            </Col>
            <Col xs={12} sm={3}>
              <Card size="small" style={{ height: '100%' }}>
                <Statistic title="Avg AHT" value={fmtTime(callStats?.totals?.AvgAHT)} />
              </Card>
            </Col>
            <Col xs={12} sm={4}>
              <Card size="small" style={{ height: '100%' }}>
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginBottom: 4, whiteSpace: 'nowrap' }}>Wrong Number / No Contact</div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <span style={{ fontSize: 24, fontWeight: 600, lineHeight: 1 }}>{callStats?.totals?.WrongNoContactQty ?? 0}</span>
                  <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', lineHeight: 1.4, paddingTop: 2 }}>
                    {(callStats?.totals?.PhoneCallCount > 0) && (
                      <div>Calls: {((callStats.totals.WrongNoContactPhone / callStats.totals.PhoneCallCount) * 100).toFixed(1)}%</div>
                    )}
                    {(callStats?.totals?.ChatCount > 0) && (
                      <div>Chat: {((callStats.totals.WrongNoContactChat / callStats.totals.ChatCount) * 100).toFixed(1)}%</div>
                    )}
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={3}>
              <Card size="small" style={{ height: '100%' }}>
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Called Support</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span style={{ fontSize: 24, fontWeight: 600 }}>{callStats?.totals?.CalledSupportQty ?? 0}</span>
                  <span style={{ fontSize: 14, color: 'rgba(0,0,0,0.45)' }}>
                    {callStats?.totals?.CallCount
                      ? ((callStats.totals.CalledSupportQty / callStats.totals.CallCount) * 100).toFixed(1)
                      : '0.0'}% of calls
                  </span>
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={3}>
              <Card size="small" style={{ height: '100%' }}>
                <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Refusals</div>
                {(refusals?.scorecard ?? []).length === 0
                  ? <span style={{ fontSize: 24, fontWeight: 600 }}>0</span>
                  : <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <span style={{ fontSize: 24, fontWeight: 600, lineHeight: 1 }}>
                        {refusals.scorecard.reduce((sum, r) => sum + r.RefusalCount, 0)}
                      </span>
                      <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)', lineHeight: 1.4, paddingTop: 2 }}>
                        {refusals.scorecard.map((r) => <div key={r.MediaName}>{r.MediaName}: {r.RefusalCount}</div>)}
                      </div>
                    </div>
                }
              </Card>
            </Col>
          </Row>

          {ahtTrend.length > 0 && (
            <Card size="small" title="AHT Over Time" style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={ahtTrend} margin={{ top: 24, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="Month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={fmtTime} tick={{ fontSize: 12 }} width={50} />
                  <Tooltip formatter={(v) => fmtTime(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="AvgAHT"       name="AHT"       stroke="#1677ff" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="AvgAgentTime" name="Talk Time"  stroke="#52c41a" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="AvgACWTime"   name="ACW"        stroke="#fa8c16" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          <div style={{ marginBottom: 8 }}>
            <Segmented
              options={[{ label: 'By Campaign', value: 'campaign' }, { label: 'By Skill', value: 'skill' }, { label: 'By Dispo', value: 'dispo' }]}
              value={callSkillView}
              onChange={setCallSkillView}
              size="small"
            />
          </div>

          <Table
            rowKey={(r) => `${r.Campaign}-${r.Skill_Name ?? ''}-${r.Dispo_Name ?? ''}-${r.Media}-${r.Direction}`}
            dataSource={callStats?.breakdown ?? []}
            columns={(() => {
              const cols = [...breakdownColumns];
              if (callSkillView === 'skill') {
                cols.splice(1, 0, { title: 'Skill', dataIndex: 'Skill_Name', key: 'Skill_Name', ellipsis: true });
              }
              if (callSkillView === 'dispo') {
                cols.splice(1, 0, { title: 'Dispo', dataIndex: 'Dispo_Name', key: 'Dispo_Name', ellipsis: true });
              }
              return cols.map(toResizable);
            })()}
            size="small"
            pagination={false}
            locale={{ emptyText: 'No call data in this date range.' }}
            components={{ header: { cell: ResizableTitle } }}
          />

          {(callStats?.skill_month ?? []).length > 0 && (() => {
            const rows   = callStats.skill_month;
            const months = [...new Set(rows.map((r) => r.Month))].sort();
            const skills = [...new Set(rows.map((r) => r.Skill_Name).filter(Boolean))].sort();
            const lookup = {};
            rows.forEach((r) => { lookup[`${r.Month}||${r.Skill_Name}`] = r; });

            const ALL_SKILL_METRICS = [
              { label: 'AHT',     key: 'AvgAHT',          time: true,  sum: false },
              { label: 'Talk',    key: 'AvgAgentTime',     time: true,  sum: false },
              { label: 'ACW',     key: 'AvgACWTime',       time: true,  sum: false },
              { label: 'Calls',   key: 'CallCount',        time: false, sum: true  },
              { label: 'Support', key: 'CalledSupportQty', time: false, sum: true  },
            ];

            // Compute totals per month — weighted avg for time metrics, sum for counts
            const totals = {};
            months.forEach((m) => {
              const monthRows = rows.filter((r) => r.Month === m);
              const totalCalls = monthRows.reduce((s, r) => s + (r.CallCount ?? 0), 0);
              const t = { CallCount: totalCalls, CalledSupportQty: monthRows.reduce((s, r) => s + (r.CalledSupportQty ?? 0), 0) };
              ['AvgAgentTime', 'AvgACWTime', 'AvgAHT'].forEach((key) => {
                t[key] = totalCalls > 0
                  ? Math.round(monthRows.reduce((s, r) => s + (r[key] ?? 0) * (r.CallCount ?? 0), 0) / totalCalls)
                  : null;
              });
              totals[m] = t;
            });

            const showAll = skillMetric === 'All';

            let pivotCols, pivotData;
            if (showAll) {
              pivotCols = [
                { title: 'Skill', dataIndex: 'skill', key: 'skill', width: 180, fixed: 'left', ellipsis: true },
                ...months.map((m) => ({
                  title: m,
                  dataIndex: m,
                  key: m,
                  align: 'right',
                  width: 120,
                  render: (cell) => cell == null ? '—' : (
                    <div style={{ lineHeight: 1.6, fontSize: 12 }}>
                      {ALL_SKILL_METRICS.map(({ label, key, time }) => {
                        const v = cell[key];
                        return (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                            <span style={{ color: '#8c8c8c' }}>{label}</span>
                            <span>{v != null ? (time ? fmtTime(v) : v) : '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  ),
                })),
              ];
              pivotData = [
                (() => {
                  const row = { skill: 'All Skills', _total: true };
                  months.forEach((m) => { row[m] = totals[m] ?? null; });
                  return row;
                })(),
                ...skills.map((s) => {
                  const row = { skill: s ?? '(none)', _total: false };
                  months.forEach((m) => { row[m] = lookup[`${m}||${s}`] ?? null; });
                  return row;
                }),
              ];
            } else {
              const METRIC_MAP = {
                AHT: { key: 'AvgAHT', time: true }, 'Talk Time': { key: 'AvgAgentTime', time: true },
                ACW: { key: 'AvgACWTime', time: true }, Calls: { key: 'CallCount', time: false },
                'Called Support': { key: 'CalledSupportQty', time: false },
              };
              const { key: metricKey, time: isTime } = METRIC_MAP[skillMetric];
              const fmt = (v) => v != null ? (isTime ? fmtTime(v) : v) : '—';
              pivotCols = [
                { title: 'Skill', dataIndex: 'skill', key: 'skill', width: 180, fixed: 'left', ellipsis: true },
                ...months.map((m) => ({
                  title: m, dataIndex: m, key: m, align: 'right', width: 90,
                  render: (v) => fmt(v),
                })),
              ];
              pivotData = [
                (() => {
                  const row = { skill: 'All Skills', _total: true };
                  months.forEach((m) => { row[m] = totals[m]?.[metricKey] ?? null; });
                  return row;
                })(),
                ...skills.map((s) => {
                  const row = { skill: s ?? '(none)', _total: false };
                  months.forEach((m) => {
                    const cell = lookup[`${m}||${s}`];
                    row[m] = cell ? cell[metricKey] : null;
                  });
                  return row;
                }),
              ];
            }

            return (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <Typography.Text strong>Skill Trend by Month</Typography.Text>
                  <Segmented
                    options={['AHT', 'Talk Time', 'ACW', 'Calls', 'Called Support', 'All']}
                    value={skillMetric}
                    onChange={setSkillMetric}
                    size="small"
                  />
                </div>
                <Table
                  rowKey="skill"
                  dataSource={pivotData}
                  columns={pivotCols}
                  size="small"
                  pagination={false}
                  scroll={{ x: true }}
                  rowClassName={(r) => r._total ? 'skill-trend-total-row' : ''}
                />
              </div>
            );
          })()}

          {(callStats?.talk_ranges ?? []).length > 0 && (() => {
            const TALK_ORDER = ['0-1 min','1-3 min','3-7 min','7-10 min','10-15 min','GT 15 min'];
            const ACW_ORDER  = ['0-1 min','1-4 min','4-8 min','GT 8 min'];
            const talkData = [...(callStats.talk_ranges)].sort((a, b) => TALK_ORDER.indexOf(a.TalkRange) - TALK_ORDER.indexOf(b.TalkRange));
            const acwData  = [...(callStats.acw_ranges ?? [])].sort((a, b) => ACW_ORDER.indexOf(a.ACWRange) - ACW_ORDER.indexOf(b.ACWRange));
            const talkCols = [
              { title: 'Campaign',      dataIndex: 'Campaign',       key: 'Campaign',       ellipsis: true },
              { title: 'Talk Range',    dataIndex: 'TalkRange',      key: 'TalkRange',      width: 90 },
              { title: '% of Contacts', dataIndex: 'PctOfContacts',  key: 'PctOfContacts',  width: 115, align: 'right', render: (v) => `${v}%` },
              { title: 'Contacts',      dataIndex: 'Contacts',       key: 'Contacts',       width: 80,  align: 'right' },
              { title: 'Max Talk',      dataIndex: 'MaxTalk',        key: 'MaxTalk',        width: 80,  align: 'right', render: fmtTime },
            ];
            const acwCols = [
              { title: 'Campaign',      dataIndex: 'Campaign',       key: 'Campaign',       ellipsis: true },
              { title: 'ACW Range',     dataIndex: 'ACWRange',       key: 'ACWRange',       width: 90 },
              { title: '% of Contacts', dataIndex: 'PctOfContacts',  key: 'PctOfContacts',  width: 115, align: 'right', render: (v) => `${v}%` },
              { title: 'Contacts',      dataIndex: 'Contacts',       key: 'Contacts',       width: 80,  align: 'right' },
              { title: 'Max ACW',       dataIndex: 'MaxACW',         key: 'MaxACW',         width: 80,  align: 'right', render: fmtTime },
            ];
            return (
              <Row gutter={24} style={{ marginTop: 20 }}>
                <Col span={12}>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Talk Time Range</Typography.Text>
                  <Table rowKey={(r) => `${r.Campaign}-${r.TalkRange}`} dataSource={talkData} columns={talkCols} size="small" pagination={false} />
                </Col>
                <Col span={12}>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>ACW Range</Typography.Text>
                  <Table rowKey={(r) => `${r.Campaign}-${r.ACWRange}`} dataSource={acwData} columns={acwCols} size="small" pagination={false} />
                </Col>
              </Row>
            );
          })()}

          {(() => {
            const scoreRows = refusals?.scorecard ?? [];
            const byMonth   = refusals?.by_month  ?? [];
            const listRows  = refusals?.list       ?? [];
            if (scoreRows.length === 0 && byMonth.length === 0 && listRows.length === 0) return null;

            const months   = [...new Set(byMonth.map((r) => r.Month))].sort();
            const medias   = [...new Set(byMonth.map((r) => r.MediaName))].sort();
            const mLookup  = {};
            byMonth.forEach((r) => { mLookup[`${r.Month}||${r.MediaName}`] = r.RefusalCount; });

            const pivotCols = [
              { title: 'Media', dataIndex: 'media', key: 'media', width: 140, fixed: 'left' },
              ...months.map((m) => ({
                title: m,
                dataIndex: m,
                key: m,
                align: 'right',
                width: 90,
                render: (v) => (v ?? '—'),
              })),
            ];
            const pivotData = medias.map((med) => {
              const row = { media: med };
              months.forEach((m) => { row[m] = mLookup[`${m}||${med}`] ?? null; });
              return row;
            });

            const listCols = [
              { title: 'Time',       dataIndex: 'RefusalTime', key: 'RefusalTime', width: 170,
                render: (v) => v ? v.replace('T', ' ').slice(0, 19) : '—' },
              { title: 'Media',      dataIndex: 'MediaName',   key: 'MediaName',   width: 120 },
              { title: 'Reason',     dataIndex: 'Reason',      key: 'Reason',      ellipsis: true },
            ];

            return (
              <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <Typography.Text strong>Refusals</Typography.Text>
                  <Segmented
                    options={[{ label: 'By Month', value: 'month' }, { label: 'List of Refusals', value: 'list' }]}
                    value={refusalsView}
                    onChange={setRefusalsView}
                    size="small"
                  />
                </div>
                {refusalsView === 'month'
                  ? <Table rowKey="media" dataSource={pivotData} columns={pivotCols} size="small" pagination={false} scroll={{ x: true }} />
                  : <Table rowKey={(r, i) => i} dataSource={listRows} columns={listCols} size="small"
                      pagination={{ defaultPageSize: 50, showSizeChanger: true }}
                      locale={{ emptyText: 'No refusals in this date range.' }}
                    />
                }
              </div>
            );
          })()}
                </>
              ),
            }]),

            // Only for agents whose QAUsers profile carries an AWS ID. Without
            // one there is nothing to match in CNEDailyStats, so the tab would
            // be permanently empty rather than merely empty for this range.
            ...(awsHasId ? [{
              key: 'aws-calls',
              label: 'AWS Stats',
              children: (() => {
                const totals = awsCalls.reduce((a, r) => {
                  a.inbound   += r.ContactsHandledIncoming || 0;
                  a.outbound  += r.ContactsHandledOutbound || 0;
                  a.callbacks += r.CallbackContactsHandled || 0;
                  a.days.add(r.IntervalDate);
                  return a;
                }, { inbound: 0, outbound: 0, callbacks: 0, days: new Set() });

                // Total over contacts, the same way the CNE Monthly Stats page
                // computes these, so the two agree on identical data. A mean of
                // the daily means would weigh a four-contact Sunday the same as
                // a sixty-contact Monday.
                //
                // Days with no total are left out of both sides rather than
                // counted as zero: a missing figure is not a fast day.
                const contactsOf = (r) => (r.ContactsHandledIncoming || 0)
                  + (r.ContactsHandledOutbound || 0)
                  + (r.CallbackContactsHandled || 0);
                const perContact = (rows, totalKey) => {
                  let num = 0, den = 0;
                  rows.forEach((r) => {
                    const c = contactsOf(r);
                    if (r[totalKey] == null || !c) return;
                    num += Number(r[totalKey]);
                    den += c;
                  });
                  return den ? num / den : null;
                };

                const cards = [
                  { title: 'Inbound Calls',       value: totals.inbound.toLocaleString() },
                  { title: 'Outbound Calls',      value: totals.outbound.toLocaleString() },
                  { title: 'Callbacks',           value: totals.callbacks.toLocaleString() },
                  { title: 'Days',                value: totals.days.size },
                  { title: 'Avg ACW (All)',       value: fmtTime(hrsToSecs(perContact(awsCalls, 'AfterContactWorkHrs'))) },
                  { title: 'Avg Add. ACW (All)',  value: fmtTime(hrsToSecs(perContact(awsCalls, 'State1ACWAdditionalHrs'))) },
                  { title: 'Avg Talk Time (All)', value: fmtTime(hrsToSecs(perContact(awsCalls, 'AgentInteractionHoldHrs'))) },
                  { title: 'Avg AHT (All)',       value: fmtTime(hrsToSecs(perContact(awsCalls, 'ContactHandleHrs'))) },
                ];

                // Each month built the same way as the cards above, so a month
                // here reads as the same number the CNE Monthly Stats page shows
                // for it.
                const byMonth = {};
                awsCalls.forEach((r) => {
                  const mk = (r.IntervalDate || '').slice(0, 7);
                  if (!mk) return;
                  (byMonth[mk] ??= []).push(r);
                });
                const trend = Object.keys(byMonth).sort().map((mk) => ({
                  Month:        dayjs(`${mk}-01`).format('MMMM YYYY'),
                  AvgAHT:       hrsToSecs(perContact(byMonth[mk], 'ContactHandleHrs')),
                  AvgAgentTime: hrsToSecs(perContact(byMonth[mk], 'AgentInteractionHoldHrs')),
                  AvgACWTime:   hrsToSecs(perContact(byMonth[mk], 'AfterContactWorkHrs')),
                }));

                const dailyCols = [
                  { title: 'Date', dataIndex: 'IntervalDate', key: 'date', width: 110,
                    defaultSortOrder: 'descend',
                    sorter: (a, b) => (a.IntervalDate || '').localeCompare(b.IntervalDate || '') },
                  { title: 'Campaign', dataIndex: 'CampaignName', key: 'campaign', width: 150, ellipsis: true,
                    sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
                  { title: 'Contacts Handled', dataIndex: 'TotalContactsHandled', key: 'total', width: 140, align: 'right',
                    sorter: (a, b) => a.TotalContactsHandled - b.TotalContactsHandled,
                    render: (v) => v?.toLocaleString() ?? '—' },
                  { title: 'Avg ACW (All)', key: 'avgacw', width: 120, align: 'right',
                    sorter: (a, b) => (a.AvgACWHrs || 0) - (b.AvgACWHrs || 0),
                    render: (_, r) => fmtTime(hrsToSecs(r.AvgACWHrs)) },
                  { title: 'Avg Add. ACW (All)', key: 'avgaddacw', width: 150, align: 'right',
                    sorter: (a, b) => (a.AvgAdditionalACWHrs || 0) - (b.AvgAdditionalACWHrs || 0),
                    render: (_, r) => fmtTime(hrsToSecs(r.AvgAdditionalACWHrs)) },
                  { title: 'Avg Talk Time (All)', key: 'avgtalk', width: 150, align: 'right',
                    sorter: (a, b) => (a.AvgAgentInteractionHoldHrs || 0) - (b.AvgAgentInteractionHoldHrs || 0),
                    render: (_, r) => fmtTime(hrsToSecs(r.AvgAgentInteractionHoldHrs)) },
                  { title: 'Avg AHT (All)', key: 'avght', width: 120, align: 'right',
                    sorter: (a, b) => (a.AvgHandleHrs || 0) - (b.AvgHandleHrs || 0),
                    render: (_, r) => fmtTime(hrsToSecs(r.AvgHandleHrs)) },
                  { title: 'Inbound', dataIndex: 'ContactsHandledIncoming', key: 'inbound', width: 100, align: 'right',
                    sorter: (a, b) => a.ContactsHandledIncoming - b.ContactsHandledIncoming,
                    render: (v) => v?.toLocaleString() ?? '—' },
                  { title: 'Outbound', dataIndex: 'ContactsHandledOutbound', key: 'outbound', width: 105, align: 'right',
                    sorter: (a, b) => a.ContactsHandledOutbound - b.ContactsHandledOutbound,
                    render: (v) => v?.toLocaleString() ?? '—' },
                  { title: 'Callbacks', dataIndex: 'CallbackContactsHandled', key: 'callbacks', width: 105, align: 'right',
                    sorter: (a, b) => a.CallbackContactsHandled - b.CallbackContactsHandled,
                    render: (v) => v?.toLocaleString() ?? '—' },
                ];

                // Where the day went, rather than what was handled. Campaign is
                // kept even though the agent is fixed: a row is one agent-day
                // per campaign, so without it a day split across two campaigns
                // reads as a repeated date with no explanation.
                const hrsCol = (title, key, width = 125) => ({
                  title, key, width, align: 'right',
                  sorter: (a, b) => (a[key] || 0) - (b[key] || 0),
                  render: (_, r) => fmtTime(hrsToSecs(r[key])),
                });

                const statusCols = [
                  { title: 'Date', dataIndex: 'IntervalDate', key: 'date', width: 110, fixed: 'left',
                    defaultSortOrder: 'descend',
                    sorter: (a, b) => (a.IntervalDate || '').localeCompare(b.IntervalDate || '') },
                  { title: 'Campaign', dataIndex: 'CampaignName', key: 'campaign', width: 140, ellipsis: true,
                    sorter: (a, b) => (a.CampaignName || '').localeCompare(b.CampaignName || '') },
                  { title: 'Contacts Handled', dataIndex: 'TotalContactsHandled', key: 'total', width: 140, align: 'right',
                    sorter: (a, b) => a.TotalContactsHandled - b.TotalContactsHandled,
                    render: (v) => v?.toLocaleString() ?? '—' },
                  hrsCol('On Break',       'State2OnBreakHrs',        110),
                  hrsCol('Lunch',          'State3LunchHrs',          100),
                  hrsCol('Online',         'OnlineTimeHrs',           110),
                  hrsCol('On Contact',     'OnContactTimeHrs',        120),
                  hrsCol('Idle',           'IdleTimeHrs',             100),
                  hrsCol('Non-Productive', 'NonProductiveTimeHrs',    140),
                  { title: 'Occupancy', dataIndex: 'OccupancyPct', key: 'occ', width: 110, align: 'right',
                    sorter: (a, b) => (a.OccupancyPct || 0) - (b.OccupancyPct || 0),
                    render: (v) => v != null ? `${Number(v).toFixed(2)}%` : '—' },
                  hrsCol('Busy',             'BusyTimeHrs',              100),
                  hrsCol('Missed Call Time', 'MissedCallTimeHrs',        140),
                  hrsCol('Chat or Email',    'State4ChatEmailHrs',       130),
                  hrsCol('Special Projects', 'State5SpecialProjectsHrs', 145),
                  hrsCol('Meeting',          'State6MeetingHrs',         110),
                  hrsCol('QA Coaching',      'State8QACoachingHrs',      125),
                  hrsCol('Outbound',         'State9OutboundHrs',        115),
                  hrsCol('Training',         'State10TrainingHrs',       110),
                  hrsCol('Away from Desk',   'State11AwayFromDeskHrs',   145),
                  hrsCol('System Issue',     'State12SystemIssueHrs',    125),
                  hrsCol('Declined Call',    'State13DeclinedCallHrs',   130),
                  hrsCol('Missed Call',      'State14MissedCallHrs',     125),
                  hrsCol('FMLA Away',        'State15FMLAAwayHrs',       120),
                ];

                // Where the month went, as a share of time signed in. The source
                // is a hierarchy, not a flat list. Online splits three ways --
                // On Contact, Idle, Non-Productive -- and Non-Productive is
                // itself the sum of the numbered agent states. Both hold to the
                // thousandth of an hour against the raw rows. A rollup shown
                // beside its own parts would count the same time twice, so the
                // parts sit under a grouped header instead of alongside.
                //
                // BusyTimeHrs and MissedCallTimeHrs are deliberately absent.
                // They are empty in every row the vendor has sent, and neither
                // could be a fourth share of Online: the three above already
                // account for all of it. Missed Call below is state 14.
                //
                // Break and lunch appear twice on purpose: as a percentage
                // inside the breakdown, so it adds up, and again as minutes at
                // the end the way the CCaaS monthly view has them. They are an
                // entitlement measured against a number of minutes, not a share
                // of the day, and a percentage alone would hide a long break
                // inside a long shift.
                const TOP_STATES = [
                  ['On Contact',     'OnContactTimeHrs'],
                  ['Idle',           'IdleTimeHrs'],
                  ['Non-Productive', 'NonProductiveTimeHrs'],
                ];

                // Every numbered state the export ships. There is no state 7.
                const NP_STATES = [
                  ['Add. ACW',         'State1ACWAdditionalHrs'],
                  ['On Break',         'State2OnBreakHrs'],
                  ['Lunch',            'State3LunchHrs'],
                  ['Chat or Email',    'State4ChatEmailHrs'],
                  ['Special Projects', 'State5SpecialProjectsHrs'],
                  ['Meeting',          'State6MeetingHrs'],
                  ['QA Coaching',      'State8QACoachingHrs'],
                  ['Outbound',         'State9OutboundHrs'],
                  ['Training',         'State10TrainingHrs'],
                  ['Away from Desk',   'State11AwayFromDeskHrs'],
                  ['System Issue',     'State12SystemIssueHrs'],
                  ['Declined Call',    'State13DeclinedCallHrs'],
                  ['Missed Call',      'State14MissedCallHrs'],
                  ['FMLA Away',        'State15FMLAAwayHrs'],
                ];

                const PCT_STATES = [...TOP_STATES, ...NP_STATES];

                const monthMap = {};
                awsCalls.forEach((r) => {
                  const mk = (r.IntervalDate || '').slice(0, 7);
                  if (!mk) return;
                  const m = monthMap[mk] || (monthMap[mk] = { MonthKey: mk, OnlineHrs: 0, BreakHrs: 0, LunchHrs: 0, states: {}, days: new Set() });
                  m.days.add(r.IntervalDate);
                  m.OnlineHrs += Number(r.OnlineTimeHrs || 0);
                  m.BreakHrs  += Number(r.State2OnBreakHrs || 0);
                  m.LunchHrs  += Number(r.State3LunchHrs || 0);
                  // Only a value the source actually sent counts. Folding a
                  // null in as a zero would render a column the API never sent
                  // as a confident 0%, which is how a missing IdleTimeHrs once
                  // read as an agent who was never idle.
                  PCT_STATES.forEach(([, col]) => {
                    if (r[col] != null) m.states[col] = (m.states[col] || 0) + Number(r[col]);
                  });
                });

                const monthRows = Object.values(monthMap)
                  .sort((a, b) => b.MonthKey.localeCompare(a.MonthKey))
                  .map((m) => {
                    const row = {
                      MonthKey:   m.MonthKey,
                      Days:       m.days.size,
                      OnlineMins: Math.round(m.OnlineHrs * 60),
                      BreakMins:  Math.round(m.BreakHrs * 60),
                      LunchMins:  Math.round(m.LunchHrs * 60),
                    };
                    PCT_STATES.forEach(([label, col]) => {
                      // No online time means no denominator, and a state no row
                      // reported is unknown -- neither is the same as a state
                      // that took none of the month.
                      row[label] = (m.OnlineHrs > 0 && m.states[col] !== undefined)
                        ? Math.round((m.states[col] / m.OnlineHrs) * 1000) / 10
                        : null;
                    });
                    return row;
                  });

                const pctCol = ([label]) => ({
                  title: `${label} %`, dataIndex: label, key: label, width: 130, align: 'right',
                  sorter: (a, b) => (a[label] ?? -1) - (b[label] ?? -1),
                  render: (v) => v == null ? <Typography.Text type="secondary">—</Typography.Text> : `${v}%`,
                });

                const monthCols = [
                  { title: 'Month', dataIndex: 'MonthKey', key: 'MonthKey', width: 100, fixed: 'left' },
                  { title: 'Days', dataIndex: 'Days', key: 'Days', width: 70, align: 'right' },
                  { title: 'Online Mins', dataIndex: 'OnlineMins', key: 'OnlineMins', width: 110, align: 'right',
                    render: (v) => v ? v.toLocaleString() : <Typography.Text type="secondary">—</Typography.Text> },
                  ...TOP_STATES.map(pctCol),
                  { title: 'Non-Productive Breakdown', key: 'np-group', children: NP_STATES.map(pctCol) },
                  { title: 'Break Mins', dataIndex: 'BreakMins', key: 'BreakMins', width: 110, align: 'right',
                    render: (v) => v ? v.toLocaleString() : <Typography.Text type="secondary">—</Typography.Text> },
                  { title: 'Lunch Mins', dataIndex: 'LunchMins', key: 'LunchMins', width: 110, align: 'right',
                    render: (v) => v ? v.toLocaleString() : <Typography.Text type="secondary">—</Typography.Text> },
                ];

                return (
                  <>
                    <Collapse
                      style={{ marginBottom: 16 }}
                      items={[{
                        key: 'aws-daily',
                        label: 'Daily Call Breakdown',
                        children: (
                          <Table
                            rowKey={(r, i) => `${r.IntervalDate}-${r.CampaignName ?? ''}-${i}`}
                            dataSource={awsCalls}
                            columns={dailyCols}
                            loading={awsLoading}
                            size="small"
                            scroll={{ x: 'max-content' }}
                            pagination={{ defaultPageSize: 31, showSizeChanger: true, pageSizeOptions: ['31', '50', '100'], showTotal: (t) => `${t} days` }}
                            locale={{ emptyText: 'No AWS call data in this date range.' }}
                          />
                        ),
                      }]}
                    />

                    <Collapse
                      style={{ marginBottom: 16 }}
                      items={[{
                        key: 'aws-status',
                        label: 'Daily Status Breakdown',
                        children: (
                          <Table
                            rowKey={(r, i) => `${r.IntervalDate}-${r.CampaignName ?? ''}-${i}`}
                            dataSource={awsCalls}
                            columns={statusCols}
                            loading={awsLoading}
                            size="small"
                            scroll={{ x: 'max-content' }}
                            pagination={{ defaultPageSize: 31, showSizeChanger: true, pageSizeOptions: ['31', '50', '100'], showTotal: (t) => `${t} days` }}
                            locale={{ emptyText: 'No AWS status data in this date range.' }}
                          />
                        ),
                      }]}
                    />

                    <Collapse
                      style={{ marginBottom: 16 }}
                      items={[{
                        key: 'aws-monthly',
                        label: 'Monthly Status Breakdown',
                        children: (
                          <Table
                            rowKey="MonthKey"
                            dataSource={monthRows}
                            columns={monthCols}
                            loading={awsLoading}
                            size="small"
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            locale={{ emptyText: 'No AWS status data in this date range.' }}
                          />
                        ),
                      }]}
                    />

                    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                      {cards.map((c) => (
                        <Card key={c.title} size="small" style={{ flex: '1 1 0', minWidth: 110 }}>
                          <Statistic title={c.title} value={c.value} />
                        </Card>
                      ))}
                    </div>

                    {trend.length > 0 && (
                      <Card size="small" title="AHT Over Time" style={{ marginBottom: 16 }}>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={trend} margin={{ top: 24, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="Month" tick={{ fontSize: 12 }} />
                            <YAxis tickFormatter={fmtTime} tick={{ fontSize: 12 }} width={50} />
                            <Tooltip formatter={(v) => fmtTime(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="AvgAHT"       name="AHT"       stroke="#1677ff" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="AvgAgentTime" name="Talk Time" stroke="#52c41a" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="AvgACWTime"   name="ACW"       stroke="#fa8c16" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card>
                    )}
                  </>
                );
              })(),
            }] : []),


            {
              key: 'evaluations',
              label: 'Evaluations',
              children: (
                <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {agentRecord != null && (
              <Col xs={12} sm={6}>
                <Card size="small">
                  <Statistic title="Qty Evals Pending" value={pendingCount}
                    valueStyle={{ color: pendingCount > 0 ? '#1677ff' : undefined }} />
                </Card>
              </Col>
            )}
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Qty Rescore Requested" value={rescoreCount}
                  valueStyle={{ color: rescoreCount > 0 ? '#722ed1' : undefined }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Qty Completed" value={completedCount}
                  valueStyle={{ color: completedCount > 0 ? '#52c41a' : undefined }} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic title="Avg Score" value={avgScore ?? '—'} suffix={avgScore !== null ? '%' : ''} />
              </Card>
            </Col>
          </Row>

          {scoreByMonth.length > 0 && (
            <Card size="small" title="Avg Eval Score by Month (Call Date)" style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={scoreByMonth} margin={{ top: 16, right: 20, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="Month" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} width={42} />
                  <Tooltip content={<ScoreBarTooltip />} />
                  {(scoreByMonth.some((d) => d.AvgTargetScore != null) ||
                    scoreByMonth.some((d) => d.AvgHighScore != null)) && (
                    <Legend verticalAlign="top" align="right" iconType="plainline"
                      payload={[
                        ...(scoreByMonth.some((d) => d.AvgTargetScore != null) ? [{ value: 'Target (Gold)', type: 'plainline', color: '#1677ff' }] : []),
                        ...(scoreByMonth.some((d) => d.AvgHighScore   != null) ? [{ value: 'Platinum',      type: 'plainline', color: '#722ed1' }] : []),
                      ]}
                    />
                  )}
                  <Bar dataKey="AvgScore" name="Avg Score" radius={[4, 4, 0, 0]} maxBarSize={72}
                    label={<ScoreBarLabel />}>
                    {scoreByMonth.map((entry) => (
                      <Cell key={entry.MonthKey} fill={scoreColor(entry.AvgScore)} />
                    ))}
                  </Bar>
                  {scoreByMonth.some((d) => d.AvgTargetScore != null) && (
                    <Line type="monotone" dataKey="AvgTargetScore" name="Target (Gold)"
                      stroke="#1677ff" strokeWidth={2} strokeDasharray="5 3"
                      dot={{ r: 4, fill: '#1677ff', strokeWidth: 0 }} activeDot={{ r: 5 }}
                      connectNulls legendType="none" />
                  )}
                  {scoreByMonth.some((d) => d.AvgHighScore != null) && (
                    <Line type="monotone" dataKey="AvgHighScore" name="Platinum"
                      stroke="#722ed1" strokeWidth={2} strokeDasharray="5 3"
                      dot={{ r: 4, fill: '#722ed1', strokeWidth: 0 }} activeDot={{ r: 5 }}
                      connectNulls legendType="none" />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Typography.Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>
            Evaluations
          </Typography.Title>

          <Table
            rowKey="ID"
            dataSource={agentRecord == null ? filteredEvalHistory.filter((r) => r.Status === 'completed' || r.Status === 'rescore_requested') : filteredEvalHistory}
            columns={evalColumns.map(toResizable)}
            size="small"
            pagination={{ defaultPageSize: 10, size: 'small' }}
            style={{ marginBottom: 24 }}
            locale={{ emptyText: 'No evaluations in this date range.' }}
            components={{ header: { cell: ResizableTitle } }}
          />

          <Typography.Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>
            Feedback Only Evaluations
            <Tag color="purple" style={{ marginLeft: 10, verticalAlign: 'middle', fontSize: 12, fontWeight: 400 }}>
              Not counted in official score
            </Tag>
          </Typography.Title>

          <Table
            rowKey="ID"
            dataSource={agentRecord == null ? filteredFeedbackHistory.filter((r) => r.Status === 'completed' || r.Status === 'rescore_requested') : filteredFeedbackHistory}
            columns={evalColumns.map(toResizable)}
            size="small"
            pagination={{ defaultPageSize: 10, size: 'small' }}
            style={{ marginBottom: 24 }}
            locale={{ emptyText: 'No feedback-only evaluations in this date range.' }}
            components={{ header: { cell: ResizableTitle } }}
          />

          <Card
            title="Section Stats (Completed Evals)"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Segmented
                size="small"
                value={sectionMode}
                onChange={setSectionMode}
                options={[
                  { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                  { label: 'By Month',       value: 'month',       icon: <CalendarOutlined /> },
                  { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                  { label: 'Occ. by Month',  value: 'occ_pivot',   icon: <WarningOutlined /> },
                ]}
              />
            }
          >
            {sectionMode === 'month_pivot' ? (() => {
              const months = [...new Set(sectionStats.map((r) => r.MonthKey))].sort();
              const mLabels = {};
              months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
              const seen = []; const map = {};
              sectionStats.forEach((r) => {
                const k = `${r.Campaign}||${r.SectionName}`;
                if (!map[k]) { map[k] = { _key: k, Campaign: r.Campaign, SectionName: r.SectionName }; seen.push(k); }
                map[k][r.MonthKey] = r.AvgPct;
              });
              const pivotData = seen.map((k) => map[k]);
              const cols = [
                { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left' },
                { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 200, ellipsis: true, fixed: 'left' },
                ...months.map((mk) => ({
                  title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                  render: (_, r) => {
                    const pct = r[mk];
                    if (pct == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
                    const c = pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f';
                    return <span style={{ color: c, fontWeight: 600 }}>{pct}%</span>;
                  },
                })),
              ];
              return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                loading={sectionLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                locale={{ emptyText: 'No completed evaluations in this date range.' }} />;
            })() : sectionMode === 'occ_pivot' ? (() => {
              const months = [...new Set(sectionStats.map((r) => r.MonthKey))].sort();
              const mLabels = {};
              months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
              const seen = []; const map = {};
              sectionStats.forEach((r) => {
                const k = `${r.Campaign}||${r.SectionName}`;
                if (!map[k]) { map[k] = { _key: k, Campaign: r.Campaign, SectionName: r.SectionName }; seen.push(k); }
                map[k][r.MonthKey] = r.Occurrences ?? 0;
              });
              const pivotData = seen.map((k) => map[k]);
              const cols = [
                { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left' },
                { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 200, ellipsis: true, fixed: 'left' },
                ...months.map((mk) => ({
                  title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                  render: (_, r) => {
                    const v = r[mk];
                    if (v == null || v === 0) return <span style={{ color: '#d9d9d9' }}>0</span>;
                    return <Tag color="orange" style={{ margin: 0 }}>{v}</Tag>;
                  },
                })),
              ];
              return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                loading={sectionLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                locale={{ emptyText: 'No completed evaluations in this date range.' }} />;
            })() : (
              <Table
                rowKey={(r) => sectionMode === 'month' ? `${r.Campaign}|${r.MonthKey}|${r.SectionName}` : `${r.Campaign}|${r.SectionName}`}
                dataSource={sectionStats}
                loading={sectionLoading}
                size="small"
                pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} sections` }}
                locale={{ emptyText: 'No completed evaluations in this date range.' }}
                columns={[
                  { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 130, ellipsis: true },
                  ...(sectionMode === 'month' ? [{ title: 'Call Date Month', dataIndex: 'Month', key: 'Month', width: 150 }] : []),
                  { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', ellipsis: true },
                  { title: '# Evals', dataIndex: 'CaseCount', key: 'CaseCount', width: 85, align: 'right' },
                  { title: 'Occurrences', dataIndex: 'Occurrences', key: 'Occurrences', width: 115, align: 'right',
                    render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
                  },
                  { title: 'Avg Score', dataIndex: 'AvgPct', key: 'AvgPct', width: 180,
                    render: (pct) => pct == null ? <Typography.Text type="secondary">—</Typography.Text> : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Progress percent={pct} showInfo={false}
                          strokeColor={pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f'}
                          size="small" style={{ flex: 1, minWidth: 60 }} />
                        <Typography.Text strong style={{ color: pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f', whiteSpace: 'nowrap' }}>
                          {pct}%
                        </Typography.Text>
                      </div>
                    ),
                  },
                  { title: 'Pts Earned',   dataIndex: 'TotalEarned', key: 'TotalEarned', width: 95,  align: 'right' },
                  { title: 'Pts Possible', dataIndex: 'TotalMax',    key: 'TotalMax',    width: 105, align: 'right' },
                ]}
              />
            )}
          </Card>

          <Card
            title="Question Stats (Completed Evals)"
            size="small"
            style={{ marginBottom: 32 }}
            extra={
              <Segmented
                size="small"
                value={questionMode}
                onChange={setQuestionMode}
                options={[
                  { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                  { label: 'By Month',       value: 'month',       icon: <CalendarOutlined /> },
                  { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                ]}
              />
            }
          >
            {questionMode === 'month_pivot' ? (() => {
              const months = [...new Set(questionStats.map((r) => r.MonthKey))].sort();
              const mLabels = {};
              months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
              const seen = []; const map = {};
              questionStats.forEach((r) => {
                const k = `${r.Campaign}||${r.SectionName}||${r.QuestionText}`;
                if (!map[k]) { map[k] = { _key: k, Campaign: r.Campaign, SectionName: r.SectionName, QuestionText: r.QuestionText }; seen.push(k); }
                map[k][r.MonthKey] = r.AvgPct;
              });
              const pivotData = seen.map((k) => map[k]);
              const cols = [
                { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left',
                  onCell: (r, i) => {
                    const prev = i > 0 ? pivotData[i - 1].Campaign : null;
                    if (r.Campaign === prev) return { rowSpan: 0 };
                    return { rowSpan: pivotData.filter((x) => x.Campaign === r.Campaign).length };
                  }
                },
                { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 150, ellipsis: true, fixed: 'left',
                  onCell: (r, i) => {
                    const prevKey = i > 0 ? `${pivotData[i - 1].Campaign}|${pivotData[i - 1].SectionName}` : null;
                    const currKey = `${r.Campaign}|${r.SectionName}`;
                    if (currKey === prevKey) return { rowSpan: 0 };
                    return { rowSpan: pivotData.filter((x) => x.Campaign === r.Campaign && x.SectionName === r.SectionName).length };
                  }
                },
                { title: 'Question', dataIndex: 'QuestionText', key: '__q', width: 220, ellipsis: true, fixed: 'left' },
                ...months.map((mk) => ({
                  title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                  render: (_, r) => {
                    const pct = r[mk];
                    if (pct == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
                    const c = pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f';
                    return <span style={{ color: c, fontWeight: 600 }}>{pct}%</span>;
                  },
                })),
              ];
              return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                loading={questionLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                locale={{ emptyText: 'No completed evaluations in this date range.' }} />;
            })() : (
              <Table
                rowKey={(r) => questionMode === 'month' ? `${r.Campaign}|${r.MonthKey}|${r.SectionName}|${r.QuestionText}` : `${r.Campaign}|${r.SectionName}|${r.QuestionText}`}
                dataSource={questionStats}
                loading={questionLoading}
                size="small"
                pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} questions` }}
                locale={{ emptyText: 'No completed evaluations in this date range.' }}
                columns={[
                  { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 130, ellipsis: true },
                  ...(questionMode === 'month' ? [{ title: 'Call Date Month', dataIndex: 'Month', key: 'Month', width: 150 }] : []),
                  { title: 'Section',  dataIndex: 'SectionName',  key: 'SectionName',  width: 150, ellipsis: true },
                  { title: 'Question', dataIndex: 'QuestionText',  key: 'QuestionText', ellipsis: true },
                  { title: '# Questions', dataIndex: 'AnswerCount', key: 'AnswerCount', width: 90, align: 'right' },
                  { title: 'Avg Score', dataIndex: 'AvgPct', key: 'AvgPct', width: 180,
                    render: (pct) => pct == null ? <Typography.Text type="secondary">—</Typography.Text> : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Progress percent={pct} showInfo={false}
                          strokeColor={pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f'}
                          size="small" style={{ flex: 1, minWidth: 60 }} />
                        <Typography.Text strong style={{ color: pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f', whiteSpace: 'nowrap' }}>
                          {pct}%
                        </Typography.Text>
                      </div>
                    ) },
                  { title: 'Pts Earned',   dataIndex: 'TotalEarned', key: 'TotalEarned', width: 95,  align: 'right' },
                  { title: 'Pts Possible', dataIndex: 'TotalMax',    key: 'TotalMax',    width: 105, align: 'right' },
                ]}
              />
            )}
          </Card>
                </>
              ),
            },
            {
              key: 'cxone-scorecards',
              label: 'CXOne Evals (Legacy)',
              children: (() => {
                const scored = cxoneData.filter((r) => r.ScorePercent != null);
                const totalEvals = cxoneData.length;
                const avgScore = scored.length
                  ? Math.round(scored.reduce((s, r) => s + parseFloat(r.ScorePercent), 0) / scored.length * 10) / 10
                  : null;

                // Monthly rollup for trend chart
                const byMonth = {};
                scored.forEach((r) => {
                  const mk = (r.CallDate || '').substring(0, 7);
                  if (!mk) return;
                  if (!byMonth[mk]) byMonth[mk] = { total: 0, count: 0 };
                  byMonth[mk].total += parseFloat(r.ScorePercent);
                  byMonth[mk].count += 1;
                });
                const monthlyTrend = Object.entries(byMonth)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([mk, d]) => ({
                    month: mk,
                    AvgScore: Math.round(d.total / d.count * 10) / 10,
                    Count: d.count,
                  }));

                // Month-by-campaign pivot for line chart
                const CXONE_COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#13c2c2', '#fadb14'];
                const campaignSet = new Set();
                const byMonthCampaign = {};
                scored.forEach((r) => {
                  const mk = (r.CallDate || '').substring(0, 7);
                  const ck = r.Campaign || '(Unknown)';
                  if (!mk) return;
                  campaignSet.add(ck);
                  if (!byMonthCampaign[mk]) byMonthCampaign[mk] = { month: mk };
                  if (!byMonthCampaign[mk][ck]) byMonthCampaign[mk][ck] = { total: 0, count: 0 };
                  byMonthCampaign[mk][ck].total += parseFloat(r.ScorePercent);
                  byMonthCampaign[mk][ck].count += 1;
                });
                const campaignList = [...campaignSet].sort();
                const campaignMonthTrend = Object.values(byMonthCampaign)
                  .sort((a, b) => a.month.localeCompare(b.month))
                  .map((row) => {
                    const out = { month: row.month };
                    campaignList.forEach((c) => {
                      if (row[c]) {
                        out[c] = Math.round(row[c].total / row[c].count * 10) / 10;
                        out[`${c}_count`] = row[c].count;
                      }
                    });
                    return out;
                  });


                return (
                  <Spin spinning={cxoneLoading}>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic title="Total Evals" value={totalEvals} />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic
                            title="Avg Score"
                            value={avgScore ?? '—'}
                            suffix={avgScore != null ? '%' : ''}
                            valueStyle={{ color: avgScore != null ? scoreColor(avgScore) : undefined }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {monthlyTrend.length > 0 && (
                      <Card size="small" title="Avg Score by Month (Call Date)" style={{ marginBottom: 16 }}>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={monthlyTrend} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} width={32} />
                            <Tooltip
                              formatter={(v, _, { payload: p }) => [`${v}% (${p.Count} eval${p.Count !== 1 ? 's' : ''})`, 'Avg Score']}
                            />
                            <Bar dataKey="AvgScore" maxBarSize={60} radius={[4, 4, 0, 0]}>
                              {monthlyTrend.map((entry, i) => (
                                <Cell key={i} fill={scoreColor(entry.AvgScore)} />
                              ))}
                              <LabelList dataKey="AvgScore" position="inside" formatter={(v) => `${v}%`}
                                style={{ fontSize: 11, fontWeight: 600, fill: '#fff' }} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    )}

                    {campaignList.length > 1 && campaignMonthTrend.length > 0 && (
                      <Card size="small" title="Avg Score by Month by Campaign (Call Date)" style={{ marginBottom: 16 }}>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={campaignMonthTrend} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} width={32} />
                            <Tooltip
                              formatter={(v, name, { payload: p }) => {
                                const cnt = p[`${name}_count`];
                                return [`${v}%${cnt != null ? ` (${cnt} eval${cnt !== 1 ? 's' : ''})` : ''}`, name];
                              }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {campaignList.map((c, i) => (
                              <Line
                                key={c}
                                type="monotone"
                                dataKey={c}
                                name={c}
                                stroke={CXONE_COLORS[i % CXONE_COLORS.length]}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                connectNulls
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </Card>
                    )}

                    {totalEvals === 0 && !cxoneLoading && (
                      <Typography.Text type="secondary">No CXOne evaluations found for this period.</Typography.Text>
                    )}

                    {(cxoneSectionsMerged.length > 0 || cxoneSectionsLoading) && (
                      <Card
                        size="small"
                        title="Section Performance"
                        style={{ marginTop: 20 }}
                        extra={
                          <Segmented
                            size="small"
                            value={cxoneSectionMode}
                            onChange={setCxoneSectionMode}
                            options={[
                              { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                              { label: 'By Month',       value: 'month',       icon: <CalendarOutlined /> },
                              { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                              { label: 'FP by Month',    value: 'occ_pivot',   icon: <WarningOutlined /> },
                            ]}
                          />
                        }
                      >
                        {cxoneSectionMode === 'month_pivot' ? (() => {
                          const months = [...new Set(cxoneSectionsMerged.map((r) => r.MonthKey).filter(Boolean))].sort();
                          const mLabels = {};
                          months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
                          const seen = []; const map = {};
                          cxoneSectionsMerged.forEach((r) => {
                            const k = `${r.Campaign}||${r.SectionName}`;
                            if (!map[k]) { map[k] = { _key: k, Campaign: r.Campaign, SectionName: r.SectionName }; seen.push(k); }
                            map[k][r.MonthKey] = r.SectionScorePct;
                          });
                          const pivotData = seen.map((k) => map[k]);
                          const cols = [
                            { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left' },
                            { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 200, ellipsis: true, fixed: 'left' },
                            ...months.map((mk) => ({
                              title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                              render: (_, r) => {
                                const pct = r[mk];
                                if (pct == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
                                const c = pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f';
                                return <span style={{ color: c, fontWeight: 600 }}>{pct}%</span>;
                              },
                            })),
                          ];
                          return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                            loading={cxoneSectionsLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                            locale={{ emptyText: 'No CXOne evaluations in this date range.' }} />;
                        })() : cxoneSectionMode === 'occ_pivot' ? (() => {
                          const months = [...new Set(cxoneSectionsMerged.map((r) => r.MonthKey).filter(Boolean))].sort();
                          const mLabels = {};
                          months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
                          const seen = []; const map = {};
                          cxoneSectionsMerged.forEach((r) => {
                            const k = `${r.Campaign}||${r.SectionName}`;
                            if (!map[k]) { map[k] = { _key: k, Campaign: r.Campaign, SectionName: r.SectionName }; seen.push(k); }
                            map[k][r.MonthKey] = r.FPCount ?? 0;
                          });
                          const pivotData = seen.map((k) => map[k]);
                          const cols = [
                            { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left' },
                            { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 200, ellipsis: true, fixed: 'left' },
                            ...months.map((mk) => ({
                              title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                              render: (_, r) => {
                                const v = r[mk];
                                if (v == null || v === 0) return <span style={{ color: '#d9d9d9' }}>0</span>;
                                return <Tag color="red" style={{ margin: 0 }}>{v}</Tag>;
                              },
                            })),
                          ];
                          return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                            loading={cxoneSectionsLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                            locale={{ emptyText: 'No CXOne evaluations in this date range.' }} />;
                        })() : (
                          <Table
                            dataSource={cxoneSectionsMerged}
                            rowKey={(r) => cxoneSectionMode === 'month'
                              ? `${r.SectionName}-${r.Campaign}-${r.MonthKey}`
                              : `${r.SectionName}-${r.Campaign}`}
                            size="small"
                            loading={cxoneSectionsLoading}
                            pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} sections` }}
                            locale={{ emptyText: 'No CXOne evaluations in this date range.' }}
                            columns={[
                              { title: 'Section',   dataIndex: 'SectionName',    ellipsis: true },
                              { title: 'Campaign',  dataIndex: 'Campaign',        width: 140, ellipsis: true },
                              ...(cxoneSectionMode === 'month'
                                ? [{ title: 'Call Date Month', dataIndex: 'Month', key: 'Month', width: 150 }]
                                : []),
                              { title: 'Evals',     dataIndex: 'EvalCount',       width: 70,  align: 'right' },
                              { title: 'Score',     dataIndex: 'SectionScorePct', width: 90,  align: 'right',
                                render: (v) => v != null
                                  ? <span style={{ color: scoreColor(parseFloat(v)), fontWeight: 600 }}>{v}%</span>
                                  : <Typography.Text type="secondary">—</Typography.Text> },
                              { title: <AntTooltip title="Full Point deduction — question scored 0 out of possible points">FP</AntTooltip>,
                                dataIndex: 'FPCount', width: 60, align: 'right',
                                render: (v) => v > 0 ? <Tag color="red" style={{ margin: 0 }}>{v}</Tag> : v },
                              { title: <AntTooltip title="Partial Point deduction — question scored above 0 but below maximum points">PP</AntTooltip>,
                                dataIndex: 'PPCount', width: 60, align: 'right',
                                render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : v },
                            ]}
                          />
                        )}
                      </Card>
                    )}

                    {(cxoneQuestions.length > 0 || cxoneQuestionsLoading) && (
                      <Card
                        size="small"
                        title="Question Performance"
                        style={{ marginTop: 16 }}
                        extra={
                          <Space size="small">
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              loading={cxoneQuestionsExporting}
                              disabled={!cxoneQuestions.length}
                              onClick={handleExportCxoneQuestions}
                            >
                              Export
                            </Button>
                            <Segmented
                              size="small"
                              value={cxoneQuestionMode}
                              onChange={setCxoneQuestionMode}
                              options={[
                                { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                                { label: 'By Month',       value: 'month',       icon: <CalendarOutlined /> },
                                { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                              ]}
                            />
                          </Space>
                        }
                      >
                        {cxoneQuestionMode === 'month_pivot' ? (() => {
                          const months = [...new Set(cxoneQuestions.map((r) => r.MonthKey).filter(Boolean))].sort();
                          const mLabels = {};
                          months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
                          const seen = []; const map = {};
                          const _qPtsRe = /\s*-\s*\d+\s*pts\s*$/i;
                          cxoneQuestions.forEach((r) => {
                            const baseSec = r.SectionName.replace(_qPtsRe, '').trim();
                            const k = `${r.Campaign}||${baseSec}||${r.QuestionName}`;
                            if (!map[k]) { map[k] = { _key: k, Campaign: r.Campaign, SectionName: baseSec, QuestionName: r.QuestionName }; seen.push(k); }
                            map[k][r.MonthKey] = r.QuestionScorePct;
                          });
                          const pivotData = seen.map((k) => map[k]);
                          const cols = [
                            { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left',
                              onCell: (r, i) => {
                                const prev = i > 0 ? pivotData[i - 1].Campaign : null;
                                if (r.Campaign === prev) return { rowSpan: 0 };
                                return { rowSpan: pivotData.filter((x) => x.Campaign === r.Campaign).length };
                              },
                            },
                            { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 150, ellipsis: true, fixed: 'left',
                              onCell: (r, i) => {
                                const prevKey = i > 0 ? `${pivotData[i - 1].Campaign}|${pivotData[i - 1].SectionName}` : null;
                                const currKey = `${r.Campaign}|${r.SectionName}`;
                                if (currKey === prevKey) return { rowSpan: 0 };
                                return { rowSpan: pivotData.filter((x) => x.Campaign === r.Campaign && x.SectionName === r.SectionName).length };
                              },
                            },
                            { title: 'Question', dataIndex: 'QuestionName', key: '__q', width: 220, ellipsis: true, fixed: 'left' },
                            ...months.map((mk) => ({
                              title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                              render: (_, r) => {
                                const pct = r[mk];
                                if (pct == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
                                const c = pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f';
                                return <span style={{ color: c, fontWeight: 600 }}>{pct}%</span>;
                              },
                            })),
                          ];
                          return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                            loading={cxoneQuestionsLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                            locale={{ emptyText: 'No CXOne evaluations in this date range.' }} />;
                        })() : (
                          <Table
                            dataSource={cxoneQuestions}
                            rowKey={(r) => cxoneQuestionMode === 'month'
                              ? `${r.SectionName}-${r.QuestionName}-${r.Campaign}-${r.MonthKey}`
                              : `${r.SectionName}-${r.QuestionName}-${r.Campaign}`}
                            size="small"
                            loading={cxoneQuestionsLoading}
                            pagination={{ defaultPageSize: 50, hideOnSinglePage: true, size: 'small', showTotal: (t) => `${t} questions` }}
                            locale={{ emptyText: 'No CXOne evaluations in this date range.' }}
                            columns={[
                              { title: 'Section',   dataIndex: 'SectionName',     width: 160, ellipsis: true,
                                render: (v) => v ? v.replace(/\s*-\s*\d+\s*pts\s*$/i, '').trim() : v },
                              { title: 'Question',  dataIndex: 'QuestionName',    ellipsis: true },
                              { title: 'Campaign',  dataIndex: 'Campaign',         width: 140, ellipsis: true },
                              ...(cxoneQuestionMode === 'month'
                                ? [{ title: 'Call Date Month', dataIndex: 'Month', key: 'Month', width: 150 }]
                                : []),
                              { title: 'Count',     dataIndex: 'AnswerCount',      width: 70,  align: 'right' },
                              { title: 'Score',     dataIndex: 'QuestionScorePct', width: 90,  align: 'right',
                                render: (v) => v != null
                                  ? <span style={{ color: scoreColor(parseFloat(v)), fontWeight: 600 }}>{v}%</span>
                                  : <Typography.Text type="secondary">—</Typography.Text> },
                              { title: <AntTooltip title="Full Point deduction — question scored 0 out of possible points">FP</AntTooltip>,
                                dataIndex: 'FPCount', width: 60, align: 'right',
                                render: (v) => v > 0 ? <Tag color="red" style={{ margin: 0 }}>{v}</Tag> : v },
                              { title: <AntTooltip title="Partial Point deduction — question scored above 0 but below maximum points">PP</AntTooltip>,
                                dataIndex: 'PPCount', width: 60, align: 'right',
                                render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : v },
                            ]}
                          />
                        )}
                      </Card>
                    )}
                  </Spin>
                );
              })(),
            },
            {
              key: 'errors',
              label: 'Errors',
              children: (
                <>
          <Row gutter={[8, 8]} style={{ marginBottom: 16, flexWrap: 'nowrap' }}>
            {agentRecord != null && (
              <Col flex={1}>
                <Card size="small">
                  <Statistic title="Submissions Pending" value={errorPendingCount}
                    valueStyle={{ color: errorPendingCount > 0 ? '#1677ff' : undefined }} />
                </Card>
              </Col>
            )}
            <Col flex={1}>
              <Card size="small">
                <Statistic title="Submissions In Dispute" value={errorDisputedCount}
                  valueStyle={{ color: errorDisputedCount > 0 ? '#722ed1' : undefined }} />
              </Card>
            </Col>
            <Col flex={1}>
              <Card size="small">
                <Statistic title="Submissions Completed" value={errorCompletedCount}
                  valueStyle={{ color: errorCompletedCount > 0 ? '#52c41a' : undefined }} />
              </Card>
            </Col>
            <Col flex={1}>
              <Card size="small">
                <Statistic title="Total Errors" value={totalErrorCount}
                  valueStyle={{ color: totalErrorCount > 0 ? '#ff4d4f' : undefined }} />
              </Card>
            </Col>
            <Col flex={1}>
              <Card size="small">
                <Statistic title="Total Feedbacks" value={totalFeedbackCount}
                  valueStyle={{ color: totalFeedbackCount > 0 ? '#fa8c16' : undefined }} />
              </Card>
            </Col>
          </Row>

          {(() => {
            const statsByMonthE = {};
            monthlyStats.forEach((r) => {
              if (r.Campaign === '__ALL__' || !r.MonthKey) return;
              if (!statsByMonthE[r.MonthKey]) statsByMonthE[r.MonthKey] = { errors: 0, calls: 0 };
              statsByMonthE[r.MonthKey].errors += (r.ErrorCount   || 0);
              statsByMonthE[r.MonthKey].calls  += (r.InboundCalls || 0);
            });
            const byMonth = {};
            errorHistory.forEach((r) => {
              const mk = (r.CreateDate || '').substring(0, 7);
              if (!mk) return;
              if (!byMonth[mk]) byMonth[mk] = { month: mk, label: dayjs(mk + '-01').format('MMM YY'), errors: 0 };
              byMonth[mk].errors += (r.TotalErrors || 0);
            });
            const chartData = Object.values(byMonth)
              .sort((a, b) => a.month.localeCompare(b.month))
              .map((d) => {
                const s = statsByMonthE[d.month];
                return { ...d, pct: s?.calls > 0 ? Math.round(s.errors / s.calls * 1000) / 10 : null };
              });
            if (chartData.length === 0) return null;
            return (
              <Card size="small" style={{ marginBottom: 16 }} title="Error Trend by Month">
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart data={chartData} margin={{ top: 20, right: 44, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} width={40} domain={[0, 100]} />
                    <Tooltip formatter={(v, name) => name === 'Error % of Calls' ? `${v}%` : v} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="left" dataKey="errors" name="Error Count" fill="#ff4d4f" maxBarSize={40} radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="errors" position="top" style={{ fontSize: 10, fill: '#ff4d4f', fontWeight: 600 }} formatter={(v) => v > 0 ? v : ''} />
                    </Bar>
                    <Line yAxisId="right" dataKey="pct" name="Error % of Calls" stroke="#fa8c16" strokeWidth={2} dot={{ r: 3 }}>
                      <LabelList dataKey="pct" position="top" offset={8} style={{ fontSize: 10, fill: '#fa8c16', fontWeight: 600 }} formatter={(v) => v != null && v > 0 ? `${v}%` : ''} />
                    </Line>
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            );
          })()}
          <Typography.Text strong style={{ display: 'block', marginBottom: 6, marginTop: 4 }}>List of Error Submissions</Typography.Text>
          <Table
            rowKey="ID"
            dataSource={agentRecord == null ? errorHistory.filter((r) => r.Status === 'completed' || r.Status === 'rescore_requested') : errorHistory}
            columns={errorColumns.map(toResizable)}
            size="small"
            loading={errorLoading}
            pagination={{ defaultPageSize: 10, size: 'small' }}
            style={{ marginBottom: 24 }}
            locale={{ emptyText: 'No error submissions in this date range.' }}
            components={{ header: { cell: ResizableTitle } }}
          />

          <Card
            title="Error Section Stats (Completed Errors)"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Segmented
                size="small"
                value={errorQuestionMode}
                onChange={setErrorQuestionMode}
                options={[
                  { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                  { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                ]}
              />
            }
          >
            {errorQuestionMode === 'month_pivot' ? (() => {
              const months = [...new Set(errorSecStats.map((r) => r.MonthKey))].sort();
              const mLabels = {};
              months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
              const secMap = {}; const secOrder = []; const secFirstCamp = {};
              errorSecStats.forEach((r) => {
                const k = `${r.Campaign}||${r.SectionName}`;
                if (!secMap[k]) {
                  const idx = secOrder.length;
                  secMap[k] = { _key: k, _idx: idx, Campaign: r.Campaign, SectionName: r.SectionName };
                  secOrder.push(k);
                  if (!(r.Campaign in secFirstCamp)) secFirstCamp[r.Campaign] = idx;
                }
                secMap[k][r.MonthKey] = { errors: r.TotalErrors || 0, feedbacks: r.TotalFeedbacks || 0 };
              });
              const secPivotData = secOrder
                .map((k) => secMap[k])
                .sort((a, b) => {
                  const cA = secFirstCamp[a.Campaign]; const cB = secFirstCamp[b.Campaign];
                  if (cA !== cB) return cA - cB;
                  return a._idx - b._idx;
                });
              const secCols = [
                { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left',
                  onCell: (r, i) => {
                    const prev = i > 0 ? secPivotData[i - 1].Campaign : null;
                    if (r.Campaign === prev) return { rowSpan: 0 };
                    let span = 1;
                    while (i + span < secPivotData.length && secPivotData[i + span].Campaign === r.Campaign) span++;
                    return { rowSpan: span };
                  }
                },
                { title: 'Section', dataIndex: 'SectionName', key: '__sec', ellipsis: true, fixed: 'left' },
                ...months.map((mk) => ({
                  title: mLabels[mk] || mk, key: mk, width: 120, align: 'center',
                  render: (_, r) => {
                    const cell = r[mk];
                    if (!cell || (cell.errors === 0 && cell.feedbacks === 0)) return <span style={{ color: '#d9d9d9' }}>—</span>;
                    return (
                      <div>
                        {cell.errors > 0 && <Tag color="red" style={{ margin: 0 }}>{cell.errors} {cell.errors === 1 ? 'error' : 'errors'}</Tag>}
                        {cell.feedbacks > 0 && <Tag color="orange" style={{ margin: '2px 0 0' }}>{cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}</Tag>}
                      </div>
                    );
                  },
                })),
              ];
              return <Table rowKey="_key" dataSource={secPivotData} columns={secCols} size="small"
                loading={errorSecLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                locale={{ emptyText: 'No completed error submissions in this date range.' }} />;
            })() : (
              <Table
                rowKey={(r) => `${r.Campaign}|${r.SectionName}`}
                dataSource={errorSecStats}
                loading={errorSecLoading}
                size="small"
                pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} section${t === 1 ? '' : 's'}` }}
                locale={{ emptyText: 'No completed error submissions in this date range.' }}
                columns={[
                  { title: 'Campaign',       dataIndex: 'Campaign',      key: 'Campaign',      width: 130, ellipsis: true },
                  { title: 'Section',        dataIndex: 'SectionName',   key: 'SectionName',   ellipsis: true },
                  { title: 'Total Errors',   dataIndex: 'TotalErrors',   key: 'TotalErrors',   width: 110, align: 'right',
                    render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span>,
                  },
                  { title: 'Total Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 120, align: 'right',
                    render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span>,
                  },
                ]}
              />
            )}
          </Card>

          <Card
            title="Error Question Stats (Completed Errors)"
            size="small"
            style={{ marginBottom: 32 }}
          >
            {errorQuestionMode === 'month_pivot' ? (() => {
              const filteredEQS = errorQuestionStats.filter((r) => r.TotalErrors > 0 || r.TotalFeedbacks > 0);
              const months = [...new Set(filteredEQS.map((r) => r.MonthKey))].sort();
              const mLabels = {};
              months.forEach((mk) => { mLabels[mk] = dayjs(mk + '-01').format('MMM YYYY'); });
              const qMap = {}; const qOrder = []; const qFirstCamp = {}; const qFirstSec = {};
              filteredEQS.forEach((r) => {
                const k = `${r.Campaign}||${r.SectionName}||${r.QuestionText}`;
                if (!qMap[k]) {
                  const idx = qOrder.length;
                  qMap[k] = { _key: k, _idx: idx, Campaign: r.Campaign, SectionName: r.SectionName, QuestionText: r.QuestionText };
                  qOrder.push(k);
                  if (!(r.Campaign in qFirstCamp)) qFirstCamp[r.Campaign] = idx;
                  const sk = `${r.Campaign}||${r.SectionName}`;
                  if (!(sk in qFirstSec)) qFirstSec[sk] = idx;
                }
                qMap[k][r.MonthKey] = { errors: r.TotalErrors || 0, feedbacks: r.TotalFeedbacks || 0 };
              });
              const pivotData = qOrder
                .map((k) => qMap[k])
                .sort((a, b) => {
                  const cA = qFirstCamp[a.Campaign]; const cB = qFirstCamp[b.Campaign];
                  if (cA !== cB) return cA - cB;
                  const skA = `${a.Campaign}||${a.SectionName}`; const skB = `${b.Campaign}||${b.SectionName}`;
                  const sA = qFirstSec[skA]; const sB = qFirstSec[skB];
                  if (sA !== sB) return sA - sB;
                  return a._idx - b._idx;
                });
              const cols = [
                { title: 'Campaign', dataIndex: 'Campaign', key: '__camp', width: 130, ellipsis: true, fixed: 'left',
                  onCell: (r, i) => {
                    const prev = i > 0 ? pivotData[i - 1].Campaign : null;
                    if (r.Campaign === prev) return { rowSpan: 0 };
                    let span = 1;
                    while (i + span < pivotData.length && pivotData[i + span].Campaign === r.Campaign) span++;
                    return { rowSpan: span };
                  }
                },
                { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 150, ellipsis: true, fixed: 'left',
                  onCell: (r, i) => {
                    const prevKey = i > 0 ? `${pivotData[i - 1].Campaign}|${pivotData[i - 1].SectionName}` : null;
                    const currKey = `${r.Campaign}|${r.SectionName}`;
                    if (currKey === prevKey) return { rowSpan: 0 };
                    let span = 1;
                    while (i + span < pivotData.length &&
                      `${pivotData[i + span].Campaign}|${pivotData[i + span].SectionName}` === currKey) span++;
                    return { rowSpan: span };
                  }
                },
                { title: 'Question', dataIndex: 'QuestionText', key: '__q', width: 220, ellipsis: true, fixed: 'left' },
                ...months.map((mk) => ({
                  title: mLabels[mk] || mk, key: mk, width: 120, align: 'center',
                  render: (_, r) => {
                    const cell = r[mk];
                    if (!cell || (cell.errors === 0 && cell.feedbacks === 0)) return <span style={{ color: '#d9d9d9' }}>—</span>;
                    return (
                      <div>
                        {cell.errors > 0 && <Tag color="red" style={{ margin: 0 }}>{cell.errors} {cell.errors === 1 ? 'error' : 'errors'}</Tag>}
                        {cell.feedbacks > 0 && <Tag color="orange" style={{ margin: '2px 0 0' }}>{cell.feedbacks} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}</Tag>}
                      </div>
                    );
                  },
                })),
              ];
              return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small"
                loading={errorQsLoading} pagination={false} scroll={{ x: 'max-content' }} bordered
                locale={{ emptyText: 'No completed error submissions in this date range.' }} />;
            })() : (
              <Table
                rowKey={(r) => `${r.Campaign}|${r.SectionName}|${r.QuestionText}`}
                dataSource={errorQuestionStats.filter((r) => r.TotalErrors > 0 || r.TotalFeedbacks > 0)}
                loading={errorQsLoading}
                size="small"
                pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} question${t === 1 ? '' : 's'}` }}
                locale={{ emptyText: 'No completed error submissions in this date range.' }}
                columns={[
                  { title: 'Campaign',        dataIndex: 'Campaign',       key: 'Campaign',       width: 130, ellipsis: true },
                  { title: 'Section',         dataIndex: 'SectionName',    key: 'SectionName',    width: 220, ellipsis: true },
                  { title: 'Question',        dataIndex: 'QuestionText',   key: 'QuestionText',   ellipsis: true },
                  { title: '# Submissions',   dataIndex: 'SubmissionCount',key: 'SubmissionCount',width: 120, align: 'right' },
                  { title: 'Total Errors',    dataIndex: 'TotalErrors',    key: 'TotalErrors',    width: 110, align: 'right',
                    render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span>,
                  },
                  { title: 'Total Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 120, align: 'right',
                    render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#d9d9d9' }}>—</span>,
                  },
                ]}
              />
            )}
          </Card>
                </>
              ),
            },
            // ── Compliance ───────────────────────────────────────────────────
            {
              key: 'compliance',
              label: 'Compliance',
              children: (() => {
                const completedIncidents = complianceRows.filter(
                  (r) => r.Type === 'incident' && ['complete', 'inactive'].includes((r.Status || '').toLowerCase())
                );
                const qtyIncidents    = completedIncidents.length;
                const qtyIssues       = completedIncidents.reduce((sum, r) => sum + (r.Issues?.length ?? 0), 0);
                const estEffortHrsCI  = completedIncidents.reduce((sum, r) => sum + (parseFloat(r.TotalEffortHrs) || 0), 0);
                const complianceColumns = [
                      {
                        title: 'Type', dataIndex: 'Type', key: 'Type', width: 140,
                        render: (v) => (
                          <Tag color={v === 'writeup' ? 'purple' : 'blue'}>
                            {v === 'writeup' ? 'Formal Writeup' : 'Incident'}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Date of Incident', dataIndex: 'DateOfIncident', key: 'DateOfIncident', width: 130,
                        defaultSortOrder: 'descend', sorter: (a, b) => (a.DateOfIncident || '').localeCompare(b.DateOfIncident || ''),
                        render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
                      },
                      {
                        title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
                        render: (v) => (
                          <Tag color={{ draft: 'default', in_progress: 'blue', complete: 'green', inactive: 'gold', canceled: 'red' }[v] ?? 'default'}>
                            {v?.replace('_', ' ')}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Warning', dataIndex: 'WarningNumber', key: 'WarningNumber', width: 90,
                        render: (v) => v
                          ? <Tag color={{ '1st': 'blue', '2nd': 'orange', '3rd': 'volcano', 'Final': 'red' }[v]}>{v}</Tag>
                          : <Typography.Text type="secondary">—</Typography.Text>,
                      },
                      {
                        title: 'Warning Expires On', dataIndex: 'WarningExpiresOn', key: 'WarningExpiresOn', width: 170,
                        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                        render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
                      },
                      {
                        title: 'QA Related', dataIndex: 'IsQARelated', key: 'IsQARelated', width: 110,
                        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                        render: (v) => v ? 'Yes' : 'No',
                      },
                      {
                        title: 'QA Related Expires On', dataIndex: 'QARelatedExpiresOn', key: 'QARelatedExpiresOn', width: 190,
                        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                        render: (v, r) => (r.IsQARelated && v) || <Typography.Text type="secondary">—</Typography.Text>,
                      },
                      {
                        title: 'Issues', key: 'Issues', ellipsis: true,
                        render: (_, r) => (r.Issues || []).length
                          ? (r.Issues || []).map((iss) => <Tag key={iss} style={{ marginBottom: 2 }}>{iss}</Tag>)
                          : <Typography.Text type="secondary">—</Typography.Text>,
                      },
                      {
                        title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 160, ellipsis: true,
                        render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
                      },
                      {
                        // Flagged only while it is still owed. Once acknowledged the
                        // deadline is history, so it reads plainly beside the date met.
                        title: 'Acknowledge Deadline', dataIndex: 'AcknowledgeDueDate', key: 'AcknowledgeDueDate', width: 160,
                        render: (v, r) => {
                          if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
                          if (r.AcknowledgedAt) return v;
                          const over = dayjs().isAfter(dayjs(v), 'day');
                          return (
                            <span style={{ color: over ? '#ff4d4f' : '#fa8c16', fontWeight: over ? 600 : undefined }}>
                              {v}{over ? ' (overdue)' : ''}
                            </span>
                          );
                        },
                      },
                      {
                        title: 'Acknowledged', dataIndex: 'AcknowledgedAt', key: 'AcknowledgedAt', width: 175,
                        render: (v) => v ? formatTS(v) : <Typography.Text type="secondary">—</Typography.Text>,
                      },
                      { title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175, render: (v) => formatTS(v) },
                ];
                return (
                <>
                  <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={8} md={6}>
                      <Card size="small">
                        <Statistic title="Qty Incidents" value={qtyIncidents}
                          valueStyle={{ color: qtyIncidents > 0 ? '#cf1322' : undefined }} />
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                          Completed incidents in the selected date range.
                        </Typography.Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={8} md={6}>
                      <Card size="small">
                        <Statistic title="Qty Incident Issues" value={qtyIssues}
                          valueStyle={{ color: qtyIssues > 0 ? '#fa8c16' : undefined }} />
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                          Total issues assigned across completed incidents.
                        </Typography.Text>
                      </Card>
                    </Col>
                    <Col xs={24} sm={8} md={6}>
                      <Card size="small">
                        <Statistic title="Active Warnings" value={activeWarningRows.length}
                          valueStyle={{ color: activeWarningRows.length > 0 ? '#cf1322' : undefined }} />
                      </Card>
                    </Col>
                  </Row>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 6, marginTop: 4 }}>Active Warnings</Typography.Text>
                  <Table
                    rowKey="ID"
                    dataSource={activeWarningRows}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    style={{ marginBottom: 24 }}
                    pagination={{ defaultPageSize: 10, size: 'small', hideOnSinglePage: true }}
                    locale={{ emptyText: 'No active warnings.' }}
                    onRow={(r) => ({
                      onClick: () => navigate(`/compliance/${r.Type === 'writeup' ? 'writeups' : 'incidents'}/${r.ID}`),
                      style: { cursor: 'pointer' },
                    })}
                    columns={complianceColumns}
                  />
                  <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                    Incidents and formal writeups with incident date from {dayjs(start).format('MMM D, YYYY')} to {dayjs(end).format('MMM D, YYYY')}.
                  </Typography.Text>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 6, marginTop: 4 }}>List of Compliance Incidents</Typography.Text>
                  <Table
                    rowKey="ID"
                    dataSource={agentRecord == null ? complianceRows.filter((r) => ['complete', 'inactive'].includes(r.Status)) : complianceRows}
                    loading={complianceLoading}
                    size="small"
                    pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
                    locale={{ emptyText: 'No compliance records in this date range.' }}
                    onRow={(r) => ({
                      onClick: () => navigate(`/compliance/${r.Type === 'writeup' ? 'writeups' : 'incidents'}/${r.ID}`),
                      style: { cursor: 'pointer' },
                    })}
                    columns={complianceColumns}
                  />
                  {agentRecord != null && (
                    <>
                      <Divider orientation="left" style={{ fontSize: 13, marginTop: 24 }}>Open AI Notifications</Divider>
                      <Table
                        rowKey="ID"
                        dataSource={notifRows}
                        loading={notifLoading}
                        size="small"
                        pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
                        locale={{ emptyText: 'No open or in-progress AI notifications.' }}
                        onRow={(r) => ({
                          onClick: () => navigate(`/notifications/${r.ID}`),
                          style: { cursor: 'pointer' },
                        })}
                        columns={[
                          {
                            title: 'Severity', dataIndex: 'Severity', key: 'Severity', width: 90,
                            render: (v) => v ? <Tag color={{ low: 'blue', medium: 'orange', high: 'red' }[v]}>{v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
                          },
                          { title: 'Type', dataIndex: 'Type', key: 'Type', width: 180, ellipsis: true, render: (v) => <Typography.Text strong>{v}</Typography.Text> },
                          { title: 'Summary', dataIndex: 'Summary', key: 'Summary', ellipsis: true },
                          { title: 'Campaign', dataIndex: 'Campaign_Name', key: 'Campaign_Name', width: 120, ellipsis: true },
                          {
                            title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
                            render: (v) => <Tag color={{ Open: 'blue', In_Progress: 'orange' }[v] ?? 'default'}>{{ Open: 'Open', In_Progress: 'In Progress' }[v] ?? v}</Tag>,
                          },
                          {
                            title: 'Assigned', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 140, ellipsis: true,
                            render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
                          },
                          { title: 'Created', dataIndex: 'CreateDate', key: 'CreateDate', width: 100,
                            defaultSortOrder: 'descend', sorter: (a, b) => (a.CreateDate || '').localeCompare(b.CreateDate || '') },
                        ]}
                      />
                    </>
                  )}
                </>
                );
              })(),
            },

            // ── Coaching ─────────────────────────────────────────────────────
            {
              key: 'coaching',
              label: 'Coaching',
              children: coachingLoading ? (
                <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
              ) : (() => {
                const activeStatuses = ['active', 'completed'];
                const activeSessions = coachingSessions.filter((r) =>
                  activeStatuses.includes((r.Status || '').toLowerCase())
                );
                const qtyActiveSessions = activeSessions.length;
                const recoachQty = activeSessions.filter((r) => r.IsRecoaching === 1).length;
                const estEffortHrs = activeSessions.reduce(
                  (sum, r) => sum + (parseFloat(r.RequestTypeEffortHrs) || 0), 0
                );
                return (
                  <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={24} sm={8} md={6}>
                        <Card size="small">
                          <Statistic title="Qty Sessions" value={qtyActiveSessions}
                            valueStyle={{ color: qtyActiveSessions > 0 ? '#1677ff' : undefined }} />
                          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                            Active or Completed sessions (all request types).
                          </Typography.Text>
                        </Card>
                      </Col>
                      <Col xs={24} sm={8} md={6}>
                        <Card size="small">
                          <Statistic title="Qty Recoaching Sessions" value={recoachQty}
                            valueStyle={{ color: recoachQty > 0 ? '#cf1322' : undefined }} />
                          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                            Active or Completed recoaching sessions.
                          </Typography.Text>
                        </Card>
                      </Col>
                    </Row>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 6, marginTop: 4 }}>List of Coaching Sessions</Typography.Text>
                    <Table
                      rowKey="ID"
                      dataSource={agentRecord == null ? coachingSessions.filter((r) => ['active', 'completed'].includes(r.Status)) : coachingSessions}
                      size="small"
                      pagination={false}
                      locale={{ emptyText: 'No coaching sessions in range.' }}
                  columns={[
                    {
                      title: 'Participant Status', dataIndex: 'MyStatus', key: 'MyStatus', width: 150,
                      render: (v) => {
                        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
                        const color = { pending: 'orange', completed: 'green', canceled: 'red' }[v] ?? 'default';
                        return <Tag color={color}>{v}</Tag>;
                      },
                    },
                    {
                      title: 'Session Status', dataIndex: 'Status', key: 'Status', width: 120,
                      render: (v) => {
                        const color = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' }[v] ?? 'default';
                        return <Tag color={color}>{v}</Tag>;
                      },
                    },
                    { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 140, ellipsis: true,
                      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                    { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', width: 140, ellipsis: true,
                      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                    { title: 'Request Type', dataIndex: 'RequestTypeName', key: 'RequestTypeName', ellipsis: true,
                      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                    { title: 'Due Date', dataIndex: 'DueDate', key: 'DueDate', width: 110,
                      defaultSortOrder: 'descend', sorter: (a, b) => (a.DueDate || '').localeCompare(b.DueDate || ''),
                      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                    { title: 'Area', dataIndex: 'AreaName', key: 'AreaName', ellipsis: true,
                      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                    {
                      title: '', key: 'view', width: 70,
                      render: (_, r) => (
                        <Link to={`/coaching/sessions/${r.ID}`}><Button size="small">View</Button></Link>
                      ),
                    },
                  ]}
                />
                  </>
                );
              })(),
            },
            // ── HelpDesk ──────────────
            {
              key: 'helpdesk',
              label: 'HelpDesk',
              // The employee's own issues: Impacted Employee only, not watching.
              children: <HelpdeskInvolvedTickets userId={agentUserId} involvedAs="impacted" />,
            },
            {
              key: 'absences',
              label: 'Absence Notifications',
              children: (() => {
                const ABSENCE_STATUS_COLOR = {
                  Submitted: 'blue', Acknowledged: 'cyan', Canceled: 'default',
                };
                const activeRows = absenceRows.filter((r) => r.Status !== 'Canceled');

                const absenceDays = (r) => {
                  if (r.AbsencePeriod === 'Multiple Consecutive Days' && r.DateEnd) {
                    return Math.max(dayjs(r.DateEnd).diff(dayjs(r.DateStart), 'day') + 1, 1);
                  }
                  return 1;
                };
                const parseMinutes = (t) => {
                  if (!t) return null;
                  const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                  if (!m) return null;
                  let h = parseInt(m[1], 10);
                  const min = parseInt(m[2], 10);
                  if (m[3].toUpperCase() === 'PM' && h !== 12) h += 12;
                  if (m[3].toUpperCase() === 'AM' && h === 12) h = 0;
                  return h * 60 + min;
                };
                const absenceHrs = (r) => {
                  if (r.StartTime && r.EndTime) {
                    const sm = parseMinutes(r.StartTime);
                    const em = parseMinutes(r.EndTime);
                    if (sm !== null && em !== null) {
                      const diff = em < sm ? (1440 - sm + em) : (em - sm);
                      return diff / 60;
                    }
                  }
                  if (r.AbsencePeriod === 'Absent Part of Day') return 4;
                  return absenceDays(r) * 8;
                };

                const qtyFull    = activeRows.filter((r) => r.AbsencePeriod !== 'Absent Part of Day').length;
                const qtyPartial = activeRows.filter((r) => r.AbsencePeriod === 'Absent Part of Day').length;
                const qtyDays    = activeRows.reduce((sum, r) => sum + absenceDays(r), 0);
                const estHrs     = activeRows.reduce((sum, r) => sum + absenceHrs(r), 0);

                return (
                  <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic title="Qty Full Day Absences" value={qtyFull}
                            valueStyle={{ color: qtyFull > 0 ? '#cf1322' : undefined }} />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic title="Qty Partial Day Absences" value={qtyPartial}
                            valueStyle={{ color: qtyPartial > 0 ? '#fa8c16' : undefined }} />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic title="Days Absent from Absence Notifications" value={qtyDays}
                            valueStyle={{ color: qtyDays > 0 ? '#cf1322' : undefined }} />
                        </Card>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Card size="small">
                          <Statistic title="Est. Hours Absent" value={Number(estHrs.toFixed(1))}
                            suffix="hrs"
                            valueStyle={{ color: estHrs > 0 ? '#fa8c16' : undefined }} />
                        </Card>
                      </Col>
                    </Row>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                      Absence notifications with start date from {dayjs(start).format('MMM D, YYYY')} to {dayjs(end).format('MMM D, YYYY')}.
                      KPIs include Submitted and Acknowledged records — Canceled are excluded.
                    </Typography.Text>
                    <Table
                      rowKey="ID"
                      dataSource={absenceRows}
                      loading={absenceLoading}
                      size="small"
                      pagination={{ defaultPageSize: 20, size: 'small', showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
                      locale={{ emptyText: 'No absence notifications in this date range.' }}
                      onRow={(r) => ({
                        onClick: () => navigate(`/absences/${r.ID}`),
                        style: { cursor: 'pointer' },
                      })}
                      columns={[
                        {
                          title: 'Status', dataIndex: 'Status', key: 'Status', width: 120,
                          render: (v) => <Tag color={ABSENCE_STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
                        },
                        { title: 'Reason', dataIndex: 'Reason', key: 'Reason', width: 200 },
                        {
                          title: 'Period', dataIndex: 'AbsencePeriod', key: 'AbsencePeriod', width: 130,
                          render: (v) => v === 'Absent Part of Day' ? 'Partial Day' : v === 'Multiple Consecutive Days' ? 'Multiple Days' : 'Entire Day',
                        },
                        { title: 'Date Start', dataIndex: 'DateStart', key: 'DateStart', width: 110,
                          defaultSortOrder: 'descend', sorter: (a, b) => (a.DateStart || '').localeCompare(b.DateStart || '') },
                        {
                          title: 'Date End', dataIndex: 'DateEnd', key: 'DateEnd', width: 110,
                          render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
                        },
                        { title: 'Start Time', dataIndex: 'StartTime', key: 'StartTime', width: 100,
                          render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                        { title: 'End Time', dataIndex: 'EndTime', key: 'EndTime', width: 100,
                          render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                        {
                          title: 'Est. Hrs', key: 'Hrs', width: 80, align: 'right',
                          render: (_, r) => absenceHrs(r).toFixed(1),
                        },
                        { title: 'Submitted', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175, render: (v) => formatTS(v) },
                      ]}
                    />
                  </>
                );
              })(),
            },

            // ── Timecard ─────────────────────────────────────────────────────
            {
              key: 'timecard',
              label: 'Adherence',
              children: timecardLoading ? (
                <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
              ) : !timecard || timecard.summary === null ? (
                <div style={{ padding: 32, color: '#888', textAlign: 'center' }}>
                  No timecard data available — Payroll ID may not be linked to this account.
                </div>
              ) : (() => {
                const s = timecard.summary;
                const fmtHrs = (v) => v != null && v > 0 ? Number(v).toFixed(2) : '—';
                const EARNING_LABEL = {
                  Reg: 'Regular', OT: 'Overtime', PTO: 'PTO', Sick: 'Sick',
                  Hol: 'Holiday', 'Hol1.5': 'Holiday Worked', Jury: 'Jury Duty', Brv: 'Bereavement',
                };
                const EARNING_TAG_COLOR = {
                  Reg: 'green', OT: 'orange', PTO: 'blue', Sick: 'purple',
                  Hol: 'cyan', 'Hol1.5': 'gold', Jury: 'default', Brv: 'default',
                };
                const EARNING_BAR_COLOR = {
                  Reg: '#52c41a', OT: '#fa8c16', PTO: '#1677ff', Sick: '#722ed1',
                  Hol: '#13c2c2', 'Hol1.5': '#faad14', Jury: '#8c8c8c', Brv: '#ff4d4f',
                };
                const CODE_ORDER = ['Reg', 'OT', 'PTO', 'Sick', 'Hol', 'Hol1.5', 'Jury', 'Brv'];

                // Monthly pivot for bar chart
                const activeCodes = CODE_ORDER.filter((c) =>
                  (timecard.monthly || []).some((r) => r.EarningCode === c)
                );
                const monthKeys = [...new Set((timecard.monthly || []).map((r) => r.MonthKey))].sort();
                const monthlyChart = monthKeys.map((mk) => {
                  const entry = { Month: dayjs(mk + '-01').format('MMM YYYY') };
                  activeCodes.forEach((code) => {
                    const row = (timecard.monthly || []).find((r) => r.MonthKey === mk && r.EarningCode === code);
                    entry[code] = row ? Number(row.Hours) : 0;
                  });
                  return entry;
                });

                // Monthly summary table: Worked / PTO / Other / Scheduled / Adherence
                const monthlyByCode = {};
                (timecard.monthly || []).forEach(({ MonthKey, EarningCode, Hours }) => {
                  if (!monthlyByCode[MonthKey]) monthlyByCode[MonthKey] = {};
                  monthlyByCode[MonthKey][EarningCode] = Number(Hours);
                });
                const monthlySchedTotals = {};
                (timecard.schedule || []).forEach(({ Date: d, TotalHrs }) => {
                  const mk = d.substring(0, 7);
                  monthlySchedTotals[mk] = (monthlySchedTotals[mk] || 0) + Number(TotalHrs || 0);
                });
                const allMonthKeys = [...new Set([
                  ...Object.keys(monthlyByCode),
                  ...Object.keys(monthlySchedTotals),
                ])].sort();
                const monthSummaryRows = allMonthKeys.map((mk) => {
                  const codes    = monthlyByCode[mk] || {};
                  const worked   = (codes['Reg'] || 0) + (codes['OT'] || 0) + (codes['Hol1.5'] || 0);
                  const pto      = (codes['PTO'] || 0) + (codes['Sick'] || 0);
                  const other    = Object.entries(codes)
                    .filter(([c]) => !['Reg', 'OT', 'Hol1.5', 'PTO', 'Sick'].includes(c))
                    .reduce((sum, [, v]) => sum + v, 0);
                  const sched    = monthlySchedTotals[mk] || 0;
                  const adh      = sched > 0 ? (worked / sched) * 100 : null;
                  const unworked = sched > 0 ? sched - worked : null;
                  return { key: mk, Month: dayjs(mk + '-01').format('MMM YYYY'), worked, pto, other, sched, adh, unworked };
                });

                return (
                  <>
                    {/* ── Monthly summary table ────────────────────────
                        Hidden on My Summary, shown on the admin agent view. */}
                    {!hideAdherenceMonthlyTables && monthSummaryRows.length > 0 && (
                      <Card title="Monthly Summary" size="small" style={{ marginBottom: 16 }}>
                        <Table
                          rowKey="key"
                          dataSource={monthSummaryRows}
                          size="small"
                          pagination={false}
                          columns={[
                            { title: 'Month',      dataIndex: 'Month',  key: 'Month',  width: 120 },
                            { title: 'Worked',     dataIndex: 'worked', key: 'worked', width: 90, align: 'right',
                              render: (v) => fmtHrs(v) },
                            { title: 'PTO',        dataIndex: 'pto',    key: 'pto',    width: 80, align: 'right',
                              render: (v) => fmtHrs(v) },
                            { title: 'Other',      dataIndex: 'other',  key: 'other',  width: 80, align: 'right',
                              render: (v) => fmtHrs(v) },
                            { title: 'Scheduled',  dataIndex: 'sched',  key: 'sched',  width: 100, align: 'right',
                              render: (v) => fmtHrs(v) },
                            { title: 'Adherence',        dataIndex: 'adh',      key: 'adh',      width: 100, align: 'right',
                              render: (v) => v != null ? `${v.toFixed(1)}%` : '—' },
                            { title: 'Not Worked',       dataIndex: 'unworked', key: 'unworked', width: 100, align: 'right',
                              render: (v) => v != null ? Number(v).toFixed(2) : '—' },
                          ]}
                        />
                      </Card>
                    )}

                    {!hideAdherenceMonthlyTables && monthlyChart.length > 0 && (
                      <Card title="Hours by Month (Detail)" size="small" style={{ marginBottom: 16 }}>
                        <Table
                          rowKey="Month"
                          dataSource={monthlyChart}
                          size="small"
                          pagination={false}
                          columns={[
                            { title: 'Month', dataIndex: 'Month', key: 'Month', width: 120 },
                            ...activeCodes.map((code) => ({
                              title: EARNING_LABEL[code] ?? code,
                              dataIndex: code,
                              key: code,
                              width: 110,
                              align: 'right',
                              render: (v) => fmtHrs(v),
                            })),
                          ]}
                        />
                      </Card>
                    )}

                    {/* ── Unified daily detail ───────────────────────── */}
                    {(() => {
                      const tcByDate      = {};
                      const carryInByDate = {};
                      (timecard.rows || []).forEach((r) => {
                        const hrs     = Number(r.Hours || 0);
                        const isCarry = r.OrigEntry === 0;
                        if (!tcByDate[r.Date]) tcByDate[r.Date] = { worked: 0, pto: 0, other: 0, punches: [] };
                        const e = tcByDate[r.Date];
                        if (['Reg', 'OT', 'Hol1.5'].includes(r.EarningCode)) e.worked += hrs;
                        else if (['PTO', 'Sick'].includes(r.EarningCode))     e.pto    += hrs;
                        else                                                   e.other  += hrs;
                        if (r.PunchIn || r.PunchOut) {
                          e.punches.push({ in: r.PunchIn?.substring(0, 5) ?? null, out: r.PunchOut?.substring(0, 5) ?? null, code: r.EarningCode });
                        }
                        if (isCarry) {
                          if (!carryInByDate[r.Date]) carryInByDate[r.Date] = { worked: 0, pto: 0, other: 0, punches: [] };
                          const ci = carryInByDate[r.Date];
                          if (['Reg', 'OT', 'Hol1.5'].includes(r.EarningCode)) ci.worked += hrs;
                          else if (['PTO', 'Sick'].includes(r.EarningCode))     ci.pto    += hrs;
                          else                                                   ci.other  += hrs;
                          if (r.PunchIn || r.PunchOut) {
                            ci.punches.push({ in: r.PunchIn?.substring(0, 5) ?? null, out: r.PunchOut?.substring(0, 5) ?? null, code: r.EarningCode });
                          }
                        }
                      });
                      const schedByDate = {};
                      (timecard.schedule || []).forEach((r) => {
                        if (!schedByDate[r.Date]) schedByDate[r.Date] = { shifts: [], schedHrs: 0, startTime: null, endTime: null, hasOrigEntry: false, hasCarry: false };
                        const sd = schedByDate[r.Date];
                        sd.schedHrs += Number(r.TotalHrs || 0);
                        if (r.OrigEntry === 1) {
                          sd.shifts.push(r.ShiftName);
                          sd.startTime    = r.StartTime;
                          sd.hasOrigEntry = true;
                          if (!sd.hasCarry) sd.endTime = r.EndTime;
                        } else {
                          sd.hasCarry = true;
                          sd.endTime  = r.EndTime;
                        }
                      });
                      const cxByDate = {};
                      (timecard.cxone_daily || []).forEach((r) => {
                        cxByDate[r.Date] = {
                          loginSessions: r.LoginSessions || [],
                          totalICMins:   r.TotalICMins ?? null,
                        };
                      });
                      const allDates = [...new Set([
                        ...Object.keys(tcByDate),
                        ...Object.keys(schedByDate),
                        ...Object.keys(cxByDate),
                      ])].sort((a, b) => b.localeCompare(a));
                      const _tm = s => { if (!s) return null; const p = String(s).split(':'); return (+p[0])*60+(+p[1]); };
                      const dailyRows = allDates.map((date) => {
                        const tc = tcByDate[date]   || {};
                        const sc = schedByDate[date] || {};
                        const cx = cxByDate[date]    || {};

                        // Midnight carry-over schedule rows (OrigEntry=0, NewStartTime='00:00:01') have no
                        // IC login event because the agent was already logged in from the prior day's shift.
                        // If the prior day had a session that crossed midnight, synthesize 00:00:01 for
                        // IC Login and IC Available so the row doesn't show blank.
                        let loginSessions = cx.loginSessions || [];
                        if (loginSessions.length === 0 && sc.startTime === '00:00:01') {
                          const prevDate = dayjs(date).subtract(1, 'day').format('YYYY-MM-DD');
                          const prevSessions = cxByDate[prevDate]?.loginSessions || [];
                          if (prevSessions.some(s => s.logout_next_day)) {
                            loginSessions = [{ login: '00:00:01', available: '00:00:01', logout: null, logout_next_day: false }];
                          }
                        }

                        return {
                          key:            date,
                          Date:           date,
                          Shift:          sc.shifts?.join(', ') || null,
                          SchedStart:      sc.startTime  || null,
                          SchedEnd:        sc.endTime    || null,
                          _schedEndNextDay: sc.hasCarry  || false,
                          SchedHrs:        sc.schedHrs   || null,
                          Worked:         tc.worked  || 0,
                          PTO:            tc.pto     || 0,
                          Other:          tc.other   || 0,
                          LoginSessions:  loginSessions,
                          TotalICMins:    cx.totalICMins ?? null,
                          Punches:        (tc.punches || []).sort((a, b) => ((a._nextDay ? 1440 : 0) + _tm(a.in || '00:00')) - ((b._nextDay ? 1440 : 0) + _tm(b.in || '00:00'))),
                        };
                      });
                      // Build merged rows: midnight carry-over rows (SchedStart='00:00:01') are
                      // folded into the prior day's row so a shift that crosses midnight shows
                      // as one line instead of two.
                      // Continuation dates: days that have only carry-over schedule entries
                      // (OrigEntry=0) with no original shift of their own. These get merged
                      // into the prior day's row.
                      const _rowsByDate  = Object.fromEntries(dailyRows.map(r => [r.Date, r]));
                      const _contDates   = new Set(
                        Object.entries(schedByDate)
                          .filter(([, sd]) => !sd.hasOrigEntry)
                          .map(([d]) => d)
                      );
                      // Also treat dates with no schedule at all but whose prior day had a
                      // session that crossed midnight (pure punch carry-over days).
                      dailyRows.forEach(r => {
                        if (schedByDate[r.Date]) return;
                        const prevDate    = dayjs(r.Date).subtract(1, 'day').format('YYYY-MM-DD');
                        const prevSessions = cxByDate[prevDate]?.loginSessions || [];
                        if (prevSessions.some(s => s.logout_next_day)) _contDates.add(r.Date);
                      });

                      const mergedRows = dailyRows
                        .filter(r => !_contDates.has(r.Date))
                        .map(r => {
                          const nextDate  = dayjs(r.Date).add(1, 'day').format('YYYY-MM-DD');
                          const co        = _contDates.has(nextDate) ? _rowsByDate[nextDate] : null;
                          // Carry-in punches on r.Date belong to the PRIOR day's shift — exclude them.
                          const myCarry   = carryInByDate[r.Date]   || { worked: 0, pto: 0, other: 0, punches: [] };
                          // Carry-in punches on nextDate belong to THIS shift — always include them.
                          const nxtCarry  = carryInByDate[nextDate] || { worked: 0, pto: 0, other: 0, punches: [] };
                          const noChange  = !co && myCarry.worked === 0 && nxtCarry.worked === 0;
                          if (noChange) return r;
                          const ownWorked  = r.Worked - myCarry.worked;
                          const ownPTO     = r.PTO    - myCarry.pto;
                          const ownOther   = r.Other  - myCarry.other;
                          const ownPunches = (r.Punches || []).filter(
                            p => !myCarry.punches.some(cp => cp.in === p.in && cp.out === p.out && cp.code === p.code)
                          );
                          // If a continuation date exists, use its totals (includes nxtCarry + any own-shift punches).
                          // Otherwise, add nxtCarry directly from carryInByDate.
                          const addWorked  = co ? co.Worked : nxtCarry.worked;
                          const addPTO     = co ? co.PTO    : nxtCarry.pto;
                          const addOther   = co ? co.Other  : nxtCarry.other;
                          const addPunches = (co ? (co.Punches || []) : nxtCarry.punches)
                                              .map(p => ({ ...p, _nextDay: true }));
                          return {
                            ...r,
                            ...(co?.SchedEnd ? { SchedEnd: co.SchedEnd, _schedEndNextDay: true } : {}),
                            SchedHrs: (r.SchedHrs || 0) + (co?.SchedHrs || 0),
                            Worked:   ownWorked + addWorked,
                            PTO:      ownPTO    + addPTO,
                            Other:    ownOther  + addOther,
                            Punches:  [...ownPunches, ...addPunches]
                                        .sort((a, b) => ((a._nextDay ? 1440 : 0) + _tm(a.in || '00:00')) - ((b._nextDay ? 1440 : 0) + _tm(b.in || '00:00'))),
                          };
                        });

                      return (
                        <>
                        <Card
                          size="small"
                          title={
                            <span>
                              Daily Detail
                              <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 6 }}>
                                — note there can be a 24 hr delay in showing recent data. Punch errors until corrected will skew the info shown
                              </Typography.Text>
                            </span>
                          }
                        >
                          <Table
                            rowKey="key"
                            dataSource={mergedRows}
                            size="small"
                            pagination={{ defaultPageSize: 31, size: 'small' }}
                            locale={{ emptyText: 'No entries in this date range.' }}
                            scroll={{ x: 'max-content' }}
                            columns={(() => {
                              const _atid = agentRecord?.TeamID ?? null;
                              const _sg = {}, _st = {};
                              dbalThresholds.forEach(t => {
                                if (t.TeamID == null) _sg[t.Metric] = t;
                                else if (t.TeamID === _atid) _st[t.Metric] = t;
                              });
                              const _thresh = { ..._sg, ..._st };
                              const _wc = new Set(['Reg', 'OT', 'Hol1.5']);
                              const _icCols = new Set(['LoginTimes', 'FirstAvailable', 'LogoutTimes', 'WorkedICGap']);
                              const _canInc = canCreateIncident && (currentUser?.CanEditIncidents ?? 0) >= 2;
                              const _ip = (tag, date, metric, value) => {
                                if (!_canInc) return tag;
                                const fmtDate = dayjs(date).format('M/D/YY (ddd)');
                                const desc = value != null
                                  ? `Punch: ${metric} - ${value}m on ${fmtDate}`
                                  : `Punch: ${metric} on ${fmtDate}`;
                                const content = <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                                  onClick={() => openComplianceModal(date, metric, value, null, desc)}>Create Compliance Incident</Button>;
                                return <Popover content={content} trigger="hover" mouseLeaveDelay={0.3}>{tag}</Popover>;
                              };
                              return [
                              {
                                // An absence notification covering the day shows under the date.
                                title: 'Date', dataIndex: 'Date', key: 'Date', width: 110, fixed: 'left',
                                render: (v) => {
                                  const abs = absenceRows.filter((a) => a.Status !== 'Canceled'
                                    && String(a.DateStart).slice(0, 10) <= v
                                    && String(a.DateEnd || a.DateStart).slice(0, 10) >= v);
                                  return (
                                    <div>
                                      <div>{v}</div>
                                      <AbsenceTag absences={abs} navigate={navigate} canOpen={(currentUser?.CanEditAbsences ?? 0) >= 1 || agentUserId === currentUser?.ID} />
                                    </div>
                                  );
                                },
                              },
                              { title: 'Shift',       dataIndex: 'Shift',     key: 'Shift',     width: 170, render: (v) => v || '—' },
                              { title: 'Sched Start', dataIndex: 'SchedStart', key: 'SchedStart', width: 115, render: v => fmtClock(v) },
                              { title: 'Sched End',   dataIndex: 'SchedEnd',   key: 'SchedEnd',   width: 105,
                                render: (v, r) => v
                                  ? <>{fmtClock(v)}{r._schedEndNextDay && <span style={{ color: '#fa8c16', fontSize: 11 }}> (+1)</span>}</>
                                  : '—' },
                              { title: 'IC Login', dataIndex: 'LoginSessions', key: 'LoginTimes', width: 110,
                                render: (sessions) => sessions?.length ? sessions.map((s, i) => <div key={i}>{fmtClock(s.login)}</div>) : '—' },
                              { title: 'IC Available', dataIndex: 'LoginSessions', key: 'FirstAvailable', width: 155,
                                render: (sessions, r) => {
                                  const hasPunch = r.Punches?.some(p => _wc.has(p.code));
                                  if (hasPunch && !sessions?.length) return _ip(<Tag color="red" style={{ margin: 0 }}>No IC Activity</Tag>, r.Date, 'IC Login to IC Available', null);
                                  if (!sessions?.length) return '—';
                                  const maxM = _thresh['IC Login to IC Available']?.MaxValue ?? 5;
                                  return sessions.map((s, i) => {
                                    const f = s.available ? fmtClock(s.available) : null;
                                    const d = s.available && s.login ? _tm(s.available) - _tm(s.login) : null;
                                    if (!f) return <div key={i}>{_ip(<Tag color="red" style={{ margin: 0 }}>No Avail</Tag>, r.Date, 'IC Login to IC Available', null)}</div>;
                                    if (d != null && d > maxM) return <div key={i}>{_ip(<Tag color="red" style={{ margin: 0 }}>{f} (+{d}m)</Tag>, r.Date, 'IC Login to IC Available', d)}</div>;
                                    return <div key={i}>{f}</div>;
                                  });
                                } },
                              { title: 'IC Logout', dataIndex: 'LoginSessions', key: 'LogoutTimes', width: 130,
                                render: (sessions, r) => {
                                  const display = sessions?.length ? sessions.map((s, i) => (
                                    <div key={i}>{s.logout ? fmtClock(s.logout) : '—'}{s.logout && s.logout_next_day ? <span style={{ color: '#fa8c16', fontSize: 11 }}> (+1)</span> : null}</div>
                                  )) : '—';
                                  const lastS   = sessions?.at(-1);
                                  const lastOut = r.Punches?.length ? [...r.Punches].filter(p => _wc.has(p.code)).sort((a, b) => (a.out||'').localeCompare(b.out||'')).at(-1)?.out : null;
                                  if (lastOut?.slice(0, 5) === '23:59') return <>{display}<Tag color="gold" style={{ margin: '2px 0 0', display: 'block' }}>Midnight Shift</Tag></>;
                                  if (lastS?.logout && lastOut) {
                                    const d = _tm(lastOut) - _tm(lastS.logout);
                                    if (d > (_thresh['Punch Out after IC Logout']?.MaxValue ?? 2))
                                      return <>{display}{_ip(<Tag color="red" style={{ margin: '2px 0 0', display: 'block' }}>punch out +{d}m</Tag>, r.Date, 'Punch Out after IC Logout', d)}</>;
                                  }
                                  return display;
                                } },
                              { title: 'Punches', dataIndex: 'Punches', key: 'Punches', width: 175,
                                render: (v, r) => {
                                  if (!v?.length) return '—';
                                  const sessions    = r.LoginSessions ?? [];
                                  const punchMaxM   = _thresh['Punch to IC Available']?.MaxValue ?? 5;
                                  const getPunchGap = (p) => {
                                    if (!_wc.has(p.code) || !p.in) return null;
                                    const pMins = _tm(p.in);
                                    const alreadyAvail = sessions.some(s =>
                                      s.available &&
                                      _tm(s.available) <= pMins &&
                                      (!s.logout || pMins <= _tm(s.logout))
                                    );
                                    if (alreadyAvail) return null;
                                    const nextAvail = sessions
                                      .filter(s => s.available && _tm(s.available) > pMins)
                                      .sort((a, b) => _tm(a.available) - _tm(b.available))[0]?.available ?? null;
                                    if (!nextAvail) return null;
                                    return _tm(nextAvail) - pMins;
                                  };
                                  return v.map((p, i) => {
                                    const gap     = getPunchGap(p);
                                    const viol    = gap != null && gap > punchMaxM;
                                    return (
                                      <div key={i}>
                                        {p.in ? fmtClock(p.in) : '—'}{p.out ? ` - ${fmtClock(p.out)}` : ''}
                                        {p._nextDay && <span style={{ color: '#fa8c16', fontSize: 11 }}> (+1)</span>}
                                        {p.code ? <span style={{ color: '#8c8c8c', fontSize: 11, marginLeft: 4 }}>({p.code})</span> : null}
                                        {viol && _ip(<Tag color="red" style={{ margin: '2px 0 0', display: 'block' }}>+{gap}m to IC Avail</Tag>, r.Date, 'Punch to IC Available', gap)}
                                      </div>
                                    );
                                  });
                                } },
                              { title: 'Worked/IC Gap', key: 'WorkedICGap', width: 120, align: 'right',
                                render: (_, r) => {
                                  if (r.TotalICMins == null) return '—';
                                  const icHrs = (r.TotalICMins / 60).toFixed(2);
                                  const gap   = r.Worked ? Math.round((r.Worked * 60) - r.TotalICMins) : null;
                                  const viol  = gap != null && gap > (_thresh['Punch IC Gap']?.MaxValue ?? 15);
                                  return (
                                    <div style={{ lineHeight: '1.4' }}>
                                      <div>IC Hrs: {icHrs}</div>
                                      {gap != null && gap > 0 && (viol
                                        ? _ip(<Tag color="red" style={{ margin: 0 }}>{gap}m gap</Tag>, r.Date, 'Punch IC Gap', gap)
                                        : <span>{gap}m gap</span>)}
                                    </div>
                                  );
                                } },
                              { title: 'Sched Hrs', dataIndex: 'SchedHrs', key: 'SchedHrs', width: 85, align: 'right',
                                render: (v) => v ? Number(v).toFixed(2) : '—' },
                              { title: 'Worked', dataIndex: 'Worked', key: 'Worked', width: 80, align: 'right', render: (v) => fmtHrs(v) },
                              { title: 'PTO',    dataIndex: 'PTO',    key: 'PTO',    width: 70, align: 'right', render: (v) => fmtHrs(v) },
                              { title: 'Other',  dataIndex: 'Other',  key: 'Other',  width: 70, align: 'right', render: (v) => fmtHrs(v) },
                              // Teams whose CCaaS tab is hidden do not sign in to
                              // CCaaS at all, so these four are structurally empty
                              // rather than empty today -- four dashes per row and
                              // a "No IC Activity" flag that cannot mean anything.
                              ].filter((c) => !hideCCaasStatus || !_icCols.has(c.key));
                            })()}
                          />
                        </Card>

                        </>
                      );
                    })()}
                  </>
                );
              })(),
            },

            // ── AWS Adherence / AWS States ───────────────────────────────────
            // DBAL's AWS vs Schedule and AWS State by Day for this agent, one row
            // per day. Only for agents with AWS data in the range; Adherence
            // above is unchanged.
            ...(awsSched.has_aws ? (() => {
              const teamId = agentRecord?.TeamID ?? null;
              const dbalMap = {}; const dbalTeamMap = {};
              dbalThresholds.forEach((t) => {
                if (t.TeamID == null) dbalMap[t.Metric] = t;
                else if (t.TeamID === teamId) dbalTeamMap[t.Metric] = t;
              });
              const awsMap = {}; const awsTeamMap = {};
              ccaasStatuses.forEach((st) => {
                if (!st.AWSColumn) return;
                if (st.TeamID == null) awsMap[st.AWSColumn] = st;
                else if (st.TeamID === teamId) awsTeamMap[st.AWSColumn] = st;
              });
              const awsThresh = { ...awsMap, ...awsTeamMap };
              // DBAL's builders offer Create Compliance Incident on CanEditIncidents
              // alone; here canCreateIncident (off on My Summary) must also allow it,
              // as it does for Adherence.
              const incUser = canCreateIncident ? currentUser : { ...currentUser, CanEditIncidents: 0 };
              const workedByDate = Object.fromEntries((awsSched.rows || []).map((r) => [r.Date, (r.Worked || 0) * 60]));
              const note = (
                <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400, marginLeft: 6 }}>
                  — note there can be a 24 hr delay in showing recent data
                </Typography.Text>
              );
              return [
                {
                  key: 'aws-adherence',
                  label: 'AWS Adherence',
                  children: (
                    <Card size="small" title={<span>Daily Detail{note}</span>}>
                      <Table
                        rowKey="Date"
                        dataSource={awsSched.rows}
                        loading={awsTabLoading}
                        size="small"
                        pagination={{ defaultPageSize: 31, size: 'small' }}
                        locale={{ emptyText: 'No entries in this date range.' }}
                        scroll={{ x: 'max-content' }}
                        columns={buildScheduleCols(navigate, incUser, null, setComplianceModal,
                          { ...dbalMap, ...dbalTeamMap }, 'aws', { perDay: true })}
                      />
                    </Card>
                  ),
                },
                {
                  key: 'aws-states',
                  label: 'AWS States',
                  children: (
                    <Card size="small" title={<span>AWS State by Day{note}</span>}>
                      <Table
                        rowKey="Date"
                        dataSource={awsStateData.rows}
                        loading={awsTabLoading}
                        size="small"
                        pagination={{ defaultPageSize: 31, size: 'small' }}
                        locale={{ emptyText: 'No AWS state data in this date range.' }}
                        scroll={{ x: 'max-content' }}
                        columns={buildAwsStateCols({
                          navigate, currentUser: incUser, date: null, setComplianceModal,
                          states: awsStateData.states || [],
                          threshFor: (label) => awsThresh[(awsStateData.columns || {})[label]],
                          workedMinsFor: (row) => workedByDate[row.Date] ?? null,
                          perDay: true,
                        })}
                      />
                    </Card>
                  ),
                },
              ];
            })() : []),

            // ── CCaaS Status ──────────────────────────────────────────────────
            ...(!hideCCaasStatus ? [{
              key: 'ccaas-status',
              label: 'CCaaS Status',
              children: (() => {
                // Team-specific entries override global (null TeamID) entries
                const agentTeamId = agentRecord?.TeamID ?? null;
                const globalThresholds = {};
                const teamThresholds   = {};
                ccaasStatuses.forEach((s) => {
                  if (s.TeamID == null) globalThresholds[s.State] = s;
                  else if (s.TeamID === agentTeamId) teamThresholds[s.State] = s;
                });
                const thresholdMap = { ...globalThresholds, ...teamThresholds };

                // Derive worked minutes per day from already-loaded timecard (Reg + OT)
                const workedMinsByDay = {};
                (timecard?.rows || []).forEach((r) => {
                  if (r.EarningCode === 'Reg' || r.EarningCode === 'OT') {
                    workedMinsByDay[r.Date] = (workedMinsByDay[r.Date] || 0) + Number(r.Hours || 0) * 60;
                  }
                });

                const dbalGlobal = {}; const dbalTeam = {};
                dbalThresholds.forEach((t) => {
                  if (t.TeamID == null) dbalGlobal[t.Metric] = t;
                  else if (t.TeamID === agentTeamId) dbalTeam[t.Metric] = t;
                });
                const dbalThresholdMap = { ...dbalGlobal, ...dbalTeam };

                // ── Other Stats by Day ────────────────────────────────────────
                const dash = <span style={{ color: '#d9d9d9' }}>—</span>;
                const T = (v) => v != null ? v : dash;
                const TS = (v) => { const f = fmtSecs(v); return f != null ? f : dash; };
                const TT = (v) => { const f = fmtTimeStr(v); return f != null ? f : dash; };

                const timeStrToSecs = (s) => {
                  if (!s) return null;
                  const parts = String(s).split(':');
                  if (parts.length === 3) return (+parts[0]) * 3600 + (+parts[1]) * 60 + (+parts[2]);
                  if (parts.length === 2) return (+parts[0]) * 60 + (+parts[1]);
                  return null;
                };

                // Wraps a formatted cell value in a compliance-create popover
                const osPop = (row, label, rawValue, formatted) => {
                  if (rawValue == null) return dash;
                  if (!canCreateIncident || (currentUser?.CanEditIncidents ?? 0) < 2) return <span>{formatted}</span>;
                  const desc = `CCaaS: ${label} - ${formatted} on ${dayjs(row.Day).format('M/D/YY (ddd)')}`;
                  const popContent = (
                    <div style={{ fontSize: 13 }}>
                      <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                        onClick={() => openComplianceModal(row.Day, label, formatted, null, desc)}>
                        Create Compliance Incident
                      </Button>
                    </div>
                  );
                  return (
                    <Popover content={popContent} trigger="hover" mouseLeaveDelay={0.3}>
                      <span style={{ cursor: 'pointer' }}>{formatted}</span>
                    </Popover>
                  );
                };

                const otherStatsCols = [
                  { title: 'Date', dataIndex: 'Day', key: 'Day', width: 110, fixed: 'left',
                    render: (v) => { const d = dayjs(v); return `${d.format('M/D/YY')} (${d.format('ddd')})`; } },
                  { title: 'Last 15 mins spent in Break',   dataIndex: 'Last15MinsInBreak',  key: 'Last15MinsInBreak',  width: 160, align: 'right', render: (v, row) => {
                    const thresh = dbalThresholdMap['Last15MinsInBreak'];
                    const display = thresh && v != null && v > thresh.MaxValue ? <Tag color="red" style={{ margin: 0, fontWeight: 700 }}>{v}</Tag> : T(v);
                    return osPop(row, 'Last 15 mins spent in Break', v, display);
                  } },
                  { title: 'Last 15 mins spent in Non-ACD', dataIndex: 'Last15MinsInNonACD', key: 'Last15MinsInNonACD', width: 180, align: 'right', render: (v, row) => {
                    const thresh = dbalThresholdMap['Last15MinsInNonACD'];
                    const display = thresh && v != null && v > thresh.MaxValue ? <Tag color="red" style={{ margin: 0, fontWeight: 700 }}>{v}</Tag> : T(v);
                    return osPop(row, 'Last 15 mins spent in Non-ACD', v, display);
                  } },
                  {
                    title: 'Last IB ACW', dataIndex: 'LastACW', key: 'LastACW', width: 90, align: 'right',
                    render: (v, row) => {
                      const f = fmtTimeStr(v);
                      if (f == null) return dash;
                      const lastSecs  = timeStrToSecs(v);
                      const avgSecs   = row.IB_Phone_Avg_ACW_Sec;
                      const isHigh    = lastSecs != null && avgSecs != null && avgSecs > 0 && lastSecs >= avgSecs * 2;
                      const cell      = <span style={{ color: isHigh ? '#ff4d4f' : undefined, fontWeight: isHigh ? 700 : undefined }}>{f}</span>;
                      const hasCreate = canCreateIncident && (currentUser?.CanEditIncidents ?? 0) >= 2;
                      const hasContact = row.LastACWContactID != null;
                      if (!hasCreate && !hasContact) return cell;
                      const desc = `CCaaS: Last IB ACW - ${f} on ${dayjs(row.Day).format('M/D/YY (ddd)')}`;
                      const popContent = (
                        <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {hasCreate && (
                            <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                              onClick={() => openComplianceModal(row.Day, 'Last IB ACW', f, null, desc)}>
                              Create Compliance Incident
                            </Button>
                          )}
                          {hasContact && (
                            <Button size="small" type="link" style={{ padding: 0, height: 'auto' }}
                              onClick={() => navigate(`/calls/${row.LastACWContactID}`)}>
                              Contact ID: {row.LastACWContactID}
                            </Button>
                          )}
                        </div>
                      );
                      return <Popover content={popContent} trigger="hover" mouseLeaveDelay={0.3}><span style={{ cursor: 'pointer' }}>{cell}</span></Popover>;
                    },
                  },
                  { title: 'IB PH Handled',   dataIndex: 'IB_PH_Handled',         key: 'IB_PH_Handled',         width: 90,  align: 'right', render: (v, row) => osPop(row, 'IB PH Handled',   v, T(v)) },
                  { title: 'IB Chat Handled', dataIndex: 'IB_Chat_Handled',       key: 'IB_Chat_Handled',       width: 95,  align: 'right', render: (v, row) => osPop(row, 'IB Chat Handled', v, T(v)) },
                  { title: 'Refused Qty',     dataIndex: 'Refused_Qty',           key: 'Refused_Qty',           width: 80,  align: 'right', render: (v, row) => osPop(row, 'Refused Qty',     v, T(v)) },
                  { title: 'IB Avg Talk', dataIndex: 'IB_Phone_Avg_Talk_Sec', key: 'IB_Phone_Avg_Talk_Sec', width: 85, align: 'right', render: (v, row) => osPop(row, 'IB Phone Avg Talk', v, TS(v)) },
                  { title: 'IB Avg ACW',  dataIndex: 'IB_Phone_Avg_ACW_Sec',  key: 'IB_Phone_Avg_ACW_Sec',  width: 80, align: 'right', render: (v, row) => osPop(row, 'IB Phone Avg ACW',  v, TS(v)) },
                  { title: 'IB Avg HT',   dataIndex: 'IB_Phone_Avg_HT_Sec',   key: 'IB_Phone_Avg_HT_Sec',   width: 75, align: 'right', render: (v, row) => osPop(row, 'IB Phone Avg HT',   v, TS(v)) },
                  { title: 'IB Max ACW',  dataIndex: 'IB_Phone_Max_ACW_Sec',  key: 'IB_Phone_Max_ACW_Sec',  width: 80, align: 'right', render: (v, row) => osPop(row, 'IB Phone Max ACW',  v, TS(v)) },
                  { title: 'OB To Support',   dataIndex: 'OB_To_Support',         key: 'OB_To_Support',         width: 90,  align: 'right', render: (v, row) => osPop(row, 'OB To Support',   v, T(v)) },
                  { title: 'OB To Tier2',     dataIndex: 'OB_To_Tier2',           key: 'OB_To_Tier2',           width: 85,  align: 'right', render: (v, row) => osPop(row, 'OB To Tier2',     v, T(v)) },
                  { title: 'Qty Holds',       dataIndex: 'Qty_Holds',             key: 'Qty_Holds',             width: 80,  align: 'right', render: (v, row) => osPop(row, 'Qty Holds',       v, T(v)) },
                  { title: 'Total Hold',      dataIndex: 'Total_Hold_Time_Sec',   key: 'Total_Hold_Time_Sec',   width: 85,  align: 'right', render: (v, row) => osPop(row, 'Total Hold Time', v, TS(v)) },
                  { title: 'OB L1 Max ACW',   dataIndex: 'OB_Leg1_Max_ACW_Sec',  key: 'OB_Leg1_Max_ACW_Sec',  width: 100, align: 'right', render: (v, row) => osPop(row, 'OB Leg1 Max ACW', v, TS(v)) },
                  { title: 'OB Max ACW',      dataIndex: 'OB_Max_ACW_Sec',        key: 'OB_Max_ACW_Sec',        width: 90,  align: 'right', render: (v, row) => osPop(row, 'OB Max ACW',      v, TS(v)) },
                ];

                const otherStatsContent = ccaasOtherLoading ? (
                  <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
                ) : (
                  <div style={{ fontSize: 12 }}>
                    <Table
                      rowKey="Day"
                      dataSource={ccaasOtherData}
                      columns={otherStatsCols}
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      bordered
                      locale={{ emptyText: 'No data in this date range.' }}
                    />
                  </div>
                );

                const fmtLogDuration = (secs) => {
                  if (secs == null || secs < 0) return '—';
                  const h = Math.floor(secs / 3600);
                  const m = Math.floor((secs % 3600) / 60);
                  const s = secs % 60;
                  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                };

                const logStateIcon = (row) => {
                  if (row.LogState === 0) return <span style={{ color: '#52c41a', marginRight: 6 }}>●</span>;
                  if (row.LogState === 2) return <span style={{ color: '#8c8c8c', marginRight: 6 }}>●</span>;
                  if (row.Available === 1) return <span style={{ color: '#52c41a', marginRight: 6 }}>●</span>;
                  if (row.Working > 0)    return <span style={{ color: '#faad14', marginRight: 6 }}>●</span>;
                  return <MinusCircleFilled style={{ color: '#ff4d4f', marginRight: 6 }} />;
                };

                const stateSummaryContent = ccaasDailyLoading ? (
                  <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
                ) : (() => {
                  // Pivot: rows = days (desc), columns = distinct states
                  const days = [...new Set(ccaasDaily.map((r) => r.Day))].sort().reverse();
                  const allStates = [...new Set(ccaasDaily.map((r) => r.State))];
                  const PRIORITY = ['Break', 'Lunch', 'Unavailable', 'Refused Contact', 'Technical Issue'];
                  const orderedStates = [
                    ...PRIORITY.filter((s) => allStates.includes(s)),
                    ...allStates.filter((s) => !PRIORITY.includes(s)).sort(),
                  ];
                  const dataMap = {};
                  ccaasDaily.forEach((r) => {
                    if (!dataMap[r.Day]) dataMap[r.Day] = {};
                    dataMap[r.Day][r.State] = r;
                  });
                  const pivotRows = days.map((day) => ({ Day: day, ...(dataMap[day] || {}) }));

                  const pivotCols = [
                    {
                      title: 'Date', dataIndex: 'Day', key: 'Day', width: 145, fixed: 'left',
                      render: (v) => {
                        const d = dayjs(v);
                        return `${d.format('M/D/YY')} (${d.format('ddd')})`;
                      },
                    },
                    ...orderedStates.map((state) => {
                      const thresh = thresholdMap[state];
                      return {
                        title: state, key: state, width: 120, align: 'right',
                        render: (_, row) => {
                          const cell = row[state];
                          if (!cell) return <span style={{ color: '#d9d9d9' }}>—</span>;
                          const mins = cell.TotalMins;
                          const cnt  = cell.OccurrenceCount;
                          const tiers = (thresh?.Tiers || []).slice().sort((a, b) => a.WorkedMinsMax - b.WorkedMinsMax);
                          const dayWorked = workedMinsByDay[row.Day] ?? null;
                          let violation = false;
                          if (tiers.length > 0) {
                            if (dayWorked != null) {
                              const tier = tiers.find((t) => dayWorked < t.WorkedMinsMax);
                              if (tier) violation = mins > tier.MaxBreakMins;
                            }
                          } else {
                            violation = (thresh?.MaxMinsDay != null && mins > thresh.MaxMinsDay)
                                     || (thresh?.MaxCountPerDay != null && cnt > thresh.MaxCountPerDay);
                          }
                          const popContent = (
                            <div style={{ fontSize: 13 }}>
                              <div style={{ marginBottom: tiers.length > 0 ? 2 : 6 }}>
                                <strong>{mins} min</strong>
                                {cnt > 1 && <span style={{ color: '#8c8c8c' }}> ({cnt}x)</span>}
                                {tiers.length === 0 && thresh?.MaxMinsDay != null && violation && (
                                  <span style={{ color: '#ff4d4f' }}> (+{(mins - thresh.MaxMinsDay).toFixed(1)} over limit)</span>
                                )}
                              </div>
                              {tiers.length > 0 && (
                                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 6 }}>
                                  Shift worked: {dayWorked != null ? `${Math.round(dayWorked)} min` : 'no punch data'}
                                </div>
                              )}
                              <Button size="small" type="link" style={{ padding: 0, height: 'auto', display: 'block' }}
                                onClick={() => { setCcaasDate(dayjs(row.Day)); setPendingTimelineState(state); setCcaasView('timeline'); }}>
                                View Timeline
                              </Button>
                              {!!(canCreateIncident && (currentUser?.CanEditIncidents ?? 0) >= 2) && (
                                <Button size="small" type="link" style={{ padding: 0, height: 'auto', display: 'block' }}
                                  onClick={() => openComplianceModal(row.Day, state, mins, cnt)}>
                                  Create Compliance Incident
                                </Button>
                              )}
                            </div>
                          );
                          return (
                            <Popover content={popContent} trigger="hover" mouseLeaveDelay={0.3}>
                              <Tag
                                color={violation ? 'red' : undefined}
                                style={{ margin: 0, cursor: 'pointer', fontWeight: violation ? 700 : undefined }}
                              >
                                {mins}m{cnt > 1 ? ` (${cnt}x)` : ''}
                              </Tag>
                            </Popover>
                          );
                        },
                      };
                    }),
                  ];

                  return (
                    <Table
                      rowKey="Day"
                      dataSource={pivotRows}
                      columns={pivotCols}
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      bordered
                      locale={{ emptyText: 'No CCaaS state data in this date range.' }}
                    />
                  );
                })();

                const logContent = ccaasLogLoading ? (
                  <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
                ) : (
                  <Table
                    rowKey={(r, i) => `${r.StartDate}-${i}`}
                    dataSource={ccaasLog}
                    size="small"
                    pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ['50', '100', '200'], size: 'small', showTotal: (t) => `${t} record${t === 1 ? '' : 's'}` }}
                    locale={{ emptyText: 'No log entries for this date range.' }}
                    columns={[
                      {
                        title: 'Date', dataIndex: 'StartDate', key: 'Date', width: 115,
                        render: (v) => v ? dayjs(v).format('M/D/YY (ddd)') : '—',
                      },
                      {
                        title: 'Start Time', dataIndex: 'StartDate', key: 'StartDate', width: 110,
                        render: (v) => v ? dayjs(v).format('h:mm:ss A') : '—',
                      },
                      {
                        title: 'End Time', dataIndex: 'EndDate', key: 'EndDate', width: 110,
                        render: (v) => v ? dayjs(v).format('h:mm:ss A') : '—',
                      },
                      {
                        title: 'Duration', dataIndex: 'DurationSecs', key: 'DurationSecs', width: 90, align: 'left',
                        render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtLogDuration(v)}</span>,
                      },
                      {
                        title: 'State', dataIndex: 'State', key: 'State', width: 280, ellipsis: true,
                        render: (v, row) => (
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            {logStateIcon(row)}
                            {v}
                          </span>
                        ),
                      },
                      {
                        title: 'Skill', dataIndex: 'SkillName', key: 'SkillName', ellipsis: true,
                        render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
                      },
                    ]}
                  />
                );

                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
                      <Segmented
                        value={ccaasView}
                        onChange={setCcaasView}
                        options={[
                          { label: 'State By Day',       value: 'summary'  },
                          { label: 'Other Stats By Day', value: 'other'    },
                          { label: 'Log',                value: 'log'      },
                          { label: 'Timeline',           value: 'timeline' },
                          { label: 'By Month',           value: 'month'    },
                        ]}
                      />
                      {(ccaasView === 'summary' || ccaasView === 'other') && (
                        <Space size={4}>
                          <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>Date range:</Typography.Text>
                          <DatePicker.RangePicker
                            value={ccaasDailyRange}
                            onChange={setCcaasDailyRange}
                            allowClear={false}
                            format="YYYY-MM-DD"
                          />
                        </Space>
                      )}
                      {(ccaasView === 'log' || ccaasView === 'timeline') && (
                        <DatePicker
                          value={ccaasDate}
                          onChange={(v) => v && setCcaasDate(v)}
                          allowClear={false}
                          format="YYYY-MM-DD"
                          disabledDate={(d) => d && d > dayjs().startOf('day')}
                        />
                      )}
                      {ccaasView === 'timeline' && timelineStateOptions.length > 0 && (
                        <Select
                          mode="multiple"
                          allowClear
                          size="small"
                          placeholder="All states"
                          value={selectedTimelineStates}
                          onChange={setSelectedTimelineStates}
                          style={{ minWidth: 200 }}
                          options={timelineStateOptions.map((s) => ({ value: s, label: s }))}
                          maxTagCount="responsive"
                        />
                      )}
                    </div>

                    {ccaasView === 'summary'  ? stateSummaryContent
                    : ccaasView === 'other'    ? otherStatsContent
                    : ccaasView === 'log'      ? logContent
                    : ccaasView === 'timeline' ? <AgentTimeline agentUserId={agentUserId} date={ccaasDate}
                        selectedStates={selectedTimelineStates}
                        onStatesLoaded={(states) => {
                          setTimelineStateOptions(states);
                          if (pendingTimelineState && states.includes(pendingTimelineState)) {
                            setSelectedTimelineStates([pendingTimelineState]);
                          } else {
                            setSelectedTimelineStates([]);
                          }
                          setPendingTimelineState(null);
                        }}
                      />
                    : ccaasView === 'month' ? (
                      ccaasMonthlyLoading ? (
                        <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>
                      ) : (
                        <Table
                          rowKey="MonthKey"
                          dataSource={ccaasMonthly}
                          size="small"
                          pagination={false}
                          locale={{ emptyText: 'No CCaaS data in this date range.' }}
                          columns={[
                            { title: 'Month',          dataIndex: 'MonthKey',            key: 'MonthKey',            width: 100, render: (v) => v ?? '—' },
                            { title: 'Work Mins',      dataIndex: 'WorkMins',            key: 'WorkMins',            width: 95,  align: 'right', render: (v) => v != null ? v : '—' },
                            { title: 'Available %',    dataIndex: 'AvailablePct',        key: 'AvailablePct',        width: 100, align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Active 4 SLA %', dataIndex: 'Active4SLAPct',       key: 'Active4SLAPct',       width: 115, align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Proj/Email %',   dataIndex: 'ProjEmailPct',        key: 'ProjEmailPct',        width: 105, align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Meetings %',     dataIndex: 'MeetingsPct',         key: 'MeetingsPct',         width: 100, align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Coaching %',     dataIndex: 'CoachingTrainingPct', key: 'CoachingTrainingPct', width: 100, align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Tech %',         dataIndex: 'TechPct',             key: 'TechPct',             width: 80,  align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Unproductive %', dataIndex: 'UnproductivePct',     key: 'UnproductivePct',     width: 120, align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Refused %',      dataIndex: 'RefusedPct',          key: 'RefusedPct',          width: 95,  align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Unavailable %',  dataIndex: 'UnavailablePct',      key: 'UnavailablePct',      width: 110, align: 'right', render: (v) => v != null ? `${v}%` : '—' },
                            { title: 'Break/Lunch Mins', dataIndex: 'BreakLunchMins',    key: 'BreakLunchMins',      width: 135, align: 'right', render: (v) => v != null ? v : '—' },
                          ]}
                        />
                      )
                    ) : null}
                  </>
                );
              })(),
            }] : []),

          ].filter(tab => tab && !_hiddenTabs.has(tab.key))} />
        </>
      )}

      <ComplianceCreateModal
        open={!!complianceModal}
        agentUserId={agentUserId}
        agentName={agentName}
        initialDate={complianceModal?.day}
        initialDescription={complianceModal?.description}
        initialTeamId={agentRecord?.TeamID}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setComplianceModal(null)}
        onCreated={() => {
          setComplianceRefreshKey((k) => k + 1);
          message.success(`Compliance incident created for ${agentName}.`);
        }}
      />

      {/* ── Create Error Modal ───────────────────────────────────────────── */}
      <ErrorCreateModal
        open={actionErrorOpen}
        agentUserId={agentUserId}
        agentName={agentName}
        initialAssignedToId={currentUser?.ID}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setActionErrorOpen(false)}
      />

      {/* ── Create Absence Notification Modal ─────────────────────────────── */}
      <AbsenceCreateModal
        open={actionAbsenceOpen}
        userId={agentUserId}
        userName={agentName}
        initialTeamId={agentRecord?.TeamID}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setActionAbsenceOpen(false)}
      />

      {/* ── Manage Badges Modal ──────────────────────────────────────────── */}
      <ManageBadgesModal
        open={badgesOpen}
        userId={agentUserId}
        userName={agentName}
        onClose={() => setBadgesOpen(false)}
        onChanged={loadBadges}
      />

      {/* ── Create Coaching Session Modal ────────────────────────────────── */}
      <CoachingCreateModal
        open={actionCoachingOpen}
        agentUserId={agentUserId}
        agentName={agentName}
        currentUser={currentUser}
        navigate={navigate}
        onClose={() => setActionCoachingOpen(false)}
      />
    </>
  );
}

